// Generated by foundation/tooling/scripts/generate_frontend_prototype_runtime.mjs.
// Do not edit by hand.

import {
  createDummyDataFactory,
  createLiveProjectionBinding,
  createTenantProjectionStore,
  createGeneratedHookRegistry,
  createPrototypeRuntimeCache,
  createRuntimeWorkbench,
  type DummyDataFactoryOptions,
  type DummySchemaSpec,
  type LiveProjectionBinding,
  type LiveProjectionBindingOptions,
  type PrototypeMode,
  type PrototypeRuntimeCache,
  type RuntimeWorkbenchAdapter,
  type TenantProjectionStore,
} from "@ovasabi/frontend-kit";

export type PrototypeGeneratedStore = {
  useSnapshot(): unknown;
};

export type PrototypeFixtureState<TRecord extends Record<string, unknown>> = {
  name: string;
  tenantId: string;
  domain: string;
  collection: string;
  records: readonly TRecord[];
};

export type PrototypeBenchmarkFixture = {
  name: string;
  lane: "dummy" | "store";
  records: number;
  run(): unknown;
};

export type PrototypeSchemaRuntimeConstants = {
  key: string;
  domain: string;
  collection: string;
  idField: string;
};

export type PrototypeDomainRuntime = {
  constants: PrototypeSchemaRuntimeConstants;
  schema: DummySchemaSpec;
  createDummyRecords(
    tenantId: string,
    count?: number,
    options?: DummyDataFactoryOptions,
    cache?: PrototypeRuntimeCache
  ): readonly Record<string, unknown>[];
  createStore(
    tenantId: string,
    initialRecords?: readonly Record<string, unknown>[],
    cache?: PrototypeRuntimeCache
  ): TenantProjectionStore<Record<string, unknown>>;
  connectLiveProjection(
    options: Omit<LiveProjectionBindingOptions<Record<string, unknown>>, "scope" | "store"> & {
      tenantId: string;
      store?: TenantProjectionStore<Record<string, unknown>>;
      cache?: PrototypeRuntimeCache;
    }
  ): LiveProjectionBinding<Record<string, unknown>>;
  registerStore(
    tenantId: string,
    store?: TenantProjectionStore<Record<string, unknown>>
  ): TenantProjectionStore<Record<string, unknown>>;
  fixtureStates: Record<string, unknown>;
  benchmarkFixtures: readonly PrototypeBenchmarkFixture[];
};

export const prototypeStoreRegistry = createGeneratedHookRegistry<PrototypeGeneratedStore>();
export const prototypeRuntimeCache = createPrototypeRuntimeCache();

export const createPrototypeRuntimeWorkbench = (
  mode: PrototypeMode = "dummy",
  adapter?: RuntimeWorkbenchAdapter
) => createRuntimeWorkbench(mode, adapter);

export type AIResolutionPrototype = Record<string, unknown>;

/** Typed field shape for assist.v1.AIResolution. Use it to narrow AIResolutionPrototype records. */
export type AIResolutionFields = {
  readonly "confidence": number;
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "model_ref": string;
  readonly "status": string;
  readonly "summary": string;
  readonly "ticket_id": string;
};

export const aIResolutionFieldOrder = ["id","ticket_id","status","summary","model_ref","confidence","created_at"] as const satisfies readonly (keyof AIResolutionFields)[];

export const aIResolutionRequiredFields = ["confidence","id","model_ref","status","summary","ticket_id"] as const satisfies readonly (keyof AIResolutionFields)[];

export type AIResolutionStore = TenantProjectionStore<AIResolutionPrototype>;

export const aIResolutionSchema = {
  "collection": "ai_resolutions",
  "domain": "assist",
  "fields": [
    {
      "kind": "float",
      "name": "confidence"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "model_ref"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "text",
      "name": "summary"
    },
    {
      "kind": "uuid",
      "name": "ticket_id"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createAIResolutionDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<AIResolutionPrototype>(aIResolutionSchema, options);

export const aIResolutionRuntimeConstants = {
  collection: aIResolutionSchema.collection,
  domain: aIResolutionSchema.domain,
  idField: aIResolutionSchema.idField ?? "id",
  key: "assist.ai_resolutions",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedAIResolutionDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly AIResolutionPrototype[] =>
  cache.getDummyRecords(aIResolutionSchema, { ...options, tenantId }, count, () =>
    createAIResolutionDummyFactory({ ...options, tenantId }).list(count)
  );

export const createAIResolutionStore = (
  tenantId: string,
  initialRecords: readonly AIResolutionPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): AIResolutionStore => {
  const scope = {
    collection: aIResolutionSchema.collection,
    domain: aIResolutionSchema.domain,
    tenantId,
  };
  const store = cache.getStore<AIResolutionPrototype>(
    scope,
    () =>
      createTenantProjectionStore<AIResolutionPrototype>(
        scope,
        (record) => String(record[aIResolutionSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectAIResolutionLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<AIResolutionPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: AIResolutionStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<AIResolutionPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createAIResolutionStore(tenantId, [], cache);
  return createLiveProjectionBinding<AIResolutionPrototype>({
    ...bindingOptions,
    scope: {
      collection: aIResolutionSchema.collection,
      domain: aIResolutionSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerAIResolutionStore = (
  tenantId: string,
  store: AIResolutionStore = createAIResolutionStore(tenantId)
): AIResolutionStore => {
  prototypeStoreRegistry.register("assist.ai_resolutions", store);
  return store;
};

export const useAIResolutionSnapshot = () => prototypeStoreRegistry.use("assist.ai_resolutions");

export const aIResolutionFixtures = {
  empty: (): AIResolutionPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly AIResolutionPrototype[] =>
    createCachedAIResolutionDummyRecords(tenantId, 8, { seed: "assist.ai_resolutions:happy" }),
  invalidMissingId: (): AIResolutionPrototype[] =>
    createAIResolutionDummyFactory({ seed: "assist.ai_resolutions:invalid" }).list(1, () => ({
      [aIResolutionSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly AIResolutionPrototype[] =>
    createCachedAIResolutionDummyRecords(tenantId, count, { seed: "assist.ai_resolutions:high-volume" }),
};

export const aIResolutionFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<AIResolutionPrototype> => ({
    collection: aIResolutionSchema.collection,
    domain: aIResolutionSchema.domain,
    name: "assist.ai_resolutions.empty",
    records: aIResolutionFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<AIResolutionPrototype> => ({
    collection: aIResolutionSchema.collection,
    domain: aIResolutionSchema.domain,
    name: "assist.ai_resolutions.happyPath",
    records: aIResolutionFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<AIResolutionPrototype> => ({
    collection: aIResolutionSchema.collection,
    domain: aIResolutionSchema.domain,
    name: "assist.ai_resolutions.invalidMissingId",
    records: aIResolutionFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<AIResolutionPrototype> => ({
    collection: aIResolutionSchema.collection,
    domain: aIResolutionSchema.domain,
    name: "assist.ai_resolutions.highVolume",
    records: aIResolutionFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const aIResolutionBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "assist.ai_resolutions.dummy.list.1000",
    records: 1000,
    run: () => createCachedAIResolutionDummyRecords("tenant-benchmark", 1000, { seed: "assist.ai_resolutions:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "assist.ai_resolutions.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createAIResolutionDummyFactory({ seed: "assist.ai_resolutions:bench-store" }).list(1000);
      const store = createAIResolutionStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "assist.ai_resolutions.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createAIResolutionDummyFactory({ seed: "assist.ai_resolutions:bench-apply" }).list(1000);
      const store = createAIResolutionStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[aIResolutionSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: aIResolutionSchema.collection,
          domain: aIResolutionSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "assist.ai_resolutions.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createAIResolutionDummyFactory({ seed: "assist.ai_resolutions:bench-reset" }).list(1000);
      const store = createAIResolutionStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "assist.ai_resolutions.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createAIResolutionDummyFactory({ seed: "assist.ai_resolutions:bench-selector" }).list(1000);
      const store = createAIResolutionStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[aIResolutionSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type HelpTicketPrototype = Record<string, unknown>;

/** Typed field shape for assist.v1.HelpTicket. Use it to narrow HelpTicketPrototype records. */
export type HelpTicketFields = {
  readonly "body": string;
  readonly "channel": string;
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "order_id": string;
  readonly "organization_id": string;
  readonly "profile_id": string;
  readonly "status": string;
  readonly "subject": string;
  readonly "updated_at"?: string | null;
};

export const helpTicketFieldOrder = ["id","organization_id","profile_id","order_id","status","channel","subject","body","created_at","updated_at"] as const satisfies readonly (keyof HelpTicketFields)[];

export const helpTicketRequiredFields = ["body","channel","id","order_id","organization_id","profile_id","status","subject"] as const satisfies readonly (keyof HelpTicketFields)[];

export type HelpTicketStore = TenantProjectionStore<HelpTicketPrototype>;

export const helpTicketSchema = {
  "collection": "help_tickets",
  "domain": "assist",
  "fields": [
    {
      "kind": "text",
      "name": "body"
    },
    {
      "kind": "string",
      "name": "channel"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "order_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "string",
      "name": "subject"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createHelpTicketDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<HelpTicketPrototype>(helpTicketSchema, options);

export const helpTicketRuntimeConstants = {
  collection: helpTicketSchema.collection,
  domain: helpTicketSchema.domain,
  idField: helpTicketSchema.idField ?? "id",
  key: "assist.help_tickets",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedHelpTicketDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly HelpTicketPrototype[] =>
  cache.getDummyRecords(helpTicketSchema, { ...options, tenantId }, count, () =>
    createHelpTicketDummyFactory({ ...options, tenantId }).list(count)
  );

export const createHelpTicketStore = (
  tenantId: string,
  initialRecords: readonly HelpTicketPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): HelpTicketStore => {
  const scope = {
    collection: helpTicketSchema.collection,
    domain: helpTicketSchema.domain,
    tenantId,
  };
  const store = cache.getStore<HelpTicketPrototype>(
    scope,
    () =>
      createTenantProjectionStore<HelpTicketPrototype>(
        scope,
        (record) => String(record[helpTicketSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectHelpTicketLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<HelpTicketPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: HelpTicketStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<HelpTicketPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createHelpTicketStore(tenantId, [], cache);
  return createLiveProjectionBinding<HelpTicketPrototype>({
    ...bindingOptions,
    scope: {
      collection: helpTicketSchema.collection,
      domain: helpTicketSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerHelpTicketStore = (
  tenantId: string,
  store: HelpTicketStore = createHelpTicketStore(tenantId)
): HelpTicketStore => {
  prototypeStoreRegistry.register("assist.help_tickets", store);
  return store;
};

export const useHelpTicketSnapshot = () => prototypeStoreRegistry.use("assist.help_tickets");

export const helpTicketFixtures = {
  empty: (): HelpTicketPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly HelpTicketPrototype[] =>
    createCachedHelpTicketDummyRecords(tenantId, 8, { seed: "assist.help_tickets:happy" }),
  invalidMissingId: (): HelpTicketPrototype[] =>
    createHelpTicketDummyFactory({ seed: "assist.help_tickets:invalid" }).list(1, () => ({
      [helpTicketSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly HelpTicketPrototype[] =>
    createCachedHelpTicketDummyRecords(tenantId, count, { seed: "assist.help_tickets:high-volume" }),
};

export const helpTicketFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<HelpTicketPrototype> => ({
    collection: helpTicketSchema.collection,
    domain: helpTicketSchema.domain,
    name: "assist.help_tickets.empty",
    records: helpTicketFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<HelpTicketPrototype> => ({
    collection: helpTicketSchema.collection,
    domain: helpTicketSchema.domain,
    name: "assist.help_tickets.happyPath",
    records: helpTicketFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<HelpTicketPrototype> => ({
    collection: helpTicketSchema.collection,
    domain: helpTicketSchema.domain,
    name: "assist.help_tickets.invalidMissingId",
    records: helpTicketFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<HelpTicketPrototype> => ({
    collection: helpTicketSchema.collection,
    domain: helpTicketSchema.domain,
    name: "assist.help_tickets.highVolume",
    records: helpTicketFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const helpTicketBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "assist.help_tickets.dummy.list.1000",
    records: 1000,
    run: () => createCachedHelpTicketDummyRecords("tenant-benchmark", 1000, { seed: "assist.help_tickets:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "assist.help_tickets.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createHelpTicketDummyFactory({ seed: "assist.help_tickets:bench-store" }).list(1000);
      const store = createHelpTicketStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "assist.help_tickets.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createHelpTicketDummyFactory({ seed: "assist.help_tickets:bench-apply" }).list(1000);
      const store = createHelpTicketStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[helpTicketSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: helpTicketSchema.collection,
          domain: helpTicketSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "assist.help_tickets.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createHelpTicketDummyFactory({ seed: "assist.help_tickets:bench-reset" }).list(1000);
      const store = createHelpTicketStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "assist.help_tickets.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createHelpTicketDummyFactory({ seed: "assist.help_tickets:bench-selector" }).list(1000);
      const store = createHelpTicketStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[helpTicketSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type CheckoutSessionPrototype = Record<string, unknown>;

/** Typed field shape for billing.v1.CheckoutSession. Use it to narrow CheckoutSessionPrototype records. */
export type CheckoutSessionFields = {
  readonly "access_code": string;
  readonly "amount_minor": number;
  readonly "checkout_url": string;
  readonly "currency": string;
  readonly "expires_at"?: string | null;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "provider": "BILLING_PROVIDER_MOCK" | "BILLING_PROVIDER_PAYSTACK";
  readonly "reference": string;
  readonly "status": "PAYMENT_STATUS_PENDING" | "PAYMENT_STATUS_AUTHORIZED" | "PAYMENT_STATUS_CAPTURED" | "PAYMENT_STATUS_FAILED" | "PAYMENT_STATUS_REFUNDED";
};

export const checkoutSessionFieldOrder = ["id","organization_id","provider","reference","checkout_url","amount_minor","currency","status","expires_at","access_code"] as const satisfies readonly (keyof CheckoutSessionFields)[];

export const checkoutSessionRequiredFields = ["access_code","amount_minor","checkout_url","currency","id","organization_id","provider","reference","status"] as const satisfies readonly (keyof CheckoutSessionFields)[];

export type CheckoutSessionStore = TenantProjectionStore<CheckoutSessionPrototype>;

export const checkoutSessionSchema = {
  "collection": "checkout_sessions",
  "domain": "billing",
  "fields": [
    {
      "kind": "string",
      "name": "access_code"
    },
    {
      "kind": "int",
      "name": "amount_minor"
    },
    {
      "kind": "url",
      "name": "checkout_url"
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "timestamp",
      "name": "expires_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "enumValues": [
        "BILLING_PROVIDER_MOCK",
        "BILLING_PROVIDER_PAYSTACK"
      ],
      "kind": "enum",
      "name": "provider"
    },
    {
      "kind": "string",
      "name": "reference"
    },
    {
      "enumValues": [
        "PAYMENT_STATUS_PENDING",
        "PAYMENT_STATUS_AUTHORIZED",
        "PAYMENT_STATUS_CAPTURED",
        "PAYMENT_STATUS_FAILED",
        "PAYMENT_STATUS_REFUNDED"
      ],
      "kind": "enum",
      "name": "status"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createCheckoutSessionDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<CheckoutSessionPrototype>(checkoutSessionSchema, options);

export const checkoutSessionRuntimeConstants = {
  collection: checkoutSessionSchema.collection,
  domain: checkoutSessionSchema.domain,
  idField: checkoutSessionSchema.idField ?? "id",
  key: "billing.checkout_sessions",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedCheckoutSessionDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly CheckoutSessionPrototype[] =>
  cache.getDummyRecords(checkoutSessionSchema, { ...options, tenantId }, count, () =>
    createCheckoutSessionDummyFactory({ ...options, tenantId }).list(count)
  );

export const createCheckoutSessionStore = (
  tenantId: string,
  initialRecords: readonly CheckoutSessionPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): CheckoutSessionStore => {
  const scope = {
    collection: checkoutSessionSchema.collection,
    domain: checkoutSessionSchema.domain,
    tenantId,
  };
  const store = cache.getStore<CheckoutSessionPrototype>(
    scope,
    () =>
      createTenantProjectionStore<CheckoutSessionPrototype>(
        scope,
        (record) => String(record[checkoutSessionSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectCheckoutSessionLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<CheckoutSessionPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: CheckoutSessionStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<CheckoutSessionPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createCheckoutSessionStore(tenantId, [], cache);
  return createLiveProjectionBinding<CheckoutSessionPrototype>({
    ...bindingOptions,
    scope: {
      collection: checkoutSessionSchema.collection,
      domain: checkoutSessionSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerCheckoutSessionStore = (
  tenantId: string,
  store: CheckoutSessionStore = createCheckoutSessionStore(tenantId)
): CheckoutSessionStore => {
  prototypeStoreRegistry.register("billing.checkout_sessions", store);
  return store;
};

export const useCheckoutSessionSnapshot = () => prototypeStoreRegistry.use("billing.checkout_sessions");

export const checkoutSessionFixtures = {
  empty: (): CheckoutSessionPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly CheckoutSessionPrototype[] =>
    createCachedCheckoutSessionDummyRecords(tenantId, 8, { seed: "billing.checkout_sessions:happy" }),
  invalidMissingId: (): CheckoutSessionPrototype[] =>
    createCheckoutSessionDummyFactory({ seed: "billing.checkout_sessions:invalid" }).list(1, () => ({
      [checkoutSessionSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly CheckoutSessionPrototype[] =>
    createCachedCheckoutSessionDummyRecords(tenantId, count, { seed: "billing.checkout_sessions:high-volume" }),
};

export const checkoutSessionFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<CheckoutSessionPrototype> => ({
    collection: checkoutSessionSchema.collection,
    domain: checkoutSessionSchema.domain,
    name: "billing.checkout_sessions.empty",
    records: checkoutSessionFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<CheckoutSessionPrototype> => ({
    collection: checkoutSessionSchema.collection,
    domain: checkoutSessionSchema.domain,
    name: "billing.checkout_sessions.happyPath",
    records: checkoutSessionFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<CheckoutSessionPrototype> => ({
    collection: checkoutSessionSchema.collection,
    domain: checkoutSessionSchema.domain,
    name: "billing.checkout_sessions.invalidMissingId",
    records: checkoutSessionFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<CheckoutSessionPrototype> => ({
    collection: checkoutSessionSchema.collection,
    domain: checkoutSessionSchema.domain,
    name: "billing.checkout_sessions.highVolume",
    records: checkoutSessionFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const checkoutSessionBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "billing.checkout_sessions.dummy.list.1000",
    records: 1000,
    run: () => createCachedCheckoutSessionDummyRecords("tenant-benchmark", 1000, { seed: "billing.checkout_sessions:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "billing.checkout_sessions.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createCheckoutSessionDummyFactory({ seed: "billing.checkout_sessions:bench-store" }).list(1000);
      const store = createCheckoutSessionStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.checkout_sessions.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createCheckoutSessionDummyFactory({ seed: "billing.checkout_sessions:bench-apply" }).list(1000);
      const store = createCheckoutSessionStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[checkoutSessionSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: checkoutSessionSchema.collection,
          domain: checkoutSessionSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.checkout_sessions.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createCheckoutSessionDummyFactory({ seed: "billing.checkout_sessions:bench-reset" }).list(1000);
      const store = createCheckoutSessionStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.checkout_sessions.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createCheckoutSessionDummyFactory({ seed: "billing.checkout_sessions:bench-selector" }).list(1000);
      const store = createCheckoutSessionStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[checkoutSessionSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type PaymentPrototype = Record<string, unknown>;

/** Typed field shape for billing.v1.Payment. Use it to narrow PaymentPrototype records. */
export type PaymentFields = {
  readonly "amount_minor": number;
  readonly "currency": string;
  readonly "id": string;
  readonly "order_id": string;
  readonly "organization_id": string;
  readonly "payer_profile_id": string;
  readonly "processed_at"?: string | null;
  readonly "provider": "BILLING_PROVIDER_MOCK" | "BILLING_PROVIDER_PAYSTACK";
  readonly "provider_txn_id": string;
  readonly "reference": string;
  readonly "status": "PAYMENT_STATUS_PENDING" | "PAYMENT_STATUS_AUTHORIZED" | "PAYMENT_STATUS_CAPTURED" | "PAYMENT_STATUS_FAILED" | "PAYMENT_STATUS_REFUNDED";
};

export const paymentFieldOrder = ["id","organization_id","provider","provider_txn_id","reference","status","amount_minor","currency","order_id","payer_profile_id","processed_at"] as const satisfies readonly (keyof PaymentFields)[];

export const paymentRequiredFields = ["amount_minor","currency","id","order_id","organization_id","payer_profile_id","provider","provider_txn_id","reference","status"] as const satisfies readonly (keyof PaymentFields)[];

export type PaymentStore = TenantProjectionStore<PaymentPrototype>;

export const paymentSchema = {
  "collection": "payments",
  "domain": "billing",
  "fields": [
    {
      "kind": "int",
      "name": "amount_minor"
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "order_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "payer_profile_id"
    },
    {
      "kind": "timestamp",
      "name": "processed_at",
      "optional": true
    },
    {
      "enumValues": [
        "BILLING_PROVIDER_MOCK",
        "BILLING_PROVIDER_PAYSTACK"
      ],
      "kind": "enum",
      "name": "provider"
    },
    {
      "kind": "uuid",
      "name": "provider_txn_id"
    },
    {
      "kind": "string",
      "name": "reference"
    },
    {
      "enumValues": [
        "PAYMENT_STATUS_PENDING",
        "PAYMENT_STATUS_AUTHORIZED",
        "PAYMENT_STATUS_CAPTURED",
        "PAYMENT_STATUS_FAILED",
        "PAYMENT_STATUS_REFUNDED"
      ],
      "kind": "enum",
      "name": "status"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createPaymentDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<PaymentPrototype>(paymentSchema, options);

export const paymentRuntimeConstants = {
  collection: paymentSchema.collection,
  domain: paymentSchema.domain,
  idField: paymentSchema.idField ?? "id",
  key: "billing.payments",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedPaymentDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly PaymentPrototype[] =>
  cache.getDummyRecords(paymentSchema, { ...options, tenantId }, count, () =>
    createPaymentDummyFactory({ ...options, tenantId }).list(count)
  );

export const createPaymentStore = (
  tenantId: string,
  initialRecords: readonly PaymentPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): PaymentStore => {
  const scope = {
    collection: paymentSchema.collection,
    domain: paymentSchema.domain,
    tenantId,
  };
  const store = cache.getStore<PaymentPrototype>(
    scope,
    () =>
      createTenantProjectionStore<PaymentPrototype>(
        scope,
        (record) => String(record[paymentSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectPaymentLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<PaymentPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: PaymentStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<PaymentPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createPaymentStore(tenantId, [], cache);
  return createLiveProjectionBinding<PaymentPrototype>({
    ...bindingOptions,
    scope: {
      collection: paymentSchema.collection,
      domain: paymentSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerPaymentStore = (
  tenantId: string,
  store: PaymentStore = createPaymentStore(tenantId)
): PaymentStore => {
  prototypeStoreRegistry.register("billing.payments", store);
  return store;
};

export const usePaymentSnapshot = () => prototypeStoreRegistry.use("billing.payments");

export const paymentFixtures = {
  empty: (): PaymentPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly PaymentPrototype[] =>
    createCachedPaymentDummyRecords(tenantId, 8, { seed: "billing.payments:happy" }),
  invalidMissingId: (): PaymentPrototype[] =>
    createPaymentDummyFactory({ seed: "billing.payments:invalid" }).list(1, () => ({
      [paymentSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly PaymentPrototype[] =>
    createCachedPaymentDummyRecords(tenantId, count, { seed: "billing.payments:high-volume" }),
};

export const paymentFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<PaymentPrototype> => ({
    collection: paymentSchema.collection,
    domain: paymentSchema.domain,
    name: "billing.payments.empty",
    records: paymentFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<PaymentPrototype> => ({
    collection: paymentSchema.collection,
    domain: paymentSchema.domain,
    name: "billing.payments.happyPath",
    records: paymentFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<PaymentPrototype> => ({
    collection: paymentSchema.collection,
    domain: paymentSchema.domain,
    name: "billing.payments.invalidMissingId",
    records: paymentFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<PaymentPrototype> => ({
    collection: paymentSchema.collection,
    domain: paymentSchema.domain,
    name: "billing.payments.highVolume",
    records: paymentFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const paymentBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "billing.payments.dummy.list.1000",
    records: 1000,
    run: () => createCachedPaymentDummyRecords("tenant-benchmark", 1000, { seed: "billing.payments:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "billing.payments.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createPaymentDummyFactory({ seed: "billing.payments:bench-store" }).list(1000);
      const store = createPaymentStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.payments.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createPaymentDummyFactory({ seed: "billing.payments:bench-apply" }).list(1000);
      const store = createPaymentStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[paymentSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: paymentSchema.collection,
          domain: paymentSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.payments.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createPaymentDummyFactory({ seed: "billing.payments:bench-reset" }).list(1000);
      const store = createPaymentStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.payments.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createPaymentDummyFactory({ seed: "billing.payments:bench-selector" }).list(1000);
      const store = createPaymentStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[paymentSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type ProfileWalletBalancePrototype = Record<string, unknown>;

/** Typed field shape for billing.v1.ProfileWalletBalance. Use it to narrow ProfileWalletBalancePrototype records. */
export type ProfileWalletBalanceFields = {
  readonly "available_minor": number;
  readonly "currency": string;
  readonly "owner_class": string;
  readonly "pending_minor": number;
  readonly "profile_id": string;
};

export const profileWalletBalanceFieldOrder = ["profile_id","owner_class","available_minor","pending_minor","currency"] as const satisfies readonly (keyof ProfileWalletBalanceFields)[];

export const profileWalletBalanceRequiredFields = ["available_minor","currency","owner_class","pending_minor","profile_id"] as const satisfies readonly (keyof ProfileWalletBalanceFields)[];

export type ProfileWalletBalanceStore = TenantProjectionStore<ProfileWalletBalancePrototype>;

export const profileWalletBalanceSchema = {
  "collection": "profile_wallet_balances",
  "domain": "billing",
  "fields": [
    {
      "kind": "int",
      "name": "available_minor"
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "string",
      "name": "owner_class"
    },
    {
      "kind": "int",
      "name": "pending_minor"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    }
  ],
  "idField": "profile_id"
} as const satisfies DummySchemaSpec;

export const createProfileWalletBalanceDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<ProfileWalletBalancePrototype>(profileWalletBalanceSchema, options);

export const profileWalletBalanceRuntimeConstants = {
  collection: profileWalletBalanceSchema.collection,
  domain: profileWalletBalanceSchema.domain,
  idField: profileWalletBalanceSchema.idField ?? "id",
  key: "billing.profile_wallet_balances",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedProfileWalletBalanceDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly ProfileWalletBalancePrototype[] =>
  cache.getDummyRecords(profileWalletBalanceSchema, { ...options, tenantId }, count, () =>
    createProfileWalletBalanceDummyFactory({ ...options, tenantId }).list(count)
  );

export const createProfileWalletBalanceStore = (
  tenantId: string,
  initialRecords: readonly ProfileWalletBalancePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): ProfileWalletBalanceStore => {
  const scope = {
    collection: profileWalletBalanceSchema.collection,
    domain: profileWalletBalanceSchema.domain,
    tenantId,
  };
  const store = cache.getStore<ProfileWalletBalancePrototype>(
    scope,
    () =>
      createTenantProjectionStore<ProfileWalletBalancePrototype>(
        scope,
        (record) => String(record[profileWalletBalanceSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectProfileWalletBalanceLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<ProfileWalletBalancePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: ProfileWalletBalanceStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<ProfileWalletBalancePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createProfileWalletBalanceStore(tenantId, [], cache);
  return createLiveProjectionBinding<ProfileWalletBalancePrototype>({
    ...bindingOptions,
    scope: {
      collection: profileWalletBalanceSchema.collection,
      domain: profileWalletBalanceSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerProfileWalletBalanceStore = (
  tenantId: string,
  store: ProfileWalletBalanceStore = createProfileWalletBalanceStore(tenantId)
): ProfileWalletBalanceStore => {
  prototypeStoreRegistry.register("billing.profile_wallet_balances", store);
  return store;
};

export const useProfileWalletBalanceSnapshot = () => prototypeStoreRegistry.use("billing.profile_wallet_balances");

export const profileWalletBalanceFixtures = {
  empty: (): ProfileWalletBalancePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly ProfileWalletBalancePrototype[] =>
    createCachedProfileWalletBalanceDummyRecords(tenantId, 8, { seed: "billing.profile_wallet_balances:happy" }),
  invalidMissingId: (): ProfileWalletBalancePrototype[] =>
    createProfileWalletBalanceDummyFactory({ seed: "billing.profile_wallet_balances:invalid" }).list(1, () => ({
      [profileWalletBalanceSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly ProfileWalletBalancePrototype[] =>
    createCachedProfileWalletBalanceDummyRecords(tenantId, count, { seed: "billing.profile_wallet_balances:high-volume" }),
};

export const profileWalletBalanceFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<ProfileWalletBalancePrototype> => ({
    collection: profileWalletBalanceSchema.collection,
    domain: profileWalletBalanceSchema.domain,
    name: "billing.profile_wallet_balances.empty",
    records: profileWalletBalanceFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<ProfileWalletBalancePrototype> => ({
    collection: profileWalletBalanceSchema.collection,
    domain: profileWalletBalanceSchema.domain,
    name: "billing.profile_wallet_balances.happyPath",
    records: profileWalletBalanceFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<ProfileWalletBalancePrototype> => ({
    collection: profileWalletBalanceSchema.collection,
    domain: profileWalletBalanceSchema.domain,
    name: "billing.profile_wallet_balances.invalidMissingId",
    records: profileWalletBalanceFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<ProfileWalletBalancePrototype> => ({
    collection: profileWalletBalanceSchema.collection,
    domain: profileWalletBalanceSchema.domain,
    name: "billing.profile_wallet_balances.highVolume",
    records: profileWalletBalanceFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const profileWalletBalanceBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "billing.profile_wallet_balances.dummy.list.1000",
    records: 1000,
    run: () => createCachedProfileWalletBalanceDummyRecords("tenant-benchmark", 1000, { seed: "billing.profile_wallet_balances:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "billing.profile_wallet_balances.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createProfileWalletBalanceDummyFactory({ seed: "billing.profile_wallet_balances:bench-store" }).list(1000);
      const store = createProfileWalletBalanceStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.profile_wallet_balances.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createProfileWalletBalanceDummyFactory({ seed: "billing.profile_wallet_balances:bench-apply" }).list(1000);
      const store = createProfileWalletBalanceStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[profileWalletBalanceSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: profileWalletBalanceSchema.collection,
          domain: profileWalletBalanceSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.profile_wallet_balances.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createProfileWalletBalanceDummyFactory({ seed: "billing.profile_wallet_balances:bench-reset" }).list(1000);
      const store = createProfileWalletBalanceStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.profile_wallet_balances.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createProfileWalletBalanceDummyFactory({ seed: "billing.profile_wallet_balances:bench-selector" }).list(1000);
      const store = createProfileWalletBalanceStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[profileWalletBalanceSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type RefundPrototype = Record<string, unknown>;

/** Typed field shape for billing.v1.Refund. Use it to narrow RefundPrototype records. */
export type RefundFields = {
  readonly "amount_minor": number;
  readonly "currency": string;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "payment_id": string;
  readonly "reference": string;
  readonly "status": "REFUND_STATUS_PENDING" | "REFUND_STATUS_SUCCEEDED" | "REFUND_STATUS_FAILED";
};

export const refundFieldOrder = ["id","organization_id","payment_id","reference","status","amount_minor","currency"] as const satisfies readonly (keyof RefundFields)[];

export const refundRequiredFields = ["amount_minor","currency","id","organization_id","payment_id","reference","status"] as const satisfies readonly (keyof RefundFields)[];

export type RefundStore = TenantProjectionStore<RefundPrototype>;

export const refundSchema = {
  "collection": "refunds",
  "domain": "billing",
  "fields": [
    {
      "kind": "int",
      "name": "amount_minor"
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "payment_id"
    },
    {
      "kind": "string",
      "name": "reference"
    },
    {
      "enumValues": [
        "REFUND_STATUS_PENDING",
        "REFUND_STATUS_SUCCEEDED",
        "REFUND_STATUS_FAILED"
      ],
      "kind": "enum",
      "name": "status"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createRefundDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<RefundPrototype>(refundSchema, options);

export const refundRuntimeConstants = {
  collection: refundSchema.collection,
  domain: refundSchema.domain,
  idField: refundSchema.idField ?? "id",
  key: "billing.refunds",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedRefundDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly RefundPrototype[] =>
  cache.getDummyRecords(refundSchema, { ...options, tenantId }, count, () =>
    createRefundDummyFactory({ ...options, tenantId }).list(count)
  );

export const createRefundStore = (
  tenantId: string,
  initialRecords: readonly RefundPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): RefundStore => {
  const scope = {
    collection: refundSchema.collection,
    domain: refundSchema.domain,
    tenantId,
  };
  const store = cache.getStore<RefundPrototype>(
    scope,
    () =>
      createTenantProjectionStore<RefundPrototype>(
        scope,
        (record) => String(record[refundSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectRefundLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<RefundPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: RefundStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<RefundPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createRefundStore(tenantId, [], cache);
  return createLiveProjectionBinding<RefundPrototype>({
    ...bindingOptions,
    scope: {
      collection: refundSchema.collection,
      domain: refundSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerRefundStore = (
  tenantId: string,
  store: RefundStore = createRefundStore(tenantId)
): RefundStore => {
  prototypeStoreRegistry.register("billing.refunds", store);
  return store;
};

export const useRefundSnapshot = () => prototypeStoreRegistry.use("billing.refunds");

export const refundFixtures = {
  empty: (): RefundPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly RefundPrototype[] =>
    createCachedRefundDummyRecords(tenantId, 8, { seed: "billing.refunds:happy" }),
  invalidMissingId: (): RefundPrototype[] =>
    createRefundDummyFactory({ seed: "billing.refunds:invalid" }).list(1, () => ({
      [refundSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly RefundPrototype[] =>
    createCachedRefundDummyRecords(tenantId, count, { seed: "billing.refunds:high-volume" }),
};

export const refundFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<RefundPrototype> => ({
    collection: refundSchema.collection,
    domain: refundSchema.domain,
    name: "billing.refunds.empty",
    records: refundFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<RefundPrototype> => ({
    collection: refundSchema.collection,
    domain: refundSchema.domain,
    name: "billing.refunds.happyPath",
    records: refundFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<RefundPrototype> => ({
    collection: refundSchema.collection,
    domain: refundSchema.domain,
    name: "billing.refunds.invalidMissingId",
    records: refundFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<RefundPrototype> => ({
    collection: refundSchema.collection,
    domain: refundSchema.domain,
    name: "billing.refunds.highVolume",
    records: refundFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const refundBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "billing.refunds.dummy.list.1000",
    records: 1000,
    run: () => createCachedRefundDummyRecords("tenant-benchmark", 1000, { seed: "billing.refunds:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "billing.refunds.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createRefundDummyFactory({ seed: "billing.refunds:bench-store" }).list(1000);
      const store = createRefundStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.refunds.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createRefundDummyFactory({ seed: "billing.refunds:bench-apply" }).list(1000);
      const store = createRefundStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[refundSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: refundSchema.collection,
          domain: refundSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.refunds.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createRefundDummyFactory({ seed: "billing.refunds:bench-reset" }).list(1000);
      const store = createRefundStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.refunds.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createRefundDummyFactory({ seed: "billing.refunds:bench-selector" }).list(1000);
      const store = createRefundStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[refundSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type SubscriptionPrototype = Record<string, unknown>;

/** Typed field shape for billing.v1.Subscription. Use it to narrow SubscriptionPrototype records. */
export type SubscriptionFields = {
  readonly "amount_minor": number;
  readonly "billing_cycle": string;
  readonly "currency": string;
  readonly "current_period_end"?: string | null;
  readonly "current_period_start"?: string | null;
  readonly "delivery_fee_waiver": boolean;
  readonly "free_delivery_used": boolean;
  readonly "grace_until"?: string | null;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "plan_code": string;
  readonly "profile_id": string;
  readonly "status": "SUBSCRIPTION_STATUS_ACTIVE" | "SUBSCRIPTION_STATUS_PAST_DUE" | "SUBSCRIPTION_STATUS_CANCEL_AT_PERIOD_END" | "SUBSCRIPTION_STATUS_CANCELLED" | "SUBSCRIPTION_STATUS_PENDING";
};

export const subscriptionFieldOrder = ["id","organization_id","profile_id","plan_code","billing_cycle","status","current_period_start","current_period_end","grace_until","delivery_fee_waiver","amount_minor","currency","free_delivery_used"] as const satisfies readonly (keyof SubscriptionFields)[];

export const subscriptionRequiredFields = ["amount_minor","billing_cycle","currency","delivery_fee_waiver","free_delivery_used","id","organization_id","plan_code","profile_id","status"] as const satisfies readonly (keyof SubscriptionFields)[];

export type SubscriptionStore = TenantProjectionStore<SubscriptionPrototype>;

export const subscriptionSchema = {
  "collection": "subscriptions",
  "domain": "billing",
  "fields": [
    {
      "kind": "int",
      "name": "amount_minor"
    },
    {
      "kind": "string",
      "name": "billing_cycle"
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "timestamp",
      "name": "current_period_end",
      "optional": true
    },
    {
      "kind": "timestamp",
      "name": "current_period_start",
      "optional": true
    },
    {
      "kind": "bool",
      "name": "delivery_fee_waiver"
    },
    {
      "kind": "bool",
      "name": "free_delivery_used"
    },
    {
      "kind": "timestamp",
      "name": "grace_until",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "plan_code"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    },
    {
      "enumValues": [
        "SUBSCRIPTION_STATUS_ACTIVE",
        "SUBSCRIPTION_STATUS_PAST_DUE",
        "SUBSCRIPTION_STATUS_CANCEL_AT_PERIOD_END",
        "SUBSCRIPTION_STATUS_CANCELLED",
        "SUBSCRIPTION_STATUS_PENDING"
      ],
      "kind": "enum",
      "name": "status"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createSubscriptionDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<SubscriptionPrototype>(subscriptionSchema, options);

export const subscriptionRuntimeConstants = {
  collection: subscriptionSchema.collection,
  domain: subscriptionSchema.domain,
  idField: subscriptionSchema.idField ?? "id",
  key: "billing.subscriptions",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedSubscriptionDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly SubscriptionPrototype[] =>
  cache.getDummyRecords(subscriptionSchema, { ...options, tenantId }, count, () =>
    createSubscriptionDummyFactory({ ...options, tenantId }).list(count)
  );

export const createSubscriptionStore = (
  tenantId: string,
  initialRecords: readonly SubscriptionPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): SubscriptionStore => {
  const scope = {
    collection: subscriptionSchema.collection,
    domain: subscriptionSchema.domain,
    tenantId,
  };
  const store = cache.getStore<SubscriptionPrototype>(
    scope,
    () =>
      createTenantProjectionStore<SubscriptionPrototype>(
        scope,
        (record) => String(record[subscriptionSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectSubscriptionLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<SubscriptionPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: SubscriptionStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<SubscriptionPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createSubscriptionStore(tenantId, [], cache);
  return createLiveProjectionBinding<SubscriptionPrototype>({
    ...bindingOptions,
    scope: {
      collection: subscriptionSchema.collection,
      domain: subscriptionSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerSubscriptionStore = (
  tenantId: string,
  store: SubscriptionStore = createSubscriptionStore(tenantId)
): SubscriptionStore => {
  prototypeStoreRegistry.register("billing.subscriptions", store);
  return store;
};

export const useSubscriptionSnapshot = () => prototypeStoreRegistry.use("billing.subscriptions");

export const subscriptionFixtures = {
  empty: (): SubscriptionPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly SubscriptionPrototype[] =>
    createCachedSubscriptionDummyRecords(tenantId, 8, { seed: "billing.subscriptions:happy" }),
  invalidMissingId: (): SubscriptionPrototype[] =>
    createSubscriptionDummyFactory({ seed: "billing.subscriptions:invalid" }).list(1, () => ({
      [subscriptionSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly SubscriptionPrototype[] =>
    createCachedSubscriptionDummyRecords(tenantId, count, { seed: "billing.subscriptions:high-volume" }),
};

export const subscriptionFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<SubscriptionPrototype> => ({
    collection: subscriptionSchema.collection,
    domain: subscriptionSchema.domain,
    name: "billing.subscriptions.empty",
    records: subscriptionFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<SubscriptionPrototype> => ({
    collection: subscriptionSchema.collection,
    domain: subscriptionSchema.domain,
    name: "billing.subscriptions.happyPath",
    records: subscriptionFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<SubscriptionPrototype> => ({
    collection: subscriptionSchema.collection,
    domain: subscriptionSchema.domain,
    name: "billing.subscriptions.invalidMissingId",
    records: subscriptionFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<SubscriptionPrototype> => ({
    collection: subscriptionSchema.collection,
    domain: subscriptionSchema.domain,
    name: "billing.subscriptions.highVolume",
    records: subscriptionFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const subscriptionBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "billing.subscriptions.dummy.list.1000",
    records: 1000,
    run: () => createCachedSubscriptionDummyRecords("tenant-benchmark", 1000, { seed: "billing.subscriptions:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "billing.subscriptions.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createSubscriptionDummyFactory({ seed: "billing.subscriptions:bench-store" }).list(1000);
      const store = createSubscriptionStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.subscriptions.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createSubscriptionDummyFactory({ seed: "billing.subscriptions:bench-apply" }).list(1000);
      const store = createSubscriptionStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[subscriptionSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: subscriptionSchema.collection,
          domain: subscriptionSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.subscriptions.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createSubscriptionDummyFactory({ seed: "billing.subscriptions:bench-reset" }).list(1000);
      const store = createSubscriptionStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "billing.subscriptions.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createSubscriptionDummyFactory({ seed: "billing.subscriptions:bench-selector" }).list(1000);
      const store = createSubscriptionStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[subscriptionSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type DeliveryPrototype = Record<string, unknown>;

/** Typed field shape for delivery.v1.Delivery. Use it to narrow DeliveryPrototype records. */
export type DeliveryFields = {
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "customer_id": string;
  readonly "customer_profile_id": string;
  readonly "delivery_option": "DELIVERY_OPTION_STANDARD" | "DELIVERY_OPTION_EXPRESS" | "DELIVERY_OPTION_CHOWDASH" | "DELIVERY_OPTION_SELF_PICKUP" | "DELIVERY_OPTION_RECURRING";
  readonly "id": string;
  readonly "merchant_display_name": string;
  readonly "merchant_profile_id": string;
  readonly "organization_id": string;
  readonly "pay_total_minor": number;
  readonly "recurrence_key": string;
  readonly "restaurant_id": string;
  readonly "rider_id": string;
  readonly "scheduled_at"?: string | null;
  readonly "source_app": "DELIVERY_SOURCE_APP_CHOOSECHOW" | "DELIVERY_SOURCE_APP_CHOWDASH" | "DELIVERY_SOURCE_APP_ADMIN";
  readonly "source_order_id": string;
  readonly "stage": "DELIVERY_STAGE_PICKUP" | "DELIVERY_STAGE_TRANSIT" | "DELIVERY_STAGE_DROPOFF" | "DELIVERY_STAGE_DONE" | "DELIVERY_STAGE_PAUSED" | "DELIVERY_STAGE_CANCELLED";
  readonly "updated_at"?: string | null;
};

export const deliveryFieldOrder = ["id","organization_id","created_at","updated_at","rider_id","customer_id","restaurant_id","stage","pay_total_minor","currency","customer_profile_id","merchant_profile_id","source_app","source_order_id","delivery_option","scheduled_at","recurrence_key","merchant_display_name"] as const satisfies readonly (keyof DeliveryFields)[];

export const deliveryRequiredFields = ["currency","customer_id","customer_profile_id","delivery_option","id","merchant_display_name","merchant_profile_id","organization_id","pay_total_minor","recurrence_key","restaurant_id","rider_id","source_app","source_order_id","stage"] as const satisfies readonly (keyof DeliveryFields)[];

export type DeliveryStore = TenantProjectionStore<DeliveryPrototype>;

export const deliverySchema = {
  "collection": "deliveries",
  "domain": "delivery",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "customer_id"
    },
    {
      "kind": "uuid",
      "name": "customer_profile_id"
    },
    {
      "enumValues": [
        "DELIVERY_OPTION_STANDARD",
        "DELIVERY_OPTION_EXPRESS",
        "DELIVERY_OPTION_CHOWDASH",
        "DELIVERY_OPTION_SELF_PICKUP",
        "DELIVERY_OPTION_RECURRING"
      ],
      "kind": "enum",
      "name": "delivery_option"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "merchant_display_name"
    },
    {
      "kind": "uuid",
      "name": "merchant_profile_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "int",
      "name": "pay_total_minor"
    },
    {
      "kind": "string",
      "name": "recurrence_key"
    },
    {
      "kind": "uuid",
      "name": "restaurant_id"
    },
    {
      "kind": "uuid",
      "name": "rider_id"
    },
    {
      "kind": "timestamp",
      "name": "scheduled_at",
      "optional": true
    },
    {
      "enumValues": [
        "DELIVERY_SOURCE_APP_CHOOSECHOW",
        "DELIVERY_SOURCE_APP_CHOWDASH",
        "DELIVERY_SOURCE_APP_ADMIN"
      ],
      "kind": "enum",
      "name": "source_app"
    },
    {
      "kind": "uuid",
      "name": "source_order_id"
    },
    {
      "enumValues": [
        "DELIVERY_STAGE_PICKUP",
        "DELIVERY_STAGE_TRANSIT",
        "DELIVERY_STAGE_DROPOFF",
        "DELIVERY_STAGE_DONE",
        "DELIVERY_STAGE_PAUSED",
        "DELIVERY_STAGE_CANCELLED"
      ],
      "kind": "enum",
      "name": "stage"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createDeliveryDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<DeliveryPrototype>(deliverySchema, options);

export const deliveryRuntimeConstants = {
  collection: deliverySchema.collection,
  domain: deliverySchema.domain,
  idField: deliverySchema.idField ?? "id",
  key: "delivery.deliveries",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedDeliveryDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly DeliveryPrototype[] =>
  cache.getDummyRecords(deliverySchema, { ...options, tenantId }, count, () =>
    createDeliveryDummyFactory({ ...options, tenantId }).list(count)
  );

export const createDeliveryStore = (
  tenantId: string,
  initialRecords: readonly DeliveryPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): DeliveryStore => {
  const scope = {
    collection: deliverySchema.collection,
    domain: deliverySchema.domain,
    tenantId,
  };
  const store = cache.getStore<DeliveryPrototype>(
    scope,
    () =>
      createTenantProjectionStore<DeliveryPrototype>(
        scope,
        (record) => String(record[deliverySchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectDeliveryLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<DeliveryPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: DeliveryStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<DeliveryPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createDeliveryStore(tenantId, [], cache);
  return createLiveProjectionBinding<DeliveryPrototype>({
    ...bindingOptions,
    scope: {
      collection: deliverySchema.collection,
      domain: deliverySchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerDeliveryStore = (
  tenantId: string,
  store: DeliveryStore = createDeliveryStore(tenantId)
): DeliveryStore => {
  prototypeStoreRegistry.register("delivery.deliveries", store);
  return store;
};

export const useDeliverySnapshot = () => prototypeStoreRegistry.use("delivery.deliveries");

export const deliveryFixtures = {
  empty: (): DeliveryPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly DeliveryPrototype[] =>
    createCachedDeliveryDummyRecords(tenantId, 8, { seed: "delivery.deliveries:happy" }),
  invalidMissingId: (): DeliveryPrototype[] =>
    createDeliveryDummyFactory({ seed: "delivery.deliveries:invalid" }).list(1, () => ({
      [deliverySchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly DeliveryPrototype[] =>
    createCachedDeliveryDummyRecords(tenantId, count, { seed: "delivery.deliveries:high-volume" }),
};

export const deliveryFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<DeliveryPrototype> => ({
    collection: deliverySchema.collection,
    domain: deliverySchema.domain,
    name: "delivery.deliveries.empty",
    records: deliveryFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<DeliveryPrototype> => ({
    collection: deliverySchema.collection,
    domain: deliverySchema.domain,
    name: "delivery.deliveries.happyPath",
    records: deliveryFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<DeliveryPrototype> => ({
    collection: deliverySchema.collection,
    domain: deliverySchema.domain,
    name: "delivery.deliveries.invalidMissingId",
    records: deliveryFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<DeliveryPrototype> => ({
    collection: deliverySchema.collection,
    domain: deliverySchema.domain,
    name: "delivery.deliveries.highVolume",
    records: deliveryFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const deliveryBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "delivery.deliveries.dummy.list.1000",
    records: 1000,
    run: () => createCachedDeliveryDummyRecords("tenant-benchmark", 1000, { seed: "delivery.deliveries:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "delivery.deliveries.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createDeliveryDummyFactory({ seed: "delivery.deliveries:bench-store" }).list(1000);
      const store = createDeliveryStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "delivery.deliveries.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createDeliveryDummyFactory({ seed: "delivery.deliveries:bench-apply" }).list(1000);
      const store = createDeliveryStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[deliverySchema.idField ?? "id"] ?? index);
        store.apply({
          collection: deliverySchema.collection,
          domain: deliverySchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "delivery.deliveries.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createDeliveryDummyFactory({ seed: "delivery.deliveries:bench-reset" }).list(1000);
      const store = createDeliveryStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "delivery.deliveries.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createDeliveryDummyFactory({ seed: "delivery.deliveries:bench-selector" }).list(1000);
      const store = createDeliveryStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[deliverySchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type DiscoverChefPrototype = Record<string, unknown>;

/** Typed field shape for discovery.v1.DiscoverChef. Use it to narrow DiscoverChefPrototype records. */
export type DiscoverChefFields = {
  readonly "area": string;
  readonly "avatar": string;
  readonly "badge": string;
  readonly "city"?: string | null;
  readonly "city_ref"?: string | null;
  readonly "cuisine": string;
  readonly "dish_count": number;
  readonly "distance_km"?: number | null;
  readonly "id": string;
  readonly "name": string;
  readonly "open": boolean;
  readonly "orders": number;
  readonly "rating": number;
  readonly "reviews": number;
};

export const discoverChefFieldOrder = ["id","name","cuisine","area","open","avatar","rating","reviews","orders","dish_count","badge","city_ref","city","distance_km"] as const satisfies readonly (keyof DiscoverChefFields)[];

export const discoverChefRequiredFields = ["area","avatar","badge","cuisine","dish_count","id","name","open","orders","rating","reviews"] as const satisfies readonly (keyof DiscoverChefFields)[];

export type DiscoverChefStore = TenantProjectionStore<DiscoverChefPrototype>;

export const discoverChefSchema = {
  "collection": "discover_chefs",
  "domain": "discovery",
  "fields": [
    {
      "kind": "string",
      "name": "area"
    },
    {
      "kind": "string",
      "name": "avatar"
    },
    {
      "kind": "string",
      "name": "badge"
    },
    {
      "kind": "string",
      "name": "city",
      "optional": true
    },
    {
      "kind": "string",
      "name": "city_ref",
      "optional": true
    },
    {
      "kind": "string",
      "name": "cuisine"
    },
    {
      "kind": "int",
      "name": "dish_count"
    },
    {
      "kind": "float",
      "name": "distance_km",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "name"
    },
    {
      "kind": "bool",
      "name": "open"
    },
    {
      "kind": "int",
      "name": "orders"
    },
    {
      "kind": "float",
      "name": "rating"
    },
    {
      "kind": "int",
      "name": "reviews"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createDiscoverChefDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<DiscoverChefPrototype>(discoverChefSchema, options);

export const discoverChefRuntimeConstants = {
  collection: discoverChefSchema.collection,
  domain: discoverChefSchema.domain,
  idField: discoverChefSchema.idField ?? "id",
  key: "discovery.discover_chefs",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedDiscoverChefDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly DiscoverChefPrototype[] =>
  cache.getDummyRecords(discoverChefSchema, { ...options, tenantId }, count, () =>
    createDiscoverChefDummyFactory({ ...options, tenantId }).list(count)
  );

export const createDiscoverChefStore = (
  tenantId: string,
  initialRecords: readonly DiscoverChefPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): DiscoverChefStore => {
  const scope = {
    collection: discoverChefSchema.collection,
    domain: discoverChefSchema.domain,
    tenantId,
  };
  const store = cache.getStore<DiscoverChefPrototype>(
    scope,
    () =>
      createTenantProjectionStore<DiscoverChefPrototype>(
        scope,
        (record) => String(record[discoverChefSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectDiscoverChefLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<DiscoverChefPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: DiscoverChefStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<DiscoverChefPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createDiscoverChefStore(tenantId, [], cache);
  return createLiveProjectionBinding<DiscoverChefPrototype>({
    ...bindingOptions,
    scope: {
      collection: discoverChefSchema.collection,
      domain: discoverChefSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerDiscoverChefStore = (
  tenantId: string,
  store: DiscoverChefStore = createDiscoverChefStore(tenantId)
): DiscoverChefStore => {
  prototypeStoreRegistry.register("discovery.discover_chefs", store);
  return store;
};

export const useDiscoverChefSnapshot = () => prototypeStoreRegistry.use("discovery.discover_chefs");

export const discoverChefFixtures = {
  empty: (): DiscoverChefPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly DiscoverChefPrototype[] =>
    createCachedDiscoverChefDummyRecords(tenantId, 8, { seed: "discovery.discover_chefs:happy" }),
  invalidMissingId: (): DiscoverChefPrototype[] =>
    createDiscoverChefDummyFactory({ seed: "discovery.discover_chefs:invalid" }).list(1, () => ({
      [discoverChefSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly DiscoverChefPrototype[] =>
    createCachedDiscoverChefDummyRecords(tenantId, count, { seed: "discovery.discover_chefs:high-volume" }),
};

export const discoverChefFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<DiscoverChefPrototype> => ({
    collection: discoverChefSchema.collection,
    domain: discoverChefSchema.domain,
    name: "discovery.discover_chefs.empty",
    records: discoverChefFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<DiscoverChefPrototype> => ({
    collection: discoverChefSchema.collection,
    domain: discoverChefSchema.domain,
    name: "discovery.discover_chefs.happyPath",
    records: discoverChefFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<DiscoverChefPrototype> => ({
    collection: discoverChefSchema.collection,
    domain: discoverChefSchema.domain,
    name: "discovery.discover_chefs.invalidMissingId",
    records: discoverChefFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<DiscoverChefPrototype> => ({
    collection: discoverChefSchema.collection,
    domain: discoverChefSchema.domain,
    name: "discovery.discover_chefs.highVolume",
    records: discoverChefFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const discoverChefBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "discovery.discover_chefs.dummy.list.1000",
    records: 1000,
    run: () => createCachedDiscoverChefDummyRecords("tenant-benchmark", 1000, { seed: "discovery.discover_chefs:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "discovery.discover_chefs.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverChefDummyFactory({ seed: "discovery.discover_chefs:bench-store" }).list(1000);
      const store = createDiscoverChefStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "discovery.discover_chefs.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverChefDummyFactory({ seed: "discovery.discover_chefs:bench-apply" }).list(1000);
      const store = createDiscoverChefStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[discoverChefSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: discoverChefSchema.collection,
          domain: discoverChefSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "discovery.discover_chefs.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverChefDummyFactory({ seed: "discovery.discover_chefs:bench-reset" }).list(1000);
      const store = createDiscoverChefStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "discovery.discover_chefs.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverChefDummyFactory({ seed: "discovery.discover_chefs:bench-selector" }).list(1000);
      const store = createDiscoverChefStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[discoverChefSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type DiscoverDishPrototype = Record<string, unknown>;

/** Typed field shape for discovery.v1.DiscoverDish. Use it to narrow DiscoverDishPrototype records. */
export type DiscoverDishFields = {
  readonly "allergens": readonly (string)[];
  readonly "available_days": readonly (number)[];
  readonly "category": string;
  readonly "chef": string;
  readonly "chef_avatar": string;
  readonly "chef_badge": string;
  readonly "chef_id": string;
  readonly "chef_profile_id": string;
  readonly "cook_time_minutes": number;
  readonly "currency": string;
  readonly "description": string;
  readonly "id": string;
  readonly "image": string;
  readonly "ingredients": readonly (string)[];
  readonly "menu_id": string;
  readonly "name": string;
  readonly "price_minor": number;
  readonly "rating": number;
  readonly "reviews": number;
  readonly "status": string;
  readonly "stock_quantity"?: number | null;
};

export const discoverDishFieldOrder = ["id","name","image","price_minor","currency","category","chef","chef_avatar","chef_id","rating","reviews","chef_profile_id","allergens","stock_quantity","description","ingredients","cook_time_minutes","available_days","menu_id","status","chef_badge"] as const satisfies readonly (keyof DiscoverDishFields)[];

export const discoverDishRequiredFields = ["allergens","available_days","category","chef","chef_avatar","chef_badge","chef_id","chef_profile_id","cook_time_minutes","currency","description","id","image","ingredients","menu_id","name","price_minor","rating","reviews","status"] as const satisfies readonly (keyof DiscoverDishFields)[];

export type DiscoverDishStore = TenantProjectionStore<DiscoverDishPrototype>;

export const discoverDishSchema = {
  "collection": "discover_dishes",
  "domain": "discovery",
  "fields": [
    {
      "kind": "string",
      "name": "allergens",
      "repeated": true
    },
    {
      "kind": "int",
      "name": "available_days",
      "repeated": true
    },
    {
      "kind": "string",
      "name": "category"
    },
    {
      "kind": "string",
      "name": "chef"
    },
    {
      "kind": "string",
      "name": "chef_avatar"
    },
    {
      "kind": "string",
      "name": "chef_badge"
    },
    {
      "kind": "uuid",
      "name": "chef_id"
    },
    {
      "kind": "uuid",
      "name": "chef_profile_id"
    },
    {
      "kind": "int",
      "name": "cook_time_minutes"
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "text",
      "name": "description"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "image"
    },
    {
      "kind": "string",
      "name": "ingredients",
      "repeated": true
    },
    {
      "kind": "uuid",
      "name": "menu_id"
    },
    {
      "kind": "string",
      "name": "name"
    },
    {
      "kind": "int",
      "name": "price_minor"
    },
    {
      "kind": "float",
      "name": "rating"
    },
    {
      "kind": "int",
      "name": "reviews"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "int",
      "name": "stock_quantity",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createDiscoverDishDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<DiscoverDishPrototype>(discoverDishSchema, options);

export const discoverDishRuntimeConstants = {
  collection: discoverDishSchema.collection,
  domain: discoverDishSchema.domain,
  idField: discoverDishSchema.idField ?? "id",
  key: "discovery.discover_dishes",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedDiscoverDishDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly DiscoverDishPrototype[] =>
  cache.getDummyRecords(discoverDishSchema, { ...options, tenantId }, count, () =>
    createDiscoverDishDummyFactory({ ...options, tenantId }).list(count)
  );

export const createDiscoverDishStore = (
  tenantId: string,
  initialRecords: readonly DiscoverDishPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): DiscoverDishStore => {
  const scope = {
    collection: discoverDishSchema.collection,
    domain: discoverDishSchema.domain,
    tenantId,
  };
  const store = cache.getStore<DiscoverDishPrototype>(
    scope,
    () =>
      createTenantProjectionStore<DiscoverDishPrototype>(
        scope,
        (record) => String(record[discoverDishSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectDiscoverDishLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<DiscoverDishPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: DiscoverDishStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<DiscoverDishPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createDiscoverDishStore(tenantId, [], cache);
  return createLiveProjectionBinding<DiscoverDishPrototype>({
    ...bindingOptions,
    scope: {
      collection: discoverDishSchema.collection,
      domain: discoverDishSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerDiscoverDishStore = (
  tenantId: string,
  store: DiscoverDishStore = createDiscoverDishStore(tenantId)
): DiscoverDishStore => {
  prototypeStoreRegistry.register("discovery.discover_dishes", store);
  return store;
};

export const useDiscoverDishSnapshot = () => prototypeStoreRegistry.use("discovery.discover_dishes");

export const discoverDishFixtures = {
  empty: (): DiscoverDishPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly DiscoverDishPrototype[] =>
    createCachedDiscoverDishDummyRecords(tenantId, 8, { seed: "discovery.discover_dishes:happy" }),
  invalidMissingId: (): DiscoverDishPrototype[] =>
    createDiscoverDishDummyFactory({ seed: "discovery.discover_dishes:invalid" }).list(1, () => ({
      [discoverDishSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly DiscoverDishPrototype[] =>
    createCachedDiscoverDishDummyRecords(tenantId, count, { seed: "discovery.discover_dishes:high-volume" }),
};

export const discoverDishFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<DiscoverDishPrototype> => ({
    collection: discoverDishSchema.collection,
    domain: discoverDishSchema.domain,
    name: "discovery.discover_dishes.empty",
    records: discoverDishFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<DiscoverDishPrototype> => ({
    collection: discoverDishSchema.collection,
    domain: discoverDishSchema.domain,
    name: "discovery.discover_dishes.happyPath",
    records: discoverDishFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<DiscoverDishPrototype> => ({
    collection: discoverDishSchema.collection,
    domain: discoverDishSchema.domain,
    name: "discovery.discover_dishes.invalidMissingId",
    records: discoverDishFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<DiscoverDishPrototype> => ({
    collection: discoverDishSchema.collection,
    domain: discoverDishSchema.domain,
    name: "discovery.discover_dishes.highVolume",
    records: discoverDishFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const discoverDishBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "discovery.discover_dishes.dummy.list.1000",
    records: 1000,
    run: () => createCachedDiscoverDishDummyRecords("tenant-benchmark", 1000, { seed: "discovery.discover_dishes:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "discovery.discover_dishes.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverDishDummyFactory({ seed: "discovery.discover_dishes:bench-store" }).list(1000);
      const store = createDiscoverDishStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "discovery.discover_dishes.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverDishDummyFactory({ seed: "discovery.discover_dishes:bench-apply" }).list(1000);
      const store = createDiscoverDishStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[discoverDishSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: discoverDishSchema.collection,
          domain: discoverDishSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "discovery.discover_dishes.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverDishDummyFactory({ seed: "discovery.discover_dishes:bench-reset" }).list(1000);
      const store = createDiscoverDishStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "discovery.discover_dishes.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createDiscoverDishDummyFactory({ seed: "discovery.discover_dishes:bench-selector" }).list(1000);
      const store = createDiscoverDishStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[discoverDishSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type ObservationPrototype = Record<string, unknown>;

/** Typed field shape for logistics_runtime.v1.Observation. Use it to narrow ObservationPrototype records. */
export type ObservationFields = {
  readonly "arrived_at_ms": number;
  readonly "departed_at_ms": number;
  readonly "from_place_id": string;
  readonly "id": string;
  readonly "mode": string;
  readonly "service_seconds": number;
  readonly "to_place_id": string;
};

export const observationFieldOrder = ["id","from_place_id","to_place_id","mode","departed_at_ms","arrived_at_ms","service_seconds"] as const satisfies readonly (keyof ObservationFields)[];

export const observationRequiredFields = ["arrived_at_ms","departed_at_ms","from_place_id","id","mode","service_seconds","to_place_id"] as const satisfies readonly (keyof ObservationFields)[];

export type ObservationStore = TenantProjectionStore<ObservationPrototype>;

export const observationSchema = {
  "collection": "observations",
  "domain": "logistics_runtime",
  "fields": [
    {
      "kind": "int",
      "name": "arrived_at_ms"
    },
    {
      "kind": "int",
      "name": "departed_at_ms"
    },
    {
      "kind": "uuid",
      "name": "from_place_id"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "mode"
    },
    {
      "kind": "int",
      "name": "service_seconds"
    },
    {
      "kind": "uuid",
      "name": "to_place_id"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createObservationDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<ObservationPrototype>(observationSchema, options);

export const observationRuntimeConstants = {
  collection: observationSchema.collection,
  domain: observationSchema.domain,
  idField: observationSchema.idField ?? "id",
  key: "logistics_runtime.observations",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedObservationDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly ObservationPrototype[] =>
  cache.getDummyRecords(observationSchema, { ...options, tenantId }, count, () =>
    createObservationDummyFactory({ ...options, tenantId }).list(count)
  );

export const createObservationStore = (
  tenantId: string,
  initialRecords: readonly ObservationPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): ObservationStore => {
  const scope = {
    collection: observationSchema.collection,
    domain: observationSchema.domain,
    tenantId,
  };
  const store = cache.getStore<ObservationPrototype>(
    scope,
    () =>
      createTenantProjectionStore<ObservationPrototype>(
        scope,
        (record) => String(record[observationSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectObservationLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<ObservationPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: ObservationStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<ObservationPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createObservationStore(tenantId, [], cache);
  return createLiveProjectionBinding<ObservationPrototype>({
    ...bindingOptions,
    scope: {
      collection: observationSchema.collection,
      domain: observationSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerObservationStore = (
  tenantId: string,
  store: ObservationStore = createObservationStore(tenantId)
): ObservationStore => {
  prototypeStoreRegistry.register("logistics_runtime.observations", store);
  return store;
};

export const useObservationSnapshot = () => prototypeStoreRegistry.use("logistics_runtime.observations");

export const observationFixtures = {
  empty: (): ObservationPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly ObservationPrototype[] =>
    createCachedObservationDummyRecords(tenantId, 8, { seed: "logistics_runtime.observations:happy" }),
  invalidMissingId: (): ObservationPrototype[] =>
    createObservationDummyFactory({ seed: "logistics_runtime.observations:invalid" }).list(1, () => ({
      [observationSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly ObservationPrototype[] =>
    createCachedObservationDummyRecords(tenantId, count, { seed: "logistics_runtime.observations:high-volume" }),
};

export const observationFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<ObservationPrototype> => ({
    collection: observationSchema.collection,
    domain: observationSchema.domain,
    name: "logistics_runtime.observations.empty",
    records: observationFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<ObservationPrototype> => ({
    collection: observationSchema.collection,
    domain: observationSchema.domain,
    name: "logistics_runtime.observations.happyPath",
    records: observationFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<ObservationPrototype> => ({
    collection: observationSchema.collection,
    domain: observationSchema.domain,
    name: "logistics_runtime.observations.invalidMissingId",
    records: observationFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<ObservationPrototype> => ({
    collection: observationSchema.collection,
    domain: observationSchema.domain,
    name: "logistics_runtime.observations.highVolume",
    records: observationFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const observationBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "logistics_runtime.observations.dummy.list.1000",
    records: 1000,
    run: () => createCachedObservationDummyRecords("tenant-benchmark", 1000, { seed: "logistics_runtime.observations:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "logistics_runtime.observations.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createObservationDummyFactory({ seed: "logistics_runtime.observations:bench-store" }).list(1000);
      const store = createObservationStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.observations.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createObservationDummyFactory({ seed: "logistics_runtime.observations:bench-apply" }).list(1000);
      const store = createObservationStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[observationSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: observationSchema.collection,
          domain: observationSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.observations.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createObservationDummyFactory({ seed: "logistics_runtime.observations:bench-reset" }).list(1000);
      const store = createObservationStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.observations.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createObservationDummyFactory({ seed: "logistics_runtime.observations:bench-selector" }).list(1000);
      const store = createObservationStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[observationSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type StopPrototype = Record<string, unknown>;

/** Typed field shape for logistics_runtime.v1.Stop. Use it to narrow StopPrototype records. */
export type StopFields = {
  readonly "id": string;
  readonly "kind": string;
  readonly "label": string;
  readonly "latest_at_ms": number;
  readonly "load_delta": number;
  readonly "point"?: Record<string, unknown> | null;
  readonly "ready_at_ms": number;
  readonly "requires_stop_id": string;
  readonly "service_seconds": number;
};

export const stopFieldOrder = ["id","label","point","kind","ready_at_ms","latest_at_ms","service_seconds","load_delta","requires_stop_id"] as const satisfies readonly (keyof StopFields)[];

export const stopRequiredFields = ["id","kind","label","latest_at_ms","load_delta","ready_at_ms","requires_stop_id","service_seconds"] as const satisfies readonly (keyof StopFields)[];

export type StopStore = TenantProjectionStore<StopPrototype>;

export const stopSchema = {
  "collection": "stops",
  "domain": "logistics_runtime",
  "fields": [
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "kind"
    },
    {
      "kind": "string",
      "name": "label"
    },
    {
      "kind": "int",
      "name": "latest_at_ms"
    },
    {
      "kind": "int",
      "name": "load_delta"
    },
    {
      "kind": "object",
      "name": "point",
      "optional": true
    },
    {
      "kind": "int",
      "name": "ready_at_ms"
    },
    {
      "kind": "uuid",
      "name": "requires_stop_id"
    },
    {
      "kind": "int",
      "name": "service_seconds"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createStopDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<StopPrototype>(stopSchema, options);

export const stopRuntimeConstants = {
  collection: stopSchema.collection,
  domain: stopSchema.domain,
  idField: stopSchema.idField ?? "id",
  key: "logistics_runtime.stops",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedStopDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly StopPrototype[] =>
  cache.getDummyRecords(stopSchema, { ...options, tenantId }, count, () =>
    createStopDummyFactory({ ...options, tenantId }).list(count)
  );

export const createStopStore = (
  tenantId: string,
  initialRecords: readonly StopPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): StopStore => {
  const scope = {
    collection: stopSchema.collection,
    domain: stopSchema.domain,
    tenantId,
  };
  const store = cache.getStore<StopPrototype>(
    scope,
    () =>
      createTenantProjectionStore<StopPrototype>(
        scope,
        (record) => String(record[stopSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectStopLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<StopPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: StopStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<StopPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createStopStore(tenantId, [], cache);
  return createLiveProjectionBinding<StopPrototype>({
    ...bindingOptions,
    scope: {
      collection: stopSchema.collection,
      domain: stopSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerStopStore = (
  tenantId: string,
  store: StopStore = createStopStore(tenantId)
): StopStore => {
  prototypeStoreRegistry.register("logistics_runtime.stops", store);
  return store;
};

export const useStopSnapshot = () => prototypeStoreRegistry.use("logistics_runtime.stops");

export const stopFixtures = {
  empty: (): StopPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly StopPrototype[] =>
    createCachedStopDummyRecords(tenantId, 8, { seed: "logistics_runtime.stops:happy" }),
  invalidMissingId: (): StopPrototype[] =>
    createStopDummyFactory({ seed: "logistics_runtime.stops:invalid" }).list(1, () => ({
      [stopSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly StopPrototype[] =>
    createCachedStopDummyRecords(tenantId, count, { seed: "logistics_runtime.stops:high-volume" }),
};

export const stopFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<StopPrototype> => ({
    collection: stopSchema.collection,
    domain: stopSchema.domain,
    name: "logistics_runtime.stops.empty",
    records: stopFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<StopPrototype> => ({
    collection: stopSchema.collection,
    domain: stopSchema.domain,
    name: "logistics_runtime.stops.happyPath",
    records: stopFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<StopPrototype> => ({
    collection: stopSchema.collection,
    domain: stopSchema.domain,
    name: "logistics_runtime.stops.invalidMissingId",
    records: stopFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<StopPrototype> => ({
    collection: stopSchema.collection,
    domain: stopSchema.domain,
    name: "logistics_runtime.stops.highVolume",
    records: stopFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const stopBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "logistics_runtime.stops.dummy.list.1000",
    records: 1000,
    run: () => createCachedStopDummyRecords("tenant-benchmark", 1000, { seed: "logistics_runtime.stops:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "logistics_runtime.stops.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createStopDummyFactory({ seed: "logistics_runtime.stops:bench-store" }).list(1000);
      const store = createStopStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.stops.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createStopDummyFactory({ seed: "logistics_runtime.stops:bench-apply" }).list(1000);
      const store = createStopStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[stopSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: stopSchema.collection,
          domain: stopSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.stops.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createStopDummyFactory({ seed: "logistics_runtime.stops:bench-reset" }).list(1000);
      const store = createStopStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.stops.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createStopDummyFactory({ seed: "logistics_runtime.stops:bench-selector" }).list(1000);
      const store = createStopStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[stopSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type UnservedStopPrototype = Record<string, unknown>;

/** Typed field shape for logistics_runtime.v1.UnservedStop. Use it to narrow UnservedStopPrototype records. */
export type UnservedStopFields = {
  readonly "reason": string;
  readonly "stop_id": string;
};

export const unservedStopFieldOrder = ["stop_id","reason"] as const satisfies readonly (keyof UnservedStopFields)[];

export const unservedStopRequiredFields = ["reason","stop_id"] as const satisfies readonly (keyof UnservedStopFields)[];

export type UnservedStopStore = TenantProjectionStore<UnservedStopPrototype>;

export const unservedStopSchema = {
  "collection": "unserved_stops",
  "domain": "logistics_runtime",
  "fields": [
    {
      "kind": "string",
      "name": "reason"
    },
    {
      "kind": "uuid",
      "name": "stop_id"
    }
  ],
  "idField": "stop_id"
} as const satisfies DummySchemaSpec;

export const createUnservedStopDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<UnservedStopPrototype>(unservedStopSchema, options);

export const unservedStopRuntimeConstants = {
  collection: unservedStopSchema.collection,
  domain: unservedStopSchema.domain,
  idField: unservedStopSchema.idField ?? "id",
  key: "logistics_runtime.unserved_stops",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedUnservedStopDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly UnservedStopPrototype[] =>
  cache.getDummyRecords(unservedStopSchema, { ...options, tenantId }, count, () =>
    createUnservedStopDummyFactory({ ...options, tenantId }).list(count)
  );

export const createUnservedStopStore = (
  tenantId: string,
  initialRecords: readonly UnservedStopPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): UnservedStopStore => {
  const scope = {
    collection: unservedStopSchema.collection,
    domain: unservedStopSchema.domain,
    tenantId,
  };
  const store = cache.getStore<UnservedStopPrototype>(
    scope,
    () =>
      createTenantProjectionStore<UnservedStopPrototype>(
        scope,
        (record) => String(record[unservedStopSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectUnservedStopLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<UnservedStopPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: UnservedStopStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<UnservedStopPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createUnservedStopStore(tenantId, [], cache);
  return createLiveProjectionBinding<UnservedStopPrototype>({
    ...bindingOptions,
    scope: {
      collection: unservedStopSchema.collection,
      domain: unservedStopSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerUnservedStopStore = (
  tenantId: string,
  store: UnservedStopStore = createUnservedStopStore(tenantId)
): UnservedStopStore => {
  prototypeStoreRegistry.register("logistics_runtime.unserved_stops", store);
  return store;
};

export const useUnservedStopSnapshot = () => prototypeStoreRegistry.use("logistics_runtime.unserved_stops");

export const unservedStopFixtures = {
  empty: (): UnservedStopPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly UnservedStopPrototype[] =>
    createCachedUnservedStopDummyRecords(tenantId, 8, { seed: "logistics_runtime.unserved_stops:happy" }),
  invalidMissingId: (): UnservedStopPrototype[] =>
    createUnservedStopDummyFactory({ seed: "logistics_runtime.unserved_stops:invalid" }).list(1, () => ({
      [unservedStopSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly UnservedStopPrototype[] =>
    createCachedUnservedStopDummyRecords(tenantId, count, { seed: "logistics_runtime.unserved_stops:high-volume" }),
};

export const unservedStopFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<UnservedStopPrototype> => ({
    collection: unservedStopSchema.collection,
    domain: unservedStopSchema.domain,
    name: "logistics_runtime.unserved_stops.empty",
    records: unservedStopFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<UnservedStopPrototype> => ({
    collection: unservedStopSchema.collection,
    domain: unservedStopSchema.domain,
    name: "logistics_runtime.unserved_stops.happyPath",
    records: unservedStopFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<UnservedStopPrototype> => ({
    collection: unservedStopSchema.collection,
    domain: unservedStopSchema.domain,
    name: "logistics_runtime.unserved_stops.invalidMissingId",
    records: unservedStopFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<UnservedStopPrototype> => ({
    collection: unservedStopSchema.collection,
    domain: unservedStopSchema.domain,
    name: "logistics_runtime.unserved_stops.highVolume",
    records: unservedStopFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const unservedStopBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "logistics_runtime.unserved_stops.dummy.list.1000",
    records: 1000,
    run: () => createCachedUnservedStopDummyRecords("tenant-benchmark", 1000, { seed: "logistics_runtime.unserved_stops:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "logistics_runtime.unserved_stops.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createUnservedStopDummyFactory({ seed: "logistics_runtime.unserved_stops:bench-store" }).list(1000);
      const store = createUnservedStopStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.unserved_stops.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createUnservedStopDummyFactory({ seed: "logistics_runtime.unserved_stops:bench-apply" }).list(1000);
      const store = createUnservedStopStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[unservedStopSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: unservedStopSchema.collection,
          domain: unservedStopSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.unserved_stops.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createUnservedStopDummyFactory({ seed: "logistics_runtime.unserved_stops:bench-reset" }).list(1000);
      const store = createUnservedStopStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics_runtime.unserved_stops.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createUnservedStopDummyFactory({ seed: "logistics_runtime.unserved_stops:bench-selector" }).list(1000);
      const store = createUnservedStopStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[unservedStopSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type OfferCandidatePrototype = Record<string, unknown>;

/** Typed field shape for logistics.v1.OfferCandidate. Use it to narrow OfferCandidatePrototype records. */
export type OfferCandidateFields = {
  readonly "currency": string;
  readonly "delivery_id": string;
  readonly "offer_id": string;
  readonly "pay_total_minor": number;
  readonly "pickup_distance_meters": number;
  readonly "pickup_eta_seconds": number;
  readonly "rider_id": string;
  readonly "route"?: Record<string, unknown> | null;
  readonly "route_distance_meters": number;
  readonly "route_duration_seconds": number;
  readonly "score": number;
};

export const offerCandidateFieldOrder = ["offer_id","delivery_id","rider_id","pickup_distance_meters","pickup_eta_seconds","route_distance_meters","route_duration_seconds","pay_total_minor","currency","score","route"] as const satisfies readonly (keyof OfferCandidateFields)[];

export const offerCandidateRequiredFields = ["currency","delivery_id","offer_id","pay_total_minor","pickup_distance_meters","pickup_eta_seconds","rider_id","route_distance_meters","route_duration_seconds","score"] as const satisfies readonly (keyof OfferCandidateFields)[];

export type OfferCandidateStore = TenantProjectionStore<OfferCandidatePrototype>;

export const offerCandidateSchema = {
  "collection": "offer_candidates",
  "domain": "logistics",
  "fields": [
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "delivery_id"
    },
    {
      "kind": "uuid",
      "name": "offer_id"
    },
    {
      "kind": "int",
      "name": "pay_total_minor"
    },
    {
      "kind": "int",
      "name": "pickup_distance_meters"
    },
    {
      "kind": "int",
      "name": "pickup_eta_seconds"
    },
    {
      "kind": "uuid",
      "name": "rider_id"
    },
    {
      "kind": "object",
      "name": "route",
      "optional": true
    },
    {
      "kind": "int",
      "name": "route_distance_meters"
    },
    {
      "kind": "int",
      "name": "route_duration_seconds"
    },
    {
      "kind": "float",
      "name": "score"
    }
  ],
  "idField": "offer_id"
} as const satisfies DummySchemaSpec;

export const createOfferCandidateDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<OfferCandidatePrototype>(offerCandidateSchema, options);

export const offerCandidateRuntimeConstants = {
  collection: offerCandidateSchema.collection,
  domain: offerCandidateSchema.domain,
  idField: offerCandidateSchema.idField ?? "id",
  key: "logistics.offer_candidates",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedOfferCandidateDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly OfferCandidatePrototype[] =>
  cache.getDummyRecords(offerCandidateSchema, { ...options, tenantId }, count, () =>
    createOfferCandidateDummyFactory({ ...options, tenantId }).list(count)
  );

export const createOfferCandidateStore = (
  tenantId: string,
  initialRecords: readonly OfferCandidatePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): OfferCandidateStore => {
  const scope = {
    collection: offerCandidateSchema.collection,
    domain: offerCandidateSchema.domain,
    tenantId,
  };
  const store = cache.getStore<OfferCandidatePrototype>(
    scope,
    () =>
      createTenantProjectionStore<OfferCandidatePrototype>(
        scope,
        (record) => String(record[offerCandidateSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectOfferCandidateLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<OfferCandidatePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: OfferCandidateStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<OfferCandidatePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createOfferCandidateStore(tenantId, [], cache);
  return createLiveProjectionBinding<OfferCandidatePrototype>({
    ...bindingOptions,
    scope: {
      collection: offerCandidateSchema.collection,
      domain: offerCandidateSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerOfferCandidateStore = (
  tenantId: string,
  store: OfferCandidateStore = createOfferCandidateStore(tenantId)
): OfferCandidateStore => {
  prototypeStoreRegistry.register("logistics.offer_candidates", store);
  return store;
};

export const useOfferCandidateSnapshot = () => prototypeStoreRegistry.use("logistics.offer_candidates");

export const offerCandidateFixtures = {
  empty: (): OfferCandidatePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly OfferCandidatePrototype[] =>
    createCachedOfferCandidateDummyRecords(tenantId, 8, { seed: "logistics.offer_candidates:happy" }),
  invalidMissingId: (): OfferCandidatePrototype[] =>
    createOfferCandidateDummyFactory({ seed: "logistics.offer_candidates:invalid" }).list(1, () => ({
      [offerCandidateSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly OfferCandidatePrototype[] =>
    createCachedOfferCandidateDummyRecords(tenantId, count, { seed: "logistics.offer_candidates:high-volume" }),
};

export const offerCandidateFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<OfferCandidatePrototype> => ({
    collection: offerCandidateSchema.collection,
    domain: offerCandidateSchema.domain,
    name: "logistics.offer_candidates.empty",
    records: offerCandidateFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<OfferCandidatePrototype> => ({
    collection: offerCandidateSchema.collection,
    domain: offerCandidateSchema.domain,
    name: "logistics.offer_candidates.happyPath",
    records: offerCandidateFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<OfferCandidatePrototype> => ({
    collection: offerCandidateSchema.collection,
    domain: offerCandidateSchema.domain,
    name: "logistics.offer_candidates.invalidMissingId",
    records: offerCandidateFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<OfferCandidatePrototype> => ({
    collection: offerCandidateSchema.collection,
    domain: offerCandidateSchema.domain,
    name: "logistics.offer_candidates.highVolume",
    records: offerCandidateFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const offerCandidateBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "logistics.offer_candidates.dummy.list.1000",
    records: 1000,
    run: () => createCachedOfferCandidateDummyRecords("tenant-benchmark", 1000, { seed: "logistics.offer_candidates:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "logistics.offer_candidates.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createOfferCandidateDummyFactory({ seed: "logistics.offer_candidates:bench-store" }).list(1000);
      const store = createOfferCandidateStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics.offer_candidates.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createOfferCandidateDummyFactory({ seed: "logistics.offer_candidates:bench-apply" }).list(1000);
      const store = createOfferCandidateStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[offerCandidateSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: offerCandidateSchema.collection,
          domain: offerCandidateSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics.offer_candidates.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createOfferCandidateDummyFactory({ seed: "logistics.offer_candidates:bench-reset" }).list(1000);
      const store = createOfferCandidateStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics.offer_candidates.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createOfferCandidateDummyFactory({ seed: "logistics.offer_candidates:bench-selector" }).list(1000);
      const store = createOfferCandidateStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[offerCandidateSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type RiderLocationPrototype = Record<string, unknown>;

/** Typed field shape for logistics.v1.RiderLocation. Use it to narrow RiderLocationPrototype records. */
export type RiderLocationFields = {
  readonly "accuracy_meters": number;
  readonly "dash_session_id": string;
  readonly "heading_degrees": number;
  readonly "observed_at"?: string | null;
  readonly "point"?: Record<string, unknown> | null;
  readonly "rider_id": string;
  readonly "speed_meters_per_second": number;
};

export const riderLocationFieldOrder = ["rider_id","dash_session_id","point","observed_at","accuracy_meters","heading_degrees","speed_meters_per_second"] as const satisfies readonly (keyof RiderLocationFields)[];

export const riderLocationRequiredFields = ["accuracy_meters","dash_session_id","heading_degrees","rider_id","speed_meters_per_second"] as const satisfies readonly (keyof RiderLocationFields)[];

export type RiderLocationStore = TenantProjectionStore<RiderLocationPrototype>;

export const riderLocationSchema = {
  "collection": "rider_locations",
  "domain": "logistics",
  "fields": [
    {
      "kind": "int",
      "name": "accuracy_meters"
    },
    {
      "kind": "uuid",
      "name": "dash_session_id"
    },
    {
      "kind": "int",
      "name": "heading_degrees"
    },
    {
      "kind": "timestamp",
      "name": "observed_at",
      "optional": true
    },
    {
      "kind": "object",
      "name": "point",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "rider_id"
    },
    {
      "kind": "int",
      "name": "speed_meters_per_second"
    }
  ],
  "idField": "rider_id"
} as const satisfies DummySchemaSpec;

export const createRiderLocationDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<RiderLocationPrototype>(riderLocationSchema, options);

export const riderLocationRuntimeConstants = {
  collection: riderLocationSchema.collection,
  domain: riderLocationSchema.domain,
  idField: riderLocationSchema.idField ?? "id",
  key: "logistics.rider_locations",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedRiderLocationDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly RiderLocationPrototype[] =>
  cache.getDummyRecords(riderLocationSchema, { ...options, tenantId }, count, () =>
    createRiderLocationDummyFactory({ ...options, tenantId }).list(count)
  );

export const createRiderLocationStore = (
  tenantId: string,
  initialRecords: readonly RiderLocationPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): RiderLocationStore => {
  const scope = {
    collection: riderLocationSchema.collection,
    domain: riderLocationSchema.domain,
    tenantId,
  };
  const store = cache.getStore<RiderLocationPrototype>(
    scope,
    () =>
      createTenantProjectionStore<RiderLocationPrototype>(
        scope,
        (record) => String(record[riderLocationSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectRiderLocationLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<RiderLocationPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: RiderLocationStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<RiderLocationPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createRiderLocationStore(tenantId, [], cache);
  return createLiveProjectionBinding<RiderLocationPrototype>({
    ...bindingOptions,
    scope: {
      collection: riderLocationSchema.collection,
      domain: riderLocationSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerRiderLocationStore = (
  tenantId: string,
  store: RiderLocationStore = createRiderLocationStore(tenantId)
): RiderLocationStore => {
  prototypeStoreRegistry.register("logistics.rider_locations", store);
  return store;
};

export const useRiderLocationSnapshot = () => prototypeStoreRegistry.use("logistics.rider_locations");

export const riderLocationFixtures = {
  empty: (): RiderLocationPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly RiderLocationPrototype[] =>
    createCachedRiderLocationDummyRecords(tenantId, 8, { seed: "logistics.rider_locations:happy" }),
  invalidMissingId: (): RiderLocationPrototype[] =>
    createRiderLocationDummyFactory({ seed: "logistics.rider_locations:invalid" }).list(1, () => ({
      [riderLocationSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly RiderLocationPrototype[] =>
    createCachedRiderLocationDummyRecords(tenantId, count, { seed: "logistics.rider_locations:high-volume" }),
};

export const riderLocationFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<RiderLocationPrototype> => ({
    collection: riderLocationSchema.collection,
    domain: riderLocationSchema.domain,
    name: "logistics.rider_locations.empty",
    records: riderLocationFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<RiderLocationPrototype> => ({
    collection: riderLocationSchema.collection,
    domain: riderLocationSchema.domain,
    name: "logistics.rider_locations.happyPath",
    records: riderLocationFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<RiderLocationPrototype> => ({
    collection: riderLocationSchema.collection,
    domain: riderLocationSchema.domain,
    name: "logistics.rider_locations.invalidMissingId",
    records: riderLocationFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<RiderLocationPrototype> => ({
    collection: riderLocationSchema.collection,
    domain: riderLocationSchema.domain,
    name: "logistics.rider_locations.highVolume",
    records: riderLocationFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const riderLocationBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "logistics.rider_locations.dummy.list.1000",
    records: 1000,
    run: () => createCachedRiderLocationDummyRecords("tenant-benchmark", 1000, { seed: "logistics.rider_locations:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "logistics.rider_locations.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createRiderLocationDummyFactory({ seed: "logistics.rider_locations:bench-store" }).list(1000);
      const store = createRiderLocationStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics.rider_locations.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createRiderLocationDummyFactory({ seed: "logistics.rider_locations:bench-apply" }).list(1000);
      const store = createRiderLocationStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[riderLocationSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: riderLocationSchema.collection,
          domain: riderLocationSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics.rider_locations.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createRiderLocationDummyFactory({ seed: "logistics.rider_locations:bench-reset" }).list(1000);
      const store = createRiderLocationStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "logistics.rider_locations.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createRiderLocationDummyFactory({ seed: "logistics.rider_locations:bench-selector" }).list(1000);
      const store = createRiderLocationStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[riderLocationSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type GeoPointPrototype = Record<string, unknown>;

/** Typed field shape for maps.v1.GeoPoint. Use it to narrow GeoPointPrototype records. */
export type GeoPointFields = {
  readonly "address": string;
  readonly "latitude": number;
  readonly "longitude": number;
  readonly "place_id": string;
};

export const geoPointFieldOrder = ["latitude","longitude","address","place_id"] as const satisfies readonly (keyof GeoPointFields)[];

export const geoPointRequiredFields = ["address","latitude","longitude","place_id"] as const satisfies readonly (keyof GeoPointFields)[];

export type GeoPointStore = TenantProjectionStore<GeoPointPrototype>;

export const geoPointSchema = {
  "collection": "geo_points",
  "domain": "maps",
  "fields": [
    {
      "kind": "string",
      "name": "address"
    },
    {
      "kind": "float",
      "name": "latitude"
    },
    {
      "kind": "float",
      "name": "longitude"
    },
    {
      "kind": "uuid",
      "name": "place_id"
    }
  ],
  "idField": "place_id"
} as const satisfies DummySchemaSpec;

export const createGeoPointDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<GeoPointPrototype>(geoPointSchema, options);

export const geoPointRuntimeConstants = {
  collection: geoPointSchema.collection,
  domain: geoPointSchema.domain,
  idField: geoPointSchema.idField ?? "id",
  key: "maps.geo_points",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedGeoPointDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly GeoPointPrototype[] =>
  cache.getDummyRecords(geoPointSchema, { ...options, tenantId }, count, () =>
    createGeoPointDummyFactory({ ...options, tenantId }).list(count)
  );

export const createGeoPointStore = (
  tenantId: string,
  initialRecords: readonly GeoPointPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): GeoPointStore => {
  const scope = {
    collection: geoPointSchema.collection,
    domain: geoPointSchema.domain,
    tenantId,
  };
  const store = cache.getStore<GeoPointPrototype>(
    scope,
    () =>
      createTenantProjectionStore<GeoPointPrototype>(
        scope,
        (record) => String(record[geoPointSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectGeoPointLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<GeoPointPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: GeoPointStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<GeoPointPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createGeoPointStore(tenantId, [], cache);
  return createLiveProjectionBinding<GeoPointPrototype>({
    ...bindingOptions,
    scope: {
      collection: geoPointSchema.collection,
      domain: geoPointSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerGeoPointStore = (
  tenantId: string,
  store: GeoPointStore = createGeoPointStore(tenantId)
): GeoPointStore => {
  prototypeStoreRegistry.register("maps.geo_points", store);
  return store;
};

export const useGeoPointSnapshot = () => prototypeStoreRegistry.use("maps.geo_points");

export const geoPointFixtures = {
  empty: (): GeoPointPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly GeoPointPrototype[] =>
    createCachedGeoPointDummyRecords(tenantId, 8, { seed: "maps.geo_points:happy" }),
  invalidMissingId: (): GeoPointPrototype[] =>
    createGeoPointDummyFactory({ seed: "maps.geo_points:invalid" }).list(1, () => ({
      [geoPointSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly GeoPointPrototype[] =>
    createCachedGeoPointDummyRecords(tenantId, count, { seed: "maps.geo_points:high-volume" }),
};

export const geoPointFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<GeoPointPrototype> => ({
    collection: geoPointSchema.collection,
    domain: geoPointSchema.domain,
    name: "maps.geo_points.empty",
    records: geoPointFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<GeoPointPrototype> => ({
    collection: geoPointSchema.collection,
    domain: geoPointSchema.domain,
    name: "maps.geo_points.happyPath",
    records: geoPointFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<GeoPointPrototype> => ({
    collection: geoPointSchema.collection,
    domain: geoPointSchema.domain,
    name: "maps.geo_points.invalidMissingId",
    records: geoPointFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<GeoPointPrototype> => ({
    collection: geoPointSchema.collection,
    domain: geoPointSchema.domain,
    name: "maps.geo_points.highVolume",
    records: geoPointFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const geoPointBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "maps.geo_points.dummy.list.1000",
    records: 1000,
    run: () => createCachedGeoPointDummyRecords("tenant-benchmark", 1000, { seed: "maps.geo_points:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "maps.geo_points.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createGeoPointDummyFactory({ seed: "maps.geo_points:bench-store" }).list(1000);
      const store = createGeoPointStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "maps.geo_points.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createGeoPointDummyFactory({ seed: "maps.geo_points:bench-apply" }).list(1000);
      const store = createGeoPointStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[geoPointSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: geoPointSchema.collection,
          domain: geoPointSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "maps.geo_points.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createGeoPointDummyFactory({ seed: "maps.geo_points:bench-reset" }).list(1000);
      const store = createGeoPointStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "maps.geo_points.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createGeoPointDummyFactory({ seed: "maps.geo_points:bench-selector" }).list(1000);
      const store = createGeoPointStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[geoPointSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type RoutePlanPrototype = Record<string, unknown>;

/** Typed field shape for maps.v1.RoutePlan. Use it to narrow RoutePlanPrototype records. */
export type RoutePlanFields = {
  readonly "delivery_id": string;
  readonly "destination"?: Record<string, unknown> | null;
  readonly "distance_meters": number;
  readonly "duration_seconds": number;
  readonly "estimated_arrival"?: string | null;
  readonly "id": string;
  readonly "legs": readonly (Record<string, unknown>)[];
  readonly "mode": string;
  readonly "organization_id": string;
  readonly "origin"?: Record<string, unknown> | null;
  readonly "polyline": readonly (Record<string, unknown>)[];
  readonly "provider": string;
  readonly "service_seconds": number;
};

export const routePlanFieldOrder = ["id","organization_id","provider","mode","origin","destination","polyline","legs","distance_meters","duration_seconds","service_seconds","estimated_arrival","delivery_id"] as const satisfies readonly (keyof RoutePlanFields)[];

export const routePlanRequiredFields = ["delivery_id","distance_meters","duration_seconds","id","legs","mode","organization_id","polyline","provider","service_seconds"] as const satisfies readonly (keyof RoutePlanFields)[];

export type RoutePlanStore = TenantProjectionStore<RoutePlanPrototype>;

export const routePlanSchema = {
  "collection": "route_plans",
  "domain": "maps",
  "fields": [
    {
      "kind": "uuid",
      "name": "delivery_id"
    },
    {
      "kind": "object",
      "name": "destination",
      "optional": true
    },
    {
      "kind": "int",
      "name": "distance_meters"
    },
    {
      "kind": "int",
      "name": "duration_seconds"
    },
    {
      "kind": "timestamp",
      "name": "estimated_arrival",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "object",
      "name": "legs",
      "repeated": true
    },
    {
      "kind": "string",
      "name": "mode"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "object",
      "name": "origin",
      "optional": true
    },
    {
      "kind": "object",
      "name": "polyline",
      "repeated": true
    },
    {
      "kind": "string",
      "name": "provider"
    },
    {
      "kind": "int",
      "name": "service_seconds"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createRoutePlanDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<RoutePlanPrototype>(routePlanSchema, options);

export const routePlanRuntimeConstants = {
  collection: routePlanSchema.collection,
  domain: routePlanSchema.domain,
  idField: routePlanSchema.idField ?? "id",
  key: "maps.route_plans",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedRoutePlanDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly RoutePlanPrototype[] =>
  cache.getDummyRecords(routePlanSchema, { ...options, tenantId }, count, () =>
    createRoutePlanDummyFactory({ ...options, tenantId }).list(count)
  );

export const createRoutePlanStore = (
  tenantId: string,
  initialRecords: readonly RoutePlanPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): RoutePlanStore => {
  const scope = {
    collection: routePlanSchema.collection,
    domain: routePlanSchema.domain,
    tenantId,
  };
  const store = cache.getStore<RoutePlanPrototype>(
    scope,
    () =>
      createTenantProjectionStore<RoutePlanPrototype>(
        scope,
        (record) => String(record[routePlanSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectRoutePlanLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<RoutePlanPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: RoutePlanStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<RoutePlanPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createRoutePlanStore(tenantId, [], cache);
  return createLiveProjectionBinding<RoutePlanPrototype>({
    ...bindingOptions,
    scope: {
      collection: routePlanSchema.collection,
      domain: routePlanSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerRoutePlanStore = (
  tenantId: string,
  store: RoutePlanStore = createRoutePlanStore(tenantId)
): RoutePlanStore => {
  prototypeStoreRegistry.register("maps.route_plans", store);
  return store;
};

export const useRoutePlanSnapshot = () => prototypeStoreRegistry.use("maps.route_plans");

export const routePlanFixtures = {
  empty: (): RoutePlanPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly RoutePlanPrototype[] =>
    createCachedRoutePlanDummyRecords(tenantId, 8, { seed: "maps.route_plans:happy" }),
  invalidMissingId: (): RoutePlanPrototype[] =>
    createRoutePlanDummyFactory({ seed: "maps.route_plans:invalid" }).list(1, () => ({
      [routePlanSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly RoutePlanPrototype[] =>
    createCachedRoutePlanDummyRecords(tenantId, count, { seed: "maps.route_plans:high-volume" }),
};

export const routePlanFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<RoutePlanPrototype> => ({
    collection: routePlanSchema.collection,
    domain: routePlanSchema.domain,
    name: "maps.route_plans.empty",
    records: routePlanFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<RoutePlanPrototype> => ({
    collection: routePlanSchema.collection,
    domain: routePlanSchema.domain,
    name: "maps.route_plans.happyPath",
    records: routePlanFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<RoutePlanPrototype> => ({
    collection: routePlanSchema.collection,
    domain: routePlanSchema.domain,
    name: "maps.route_plans.invalidMissingId",
    records: routePlanFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<RoutePlanPrototype> => ({
    collection: routePlanSchema.collection,
    domain: routePlanSchema.domain,
    name: "maps.route_plans.highVolume",
    records: routePlanFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const routePlanBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "maps.route_plans.dummy.list.1000",
    records: 1000,
    run: () => createCachedRoutePlanDummyRecords("tenant-benchmark", 1000, { seed: "maps.route_plans:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "maps.route_plans.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createRoutePlanDummyFactory({ seed: "maps.route_plans:bench-store" }).list(1000);
      const store = createRoutePlanStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "maps.route_plans.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createRoutePlanDummyFactory({ seed: "maps.route_plans:bench-apply" }).list(1000);
      const store = createRoutePlanStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[routePlanSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: routePlanSchema.collection,
          domain: routePlanSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "maps.route_plans.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createRoutePlanDummyFactory({ seed: "maps.route_plans:bench-reset" }).list(1000);
      const store = createRoutePlanStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "maps.route_plans.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createRoutePlanDummyFactory({ seed: "maps.route_plans:bench-selector" }).list(1000);
      const store = createRoutePlanStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[routePlanSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type CartItemPrototype = Record<string, unknown>;

/** Typed field shape for marketplace.v1.CartItem. Use it to narrow CartItemPrototype records. */
export type CartItemFields = {
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "customer_profile_id": string;
  readonly "id": string;
  readonly "item_name": string;
  readonly "item_ref": string;
  readonly "merchant_profile_id": string;
  readonly "organization_id": string;
  readonly "quantity": number;
  readonly "special_instructions": string;
  readonly "status": string;
  readonly "unit_amount_minor": number;
  readonly "updated_at"?: string | null;
};

export const cartItemFieldOrder = ["id","organization_id","created_at","updated_at","customer_profile_id","merchant_profile_id","item_ref","item_name","quantity","unit_amount_minor","currency","special_instructions","status"] as const satisfies readonly (keyof CartItemFields)[];

export const cartItemRequiredFields = ["currency","customer_profile_id","id","item_name","item_ref","merchant_profile_id","organization_id","quantity","special_instructions","status","unit_amount_minor"] as const satisfies readonly (keyof CartItemFields)[];

export type CartItemStore = TenantProjectionStore<CartItemPrototype>;

export const cartItemSchema = {
  "collection": "carts",
  "domain": "marketplace",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "customer_profile_id"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "item_name"
    },
    {
      "kind": "string",
      "name": "item_ref"
    },
    {
      "kind": "uuid",
      "name": "merchant_profile_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "int",
      "name": "quantity"
    },
    {
      "kind": "string",
      "name": "special_instructions"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "int",
      "name": "unit_amount_minor"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createCartItemDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<CartItemPrototype>(cartItemSchema, options);

export const cartItemRuntimeConstants = {
  collection: cartItemSchema.collection,
  domain: cartItemSchema.domain,
  idField: cartItemSchema.idField ?? "id",
  key: "marketplace.carts",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedCartItemDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly CartItemPrototype[] =>
  cache.getDummyRecords(cartItemSchema, { ...options, tenantId }, count, () =>
    createCartItemDummyFactory({ ...options, tenantId }).list(count)
  );

export const createCartItemStore = (
  tenantId: string,
  initialRecords: readonly CartItemPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): CartItemStore => {
  const scope = {
    collection: cartItemSchema.collection,
    domain: cartItemSchema.domain,
    tenantId,
  };
  const store = cache.getStore<CartItemPrototype>(
    scope,
    () =>
      createTenantProjectionStore<CartItemPrototype>(
        scope,
        (record) => String(record[cartItemSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectCartItemLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<CartItemPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: CartItemStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<CartItemPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createCartItemStore(tenantId, [], cache);
  return createLiveProjectionBinding<CartItemPrototype>({
    ...bindingOptions,
    scope: {
      collection: cartItemSchema.collection,
      domain: cartItemSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerCartItemStore = (
  tenantId: string,
  store: CartItemStore = createCartItemStore(tenantId)
): CartItemStore => {
  prototypeStoreRegistry.register("marketplace.carts", store);
  return store;
};

export const useCartItemSnapshot = () => prototypeStoreRegistry.use("marketplace.carts");

export const cartItemFixtures = {
  empty: (): CartItemPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly CartItemPrototype[] =>
    createCachedCartItemDummyRecords(tenantId, 8, { seed: "marketplace.carts:happy" }),
  invalidMissingId: (): CartItemPrototype[] =>
    createCartItemDummyFactory({ seed: "marketplace.carts:invalid" }).list(1, () => ({
      [cartItemSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly CartItemPrototype[] =>
    createCachedCartItemDummyRecords(tenantId, count, { seed: "marketplace.carts:high-volume" }),
};

export const cartItemFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<CartItemPrototype> => ({
    collection: cartItemSchema.collection,
    domain: cartItemSchema.domain,
    name: "marketplace.carts.empty",
    records: cartItemFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<CartItemPrototype> => ({
    collection: cartItemSchema.collection,
    domain: cartItemSchema.domain,
    name: "marketplace.carts.happyPath",
    records: cartItemFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<CartItemPrototype> => ({
    collection: cartItemSchema.collection,
    domain: cartItemSchema.domain,
    name: "marketplace.carts.invalidMissingId",
    records: cartItemFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<CartItemPrototype> => ({
    collection: cartItemSchema.collection,
    domain: cartItemSchema.domain,
    name: "marketplace.carts.highVolume",
    records: cartItemFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const cartItemBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "marketplace.carts.dummy.list.1000",
    records: 1000,
    run: () => createCachedCartItemDummyRecords("tenant-benchmark", 1000, { seed: "marketplace.carts:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "marketplace.carts.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createCartItemDummyFactory({ seed: "marketplace.carts:bench-store" }).list(1000);
      const store = createCartItemStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.carts.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createCartItemDummyFactory({ seed: "marketplace.carts:bench-apply" }).list(1000);
      const store = createCartItemStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[cartItemSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: cartItemSchema.collection,
          domain: cartItemSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.carts.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createCartItemDummyFactory({ seed: "marketplace.carts:bench-reset" }).list(1000);
      const store = createCartItemStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.carts.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createCartItemDummyFactory({ seed: "marketplace.carts:bench-selector" }).list(1000);
      const store = createCartItemStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[cartItemSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MarketplaceOrderPrototype = Record<string, unknown>;

/** Typed field shape for marketplace.v1.MarketplaceOrder. Use it to narrow MarketplaceOrderPrototype records. */
export type MarketplaceOrderFields = {
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "customer_profile_id": string;
  readonly "delivery_choice": "DELIVERY_CHOICE_STANDARD" | "DELIVERY_CHOICE_EXPRESS" | "DELIVERY_CHOICE_CHOWDASH" | "DELIVERY_CHOICE_SELF_PICKUP" | "DELIVERY_CHOICE_RECURRING";
  readonly "delivery_fee_minor": number;
  readonly "delivery_id": string;
  readonly "discount_applied_minor": number;
  readonly "id": string;
  readonly "merchant_profile_id": string;
  readonly "organization_id": string;
  readonly "pickup_address": string;
  readonly "promo_code": string;
  readonly "recurrence_key": string;
  readonly "scheduled_at"?: string | null;
  readonly "service_fee_minor": number;
  readonly "status": "ORDER_STATUS_DRAFT" | "ORDER_STATUS_PENDING_PAYMENT" | "ORDER_STATUS_PAID" | "ORDER_STATUS_ACCEPTED" | "ORDER_STATUS_PREPARING" | "ORDER_STATUS_READY" | "ORDER_STATUS_DELIVERY_REQUESTED" | "ORDER_STATUS_IN_DELIVERY" | "ORDER_STATUS_COMPLETED" | "ORDER_STATUS_CANCELLED" | "ORDER_STATUS_REFUNDED";
  readonly "subtotal_minor": number;
  readonly "total_minor": number;
  readonly "updated_at"?: string | null;
};

export const marketplaceOrderFieldOrder = ["id","organization_id","created_at","updated_at","customer_profile_id","merchant_profile_id","delivery_id","status","subtotal_minor","delivery_fee_minor","total_minor","currency","delivery_choice","scheduled_at","recurrence_key","promo_code","discount_applied_minor","pickup_address","service_fee_minor"] as const satisfies readonly (keyof MarketplaceOrderFields)[];

export const marketplaceOrderRequiredFields = ["currency","customer_profile_id","delivery_choice","delivery_fee_minor","delivery_id","discount_applied_minor","id","merchant_profile_id","organization_id","pickup_address","promo_code","recurrence_key","service_fee_minor","status","subtotal_minor","total_minor"] as const satisfies readonly (keyof MarketplaceOrderFields)[];

export type MarketplaceOrderStore = TenantProjectionStore<MarketplaceOrderPrototype>;

export const marketplaceOrderSchema = {
  "collection": "marketplace_orders",
  "domain": "marketplace",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "customer_profile_id"
    },
    {
      "enumValues": [
        "DELIVERY_CHOICE_STANDARD",
        "DELIVERY_CHOICE_EXPRESS",
        "DELIVERY_CHOICE_CHOWDASH",
        "DELIVERY_CHOICE_SELF_PICKUP",
        "DELIVERY_CHOICE_RECURRING"
      ],
      "kind": "enum",
      "name": "delivery_choice"
    },
    {
      "kind": "int",
      "name": "delivery_fee_minor"
    },
    {
      "kind": "uuid",
      "name": "delivery_id"
    },
    {
      "kind": "int",
      "name": "discount_applied_minor"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "merchant_profile_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "pickup_address"
    },
    {
      "kind": "string",
      "name": "promo_code"
    },
    {
      "kind": "string",
      "name": "recurrence_key"
    },
    {
      "kind": "timestamp",
      "name": "scheduled_at",
      "optional": true
    },
    {
      "kind": "int",
      "name": "service_fee_minor"
    },
    {
      "enumValues": [
        "ORDER_STATUS_DRAFT",
        "ORDER_STATUS_PENDING_PAYMENT",
        "ORDER_STATUS_PAID",
        "ORDER_STATUS_ACCEPTED",
        "ORDER_STATUS_PREPARING",
        "ORDER_STATUS_READY",
        "ORDER_STATUS_DELIVERY_REQUESTED",
        "ORDER_STATUS_IN_DELIVERY",
        "ORDER_STATUS_COMPLETED",
        "ORDER_STATUS_CANCELLED",
        "ORDER_STATUS_REFUNDED"
      ],
      "kind": "enum",
      "name": "status"
    },
    {
      "kind": "int",
      "name": "subtotal_minor"
    },
    {
      "kind": "int",
      "name": "total_minor"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMarketplaceOrderDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MarketplaceOrderPrototype>(marketplaceOrderSchema, options);

export const marketplaceOrderRuntimeConstants = {
  collection: marketplaceOrderSchema.collection,
  domain: marketplaceOrderSchema.domain,
  idField: marketplaceOrderSchema.idField ?? "id",
  key: "marketplace.marketplace_orders",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMarketplaceOrderDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MarketplaceOrderPrototype[] =>
  cache.getDummyRecords(marketplaceOrderSchema, { ...options, tenantId }, count, () =>
    createMarketplaceOrderDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMarketplaceOrderStore = (
  tenantId: string,
  initialRecords: readonly MarketplaceOrderPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MarketplaceOrderStore => {
  const scope = {
    collection: marketplaceOrderSchema.collection,
    domain: marketplaceOrderSchema.domain,
    tenantId,
  };
  const store = cache.getStore<MarketplaceOrderPrototype>(
    scope,
    () =>
      createTenantProjectionStore<MarketplaceOrderPrototype>(
        scope,
        (record) => String(record[marketplaceOrderSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMarketplaceOrderLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MarketplaceOrderPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MarketplaceOrderStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MarketplaceOrderPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMarketplaceOrderStore(tenantId, [], cache);
  return createLiveProjectionBinding<MarketplaceOrderPrototype>({
    ...bindingOptions,
    scope: {
      collection: marketplaceOrderSchema.collection,
      domain: marketplaceOrderSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMarketplaceOrderStore = (
  tenantId: string,
  store: MarketplaceOrderStore = createMarketplaceOrderStore(tenantId)
): MarketplaceOrderStore => {
  prototypeStoreRegistry.register("marketplace.marketplace_orders", store);
  return store;
};

export const useMarketplaceOrderSnapshot = () => prototypeStoreRegistry.use("marketplace.marketplace_orders");

export const marketplaceOrderFixtures = {
  empty: (): MarketplaceOrderPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MarketplaceOrderPrototype[] =>
    createCachedMarketplaceOrderDummyRecords(tenantId, 8, { seed: "marketplace.marketplace_orders:happy" }),
  invalidMissingId: (): MarketplaceOrderPrototype[] =>
    createMarketplaceOrderDummyFactory({ seed: "marketplace.marketplace_orders:invalid" }).list(1, () => ({
      [marketplaceOrderSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MarketplaceOrderPrototype[] =>
    createCachedMarketplaceOrderDummyRecords(tenantId, count, { seed: "marketplace.marketplace_orders:high-volume" }),
};

export const marketplaceOrderFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MarketplaceOrderPrototype> => ({
    collection: marketplaceOrderSchema.collection,
    domain: marketplaceOrderSchema.domain,
    name: "marketplace.marketplace_orders.empty",
    records: marketplaceOrderFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MarketplaceOrderPrototype> => ({
    collection: marketplaceOrderSchema.collection,
    domain: marketplaceOrderSchema.domain,
    name: "marketplace.marketplace_orders.happyPath",
    records: marketplaceOrderFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MarketplaceOrderPrototype> => ({
    collection: marketplaceOrderSchema.collection,
    domain: marketplaceOrderSchema.domain,
    name: "marketplace.marketplace_orders.invalidMissingId",
    records: marketplaceOrderFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MarketplaceOrderPrototype> => ({
    collection: marketplaceOrderSchema.collection,
    domain: marketplaceOrderSchema.domain,
    name: "marketplace.marketplace_orders.highVolume",
    records: marketplaceOrderFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const marketplaceOrderBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "marketplace.marketplace_orders.dummy.list.1000",
    records: 1000,
    run: () => createCachedMarketplaceOrderDummyRecords("tenant-benchmark", 1000, { seed: "marketplace.marketplace_orders:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "marketplace.marketplace_orders.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMarketplaceOrderDummyFactory({ seed: "marketplace.marketplace_orders:bench-store" }).list(1000);
      const store = createMarketplaceOrderStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.marketplace_orders.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMarketplaceOrderDummyFactory({ seed: "marketplace.marketplace_orders:bench-apply" }).list(1000);
      const store = createMarketplaceOrderStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[marketplaceOrderSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: marketplaceOrderSchema.collection,
          domain: marketplaceOrderSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.marketplace_orders.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMarketplaceOrderDummyFactory({ seed: "marketplace.marketplace_orders:bench-reset" }).list(1000);
      const store = createMarketplaceOrderStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.marketplace_orders.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMarketplaceOrderDummyFactory({ seed: "marketplace.marketplace_orders:bench-selector" }).list(1000);
      const store = createMarketplaceOrderStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[marketplaceOrderSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type OrderItemPrototype = Record<string, unknown>;

/** Typed field shape for marketplace.v1.OrderItem. Use it to narrow OrderItemPrototype records. */
export type OrderItemFields = {
  readonly "currency": string;
  readonly "item_name": string;
  readonly "item_ref": string;
  readonly "line_total_minor": number;
  readonly "quantity": number;
  readonly "special_instructions": string;
  readonly "unit_amount_minor": number;
};

export const orderItemFieldOrder = ["item_ref","item_name","quantity","unit_amount_minor","line_total_minor","currency","special_instructions"] as const satisfies readonly (keyof OrderItemFields)[];

export const orderItemRequiredFields = ["currency","item_name","item_ref","line_total_minor","quantity","special_instructions","unit_amount_minor"] as const satisfies readonly (keyof OrderItemFields)[];

export type OrderItemStore = TenantProjectionStore<OrderItemPrototype>;

export const orderItemSchema = {
  "collection": "orders",
  "domain": "marketplace",
  "fields": [
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "string",
      "name": "item_name"
    },
    {
      "kind": "string",
      "name": "item_ref"
    },
    {
      "kind": "int",
      "name": "line_total_minor"
    },
    {
      "kind": "int",
      "name": "quantity"
    },
    {
      "kind": "string",
      "name": "special_instructions"
    },
    {
      "kind": "int",
      "name": "unit_amount_minor"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createOrderItemDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<OrderItemPrototype>(orderItemSchema, options);

export const orderItemRuntimeConstants = {
  collection: orderItemSchema.collection,
  domain: orderItemSchema.domain,
  idField: orderItemSchema.idField ?? "id",
  key: "marketplace.orders",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedOrderItemDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly OrderItemPrototype[] =>
  cache.getDummyRecords(orderItemSchema, { ...options, tenantId }, count, () =>
    createOrderItemDummyFactory({ ...options, tenantId }).list(count)
  );

export const createOrderItemStore = (
  tenantId: string,
  initialRecords: readonly OrderItemPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): OrderItemStore => {
  const scope = {
    collection: orderItemSchema.collection,
    domain: orderItemSchema.domain,
    tenantId,
  };
  const store = cache.getStore<OrderItemPrototype>(
    scope,
    () =>
      createTenantProjectionStore<OrderItemPrototype>(
        scope,
        (record) => String(record[orderItemSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectOrderItemLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<OrderItemPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: OrderItemStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<OrderItemPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createOrderItemStore(tenantId, [], cache);
  return createLiveProjectionBinding<OrderItemPrototype>({
    ...bindingOptions,
    scope: {
      collection: orderItemSchema.collection,
      domain: orderItemSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerOrderItemStore = (
  tenantId: string,
  store: OrderItemStore = createOrderItemStore(tenantId)
): OrderItemStore => {
  prototypeStoreRegistry.register("marketplace.orders", store);
  return store;
};

export const useOrderItemSnapshot = () => prototypeStoreRegistry.use("marketplace.orders");

export const orderItemFixtures = {
  empty: (): OrderItemPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly OrderItemPrototype[] =>
    createCachedOrderItemDummyRecords(tenantId, 8, { seed: "marketplace.orders:happy" }),
  invalidMissingId: (): OrderItemPrototype[] =>
    createOrderItemDummyFactory({ seed: "marketplace.orders:invalid" }).list(1, () => ({
      [orderItemSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly OrderItemPrototype[] =>
    createCachedOrderItemDummyRecords(tenantId, count, { seed: "marketplace.orders:high-volume" }),
};

export const orderItemFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderItemPrototype> => ({
    collection: orderItemSchema.collection,
    domain: orderItemSchema.domain,
    name: "marketplace.orders.empty",
    records: orderItemFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderItemPrototype> => ({
    collection: orderItemSchema.collection,
    domain: orderItemSchema.domain,
    name: "marketplace.orders.happyPath",
    records: orderItemFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderItemPrototype> => ({
    collection: orderItemSchema.collection,
    domain: orderItemSchema.domain,
    name: "marketplace.orders.invalidMissingId",
    records: orderItemFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<OrderItemPrototype> => ({
    collection: orderItemSchema.collection,
    domain: orderItemSchema.domain,
    name: "marketplace.orders.highVolume",
    records: orderItemFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const orderItemBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "marketplace.orders.dummy.list.1000",
    records: 1000,
    run: () => createCachedOrderItemDummyRecords("tenant-benchmark", 1000, { seed: "marketplace.orders:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "marketplace.orders.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createOrderItemDummyFactory({ seed: "marketplace.orders:bench-store" }).list(1000);
      const store = createOrderItemStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.orders.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createOrderItemDummyFactory({ seed: "marketplace.orders:bench-apply" }).list(1000);
      const store = createOrderItemStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[orderItemSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: orderItemSchema.collection,
          domain: orderItemSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.orders.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createOrderItemDummyFactory({ seed: "marketplace.orders:bench-reset" }).list(1000);
      const store = createOrderItemStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "marketplace.orders.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createOrderItemDummyFactory({ seed: "marketplace.orders:bench-selector" }).list(1000);
      const store = createOrderItemStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[orderItemSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MealPlanDropPrototype = Record<string, unknown>;

/** Typed field shape for mealplan.v1.MealPlanDrop. Use it to narrow MealPlanDropPrototype records. */
export type MealPlanDropFields = {
  readonly "cycle_index": number;
  readonly "drop_index": number;
  readonly "id": string;
  readonly "release_amount_minor": number;
  readonly "rider_id": string;
  readonly "scheduled_date": string;
  readonly "slot": string;
  readonly "status": string;
  readonly "subscription_id": string;
};

export const mealPlanDropFieldOrder = ["id","subscription_id","cycle_index","drop_index","scheduled_date","slot","status","rider_id","release_amount_minor"] as const satisfies readonly (keyof MealPlanDropFields)[];

export const mealPlanDropRequiredFields = ["cycle_index","drop_index","id","release_amount_minor","rider_id","scheduled_date","slot","status","subscription_id"] as const satisfies readonly (keyof MealPlanDropFields)[];

export type MealPlanDropStore = TenantProjectionStore<MealPlanDropPrototype>;

export const mealPlanDropSchema = {
  "collection": "meal_plan_drops",
  "domain": "mealplan",
  "fields": [
    {
      "kind": "int",
      "name": "cycle_index"
    },
    {
      "kind": "int",
      "name": "drop_index"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "int",
      "name": "release_amount_minor"
    },
    {
      "kind": "uuid",
      "name": "rider_id"
    },
    {
      "kind": "timestamp",
      "name": "scheduled_date"
    },
    {
      "kind": "string",
      "name": "slot"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "uuid",
      "name": "subscription_id"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMealPlanDropDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MealPlanDropPrototype>(mealPlanDropSchema, options);

export const mealPlanDropRuntimeConstants = {
  collection: mealPlanDropSchema.collection,
  domain: mealPlanDropSchema.domain,
  idField: mealPlanDropSchema.idField ?? "id",
  key: "mealplan.meal_plan_drops",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMealPlanDropDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MealPlanDropPrototype[] =>
  cache.getDummyRecords(mealPlanDropSchema, { ...options, tenantId }, count, () =>
    createMealPlanDropDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMealPlanDropStore = (
  tenantId: string,
  initialRecords: readonly MealPlanDropPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MealPlanDropStore => {
  const scope = {
    collection: mealPlanDropSchema.collection,
    domain: mealPlanDropSchema.domain,
    tenantId,
  };
  const store = cache.getStore<MealPlanDropPrototype>(
    scope,
    () =>
      createTenantProjectionStore<MealPlanDropPrototype>(
        scope,
        (record) => String(record[mealPlanDropSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMealPlanDropLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MealPlanDropPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MealPlanDropStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MealPlanDropPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMealPlanDropStore(tenantId, [], cache);
  return createLiveProjectionBinding<MealPlanDropPrototype>({
    ...bindingOptions,
    scope: {
      collection: mealPlanDropSchema.collection,
      domain: mealPlanDropSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMealPlanDropStore = (
  tenantId: string,
  store: MealPlanDropStore = createMealPlanDropStore(tenantId)
): MealPlanDropStore => {
  prototypeStoreRegistry.register("mealplan.meal_plan_drops", store);
  return store;
};

export const useMealPlanDropSnapshot = () => prototypeStoreRegistry.use("mealplan.meal_plan_drops");

export const mealPlanDropFixtures = {
  empty: (): MealPlanDropPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MealPlanDropPrototype[] =>
    createCachedMealPlanDropDummyRecords(tenantId, 8, { seed: "mealplan.meal_plan_drops:happy" }),
  invalidMissingId: (): MealPlanDropPrototype[] =>
    createMealPlanDropDummyFactory({ seed: "mealplan.meal_plan_drops:invalid" }).list(1, () => ({
      [mealPlanDropSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MealPlanDropPrototype[] =>
    createCachedMealPlanDropDummyRecords(tenantId, count, { seed: "mealplan.meal_plan_drops:high-volume" }),
};

export const mealPlanDropFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanDropPrototype> => ({
    collection: mealPlanDropSchema.collection,
    domain: mealPlanDropSchema.domain,
    name: "mealplan.meal_plan_drops.empty",
    records: mealPlanDropFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanDropPrototype> => ({
    collection: mealPlanDropSchema.collection,
    domain: mealPlanDropSchema.domain,
    name: "mealplan.meal_plan_drops.happyPath",
    records: mealPlanDropFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanDropPrototype> => ({
    collection: mealPlanDropSchema.collection,
    domain: mealPlanDropSchema.domain,
    name: "mealplan.meal_plan_drops.invalidMissingId",
    records: mealPlanDropFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MealPlanDropPrototype> => ({
    collection: mealPlanDropSchema.collection,
    domain: mealPlanDropSchema.domain,
    name: "mealplan.meal_plan_drops.highVolume",
    records: mealPlanDropFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const mealPlanDropBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "mealplan.meal_plan_drops.dummy.list.1000",
    records: 1000,
    run: () => createCachedMealPlanDropDummyRecords("tenant-benchmark", 1000, { seed: "mealplan.meal_plan_drops:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_drops.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDropDummyFactory({ seed: "mealplan.meal_plan_drops:bench-store" }).list(1000);
      const store = createMealPlanDropStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_drops.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDropDummyFactory({ seed: "mealplan.meal_plan_drops:bench-apply" }).list(1000);
      const store = createMealPlanDropStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[mealPlanDropSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: mealPlanDropSchema.collection,
          domain: mealPlanDropSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_drops.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDropDummyFactory({ seed: "mealplan.meal_plan_drops:bench-reset" }).list(1000);
      const store = createMealPlanDropStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_drops.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDropDummyFactory({ seed: "mealplan.meal_plan_drops:bench-selector" }).list(1000);
      const store = createMealPlanDropStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[mealPlanDropSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MealPlanSubscriptionPrototype = Record<string, unknown>;

/** Typed field shape for mealplan.v1.MealPlanSubscription. Use it to narrow MealPlanSubscriptionPrototype records. */
export type MealPlanSubscriptionFields = {
  readonly "chef_payout_minor": number;
  readonly "chef_profile_id": string;
  readonly "currency": string;
  readonly "customer_profile_id": string;
  readonly "customer_total_minor": number;
  readonly "cycle_end": string;
  readonly "cycle_start": string;
  readonly "drops_per_week": number;
  readonly "escrow_balance_minor": number;
  readonly "id": string;
  readonly "meal_plan_id": string;
  readonly "platform_revenue_minor": number;
  readonly "rates_snapshot": string;
  readonly "skips_remaining": number;
  readonly "status": string;
  readonly "weeks_per_cycle": number;
  readonly "zone_id": string;
};

export const mealPlanSubscriptionFieldOrder = ["id","customer_profile_id","meal_plan_id","chef_profile_id","zone_id","status","cycle_start","cycle_end","drops_per_week","weeks_per_cycle","rates_snapshot","customer_total_minor","chef_payout_minor","platform_revenue_minor","currency","skips_remaining","escrow_balance_minor"] as const satisfies readonly (keyof MealPlanSubscriptionFields)[];

export const mealPlanSubscriptionRequiredFields = ["chef_payout_minor","chef_profile_id","currency","customer_profile_id","customer_total_minor","cycle_end","cycle_start","drops_per_week","escrow_balance_minor","id","meal_plan_id","platform_revenue_minor","rates_snapshot","skips_remaining","status","weeks_per_cycle","zone_id"] as const satisfies readonly (keyof MealPlanSubscriptionFields)[];

export type MealPlanSubscriptionStore = TenantProjectionStore<MealPlanSubscriptionPrototype>;

export const mealPlanSubscriptionSchema = {
  "collection": "meal_plan_subscriptions",
  "domain": "mealplan",
  "fields": [
    {
      "kind": "int",
      "name": "chef_payout_minor"
    },
    {
      "kind": "uuid",
      "name": "chef_profile_id"
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "customer_profile_id"
    },
    {
      "kind": "int",
      "name": "customer_total_minor"
    },
    {
      "kind": "string",
      "name": "cycle_end"
    },
    {
      "kind": "string",
      "name": "cycle_start"
    },
    {
      "kind": "int",
      "name": "drops_per_week"
    },
    {
      "kind": "int",
      "name": "escrow_balance_minor"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "meal_plan_id"
    },
    {
      "kind": "int",
      "name": "platform_revenue_minor"
    },
    {
      "kind": "string",
      "name": "rates_snapshot"
    },
    {
      "kind": "int",
      "name": "skips_remaining"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "int",
      "name": "weeks_per_cycle"
    },
    {
      "kind": "uuid",
      "name": "zone_id"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMealPlanSubscriptionDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MealPlanSubscriptionPrototype>(mealPlanSubscriptionSchema, options);

export const mealPlanSubscriptionRuntimeConstants = {
  collection: mealPlanSubscriptionSchema.collection,
  domain: mealPlanSubscriptionSchema.domain,
  idField: mealPlanSubscriptionSchema.idField ?? "id",
  key: "mealplan.meal_plan_subscriptions",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMealPlanSubscriptionDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MealPlanSubscriptionPrototype[] =>
  cache.getDummyRecords(mealPlanSubscriptionSchema, { ...options, tenantId }, count, () =>
    createMealPlanSubscriptionDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMealPlanSubscriptionStore = (
  tenantId: string,
  initialRecords: readonly MealPlanSubscriptionPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MealPlanSubscriptionStore => {
  const scope = {
    collection: mealPlanSubscriptionSchema.collection,
    domain: mealPlanSubscriptionSchema.domain,
    tenantId,
  };
  const store = cache.getStore<MealPlanSubscriptionPrototype>(
    scope,
    () =>
      createTenantProjectionStore<MealPlanSubscriptionPrototype>(
        scope,
        (record) => String(record[mealPlanSubscriptionSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMealPlanSubscriptionLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MealPlanSubscriptionPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MealPlanSubscriptionStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MealPlanSubscriptionPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMealPlanSubscriptionStore(tenantId, [], cache);
  return createLiveProjectionBinding<MealPlanSubscriptionPrototype>({
    ...bindingOptions,
    scope: {
      collection: mealPlanSubscriptionSchema.collection,
      domain: mealPlanSubscriptionSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMealPlanSubscriptionStore = (
  tenantId: string,
  store: MealPlanSubscriptionStore = createMealPlanSubscriptionStore(tenantId)
): MealPlanSubscriptionStore => {
  prototypeStoreRegistry.register("mealplan.meal_plan_subscriptions", store);
  return store;
};

export const useMealPlanSubscriptionSnapshot = () => prototypeStoreRegistry.use("mealplan.meal_plan_subscriptions");

export const mealPlanSubscriptionFixtures = {
  empty: (): MealPlanSubscriptionPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MealPlanSubscriptionPrototype[] =>
    createCachedMealPlanSubscriptionDummyRecords(tenantId, 8, { seed: "mealplan.meal_plan_subscriptions:happy" }),
  invalidMissingId: (): MealPlanSubscriptionPrototype[] =>
    createMealPlanSubscriptionDummyFactory({ seed: "mealplan.meal_plan_subscriptions:invalid" }).list(1, () => ({
      [mealPlanSubscriptionSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MealPlanSubscriptionPrototype[] =>
    createCachedMealPlanSubscriptionDummyRecords(tenantId, count, { seed: "mealplan.meal_plan_subscriptions:high-volume" }),
};

export const mealPlanSubscriptionFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanSubscriptionPrototype> => ({
    collection: mealPlanSubscriptionSchema.collection,
    domain: mealPlanSubscriptionSchema.domain,
    name: "mealplan.meal_plan_subscriptions.empty",
    records: mealPlanSubscriptionFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanSubscriptionPrototype> => ({
    collection: mealPlanSubscriptionSchema.collection,
    domain: mealPlanSubscriptionSchema.domain,
    name: "mealplan.meal_plan_subscriptions.happyPath",
    records: mealPlanSubscriptionFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanSubscriptionPrototype> => ({
    collection: mealPlanSubscriptionSchema.collection,
    domain: mealPlanSubscriptionSchema.domain,
    name: "mealplan.meal_plan_subscriptions.invalidMissingId",
    records: mealPlanSubscriptionFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MealPlanSubscriptionPrototype> => ({
    collection: mealPlanSubscriptionSchema.collection,
    domain: mealPlanSubscriptionSchema.domain,
    name: "mealplan.meal_plan_subscriptions.highVolume",
    records: mealPlanSubscriptionFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const mealPlanSubscriptionBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "mealplan.meal_plan_subscriptions.dummy.list.1000",
    records: 1000,
    run: () => createCachedMealPlanSubscriptionDummyRecords("tenant-benchmark", 1000, { seed: "mealplan.meal_plan_subscriptions:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_subscriptions.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanSubscriptionDummyFactory({ seed: "mealplan.meal_plan_subscriptions:bench-store" }).list(1000);
      const store = createMealPlanSubscriptionStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_subscriptions.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanSubscriptionDummyFactory({ seed: "mealplan.meal_plan_subscriptions:bench-apply" }).list(1000);
      const store = createMealPlanSubscriptionStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[mealPlanSubscriptionSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: mealPlanSubscriptionSchema.collection,
          domain: mealPlanSubscriptionSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_subscriptions.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanSubscriptionDummyFactory({ seed: "mealplan.meal_plan_subscriptions:bench-reset" }).list(1000);
      const store = createMealPlanSubscriptionStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_subscriptions.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanSubscriptionDummyFactory({ seed: "mealplan.meal_plan_subscriptions:bench-selector" }).list(1000);
      const store = createMealPlanSubscriptionStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[mealPlanSubscriptionSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MealPlanZonePrototype = Record<string, unknown>;

/** Typed field shape for mealplan.v1.MealPlanZone. Use it to narrow MealPlanZonePrototype records. */
export type MealPlanZoneFields = {
  readonly "delivery_fee_per_drop_minor": number;
  readonly "id": string;
  readonly "is_below_pool": boolean;
  readonly "min_chefs": number;
  readonly "name": string;
};

export const mealPlanZoneFieldOrder = ["id","name","min_chefs","delivery_fee_per_drop_minor","is_below_pool"] as const satisfies readonly (keyof MealPlanZoneFields)[];

export const mealPlanZoneRequiredFields = ["delivery_fee_per_drop_minor","id","is_below_pool","min_chefs","name"] as const satisfies readonly (keyof MealPlanZoneFields)[];

export type MealPlanZoneStore = TenantProjectionStore<MealPlanZonePrototype>;

export const mealPlanZoneSchema = {
  "collection": "meal_plan_zones",
  "domain": "mealplan",
  "fields": [
    {
      "kind": "int",
      "name": "delivery_fee_per_drop_minor"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "bool",
      "name": "is_below_pool"
    },
    {
      "kind": "int",
      "name": "min_chefs"
    },
    {
      "kind": "string",
      "name": "name"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMealPlanZoneDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MealPlanZonePrototype>(mealPlanZoneSchema, options);

export const mealPlanZoneRuntimeConstants = {
  collection: mealPlanZoneSchema.collection,
  domain: mealPlanZoneSchema.domain,
  idField: mealPlanZoneSchema.idField ?? "id",
  key: "mealplan.meal_plan_zones",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMealPlanZoneDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MealPlanZonePrototype[] =>
  cache.getDummyRecords(mealPlanZoneSchema, { ...options, tenantId }, count, () =>
    createMealPlanZoneDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMealPlanZoneStore = (
  tenantId: string,
  initialRecords: readonly MealPlanZonePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MealPlanZoneStore => {
  const scope = {
    collection: mealPlanZoneSchema.collection,
    domain: mealPlanZoneSchema.domain,
    tenantId,
  };
  const store = cache.getStore<MealPlanZonePrototype>(
    scope,
    () =>
      createTenantProjectionStore<MealPlanZonePrototype>(
        scope,
        (record) => String(record[mealPlanZoneSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMealPlanZoneLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MealPlanZonePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MealPlanZoneStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MealPlanZonePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMealPlanZoneStore(tenantId, [], cache);
  return createLiveProjectionBinding<MealPlanZonePrototype>({
    ...bindingOptions,
    scope: {
      collection: mealPlanZoneSchema.collection,
      domain: mealPlanZoneSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMealPlanZoneStore = (
  tenantId: string,
  store: MealPlanZoneStore = createMealPlanZoneStore(tenantId)
): MealPlanZoneStore => {
  prototypeStoreRegistry.register("mealplan.meal_plan_zones", store);
  return store;
};

export const useMealPlanZoneSnapshot = () => prototypeStoreRegistry.use("mealplan.meal_plan_zones");

export const mealPlanZoneFixtures = {
  empty: (): MealPlanZonePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MealPlanZonePrototype[] =>
    createCachedMealPlanZoneDummyRecords(tenantId, 8, { seed: "mealplan.meal_plan_zones:happy" }),
  invalidMissingId: (): MealPlanZonePrototype[] =>
    createMealPlanZoneDummyFactory({ seed: "mealplan.meal_plan_zones:invalid" }).list(1, () => ({
      [mealPlanZoneSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MealPlanZonePrototype[] =>
    createCachedMealPlanZoneDummyRecords(tenantId, count, { seed: "mealplan.meal_plan_zones:high-volume" }),
};

export const mealPlanZoneFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanZonePrototype> => ({
    collection: mealPlanZoneSchema.collection,
    domain: mealPlanZoneSchema.domain,
    name: "mealplan.meal_plan_zones.empty",
    records: mealPlanZoneFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanZonePrototype> => ({
    collection: mealPlanZoneSchema.collection,
    domain: mealPlanZoneSchema.domain,
    name: "mealplan.meal_plan_zones.happyPath",
    records: mealPlanZoneFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanZonePrototype> => ({
    collection: mealPlanZoneSchema.collection,
    domain: mealPlanZoneSchema.domain,
    name: "mealplan.meal_plan_zones.invalidMissingId",
    records: mealPlanZoneFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MealPlanZonePrototype> => ({
    collection: mealPlanZoneSchema.collection,
    domain: mealPlanZoneSchema.domain,
    name: "mealplan.meal_plan_zones.highVolume",
    records: mealPlanZoneFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const mealPlanZoneBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "mealplan.meal_plan_zones.dummy.list.1000",
    records: 1000,
    run: () => createCachedMealPlanZoneDummyRecords("tenant-benchmark", 1000, { seed: "mealplan.meal_plan_zones:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_zones.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanZoneDummyFactory({ seed: "mealplan.meal_plan_zones:bench-store" }).list(1000);
      const store = createMealPlanZoneStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_zones.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanZoneDummyFactory({ seed: "mealplan.meal_plan_zones:bench-apply" }).list(1000);
      const store = createMealPlanZoneStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[mealPlanZoneSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: mealPlanZoneSchema.collection,
          domain: mealPlanZoneSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_zones.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanZoneDummyFactory({ seed: "mealplan.meal_plan_zones:bench-reset" }).list(1000);
      const store = createMealPlanZoneStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "mealplan.meal_plan_zones.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanZoneDummyFactory({ seed: "mealplan.meal_plan_zones:bench-selector" }).list(1000);
      const store = createMealPlanZoneStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[mealPlanZoneSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type ChefMenuPrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.ChefMenu. Use it to narrow ChefMenuPrototype records. */
export type ChefMenuFields = {
  readonly "cadence": "MENU_CADENCE_WEEKLY" | "MENU_CADENCE_DAILY" | "MENU_CADENCE_MONTHLY" | "MENU_CADENCE_ONE_OFF";
  readonly "chef_profile_id": string;
  readonly "cover_image_url": string;
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "id": string;
  readonly "location_id": string;
  readonly "organization_id": string;
  readonly "status": "MENU_STATUS_DRAFT" | "MENU_STATUS_PUBLISHED" | "MENU_STATUS_PAUSED" | "MENU_STATUS_ARCHIVED";
  readonly "summary": string;
  readonly "title": string;
  readonly "updated_at"?: string | null;
  readonly "week_start": string;
};

export const chefMenuFieldOrder = ["id","organization_id","created_at","updated_at","chef_profile_id","title","summary","cadence","status","week_start","cover_image_url","currency","location_id"] as const satisfies readonly (keyof ChefMenuFields)[];

export const chefMenuRequiredFields = ["cadence","chef_profile_id","cover_image_url","currency","id","location_id","organization_id","status","summary","title","week_start"] as const satisfies readonly (keyof ChefMenuFields)[];

export type ChefMenuStore = TenantProjectionStore<ChefMenuPrototype>;

export const chefMenuSchema = {
  "collection": "chef_menus",
  "domain": "menu",
  "fields": [
    {
      "enumValues": [
        "MENU_CADENCE_WEEKLY",
        "MENU_CADENCE_DAILY",
        "MENU_CADENCE_MONTHLY",
        "MENU_CADENCE_ONE_OFF"
      ],
      "kind": "enum",
      "name": "cadence"
    },
    {
      "kind": "uuid",
      "name": "chef_profile_id"
    },
    {
      "kind": "url",
      "name": "cover_image_url"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "location_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "enumValues": [
        "MENU_STATUS_DRAFT",
        "MENU_STATUS_PUBLISHED",
        "MENU_STATUS_PAUSED",
        "MENU_STATUS_ARCHIVED"
      ],
      "kind": "enum",
      "name": "status"
    },
    {
      "kind": "text",
      "name": "summary"
    },
    {
      "kind": "string",
      "name": "title"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "week_start"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createChefMenuDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<ChefMenuPrototype>(chefMenuSchema, options);

export const chefMenuRuntimeConstants = {
  collection: chefMenuSchema.collection,
  domain: chefMenuSchema.domain,
  idField: chefMenuSchema.idField ?? "id",
  key: "menu.chef_menus",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedChefMenuDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly ChefMenuPrototype[] =>
  cache.getDummyRecords(chefMenuSchema, { ...options, tenantId }, count, () =>
    createChefMenuDummyFactory({ ...options, tenantId }).list(count)
  );

export const createChefMenuStore = (
  tenantId: string,
  initialRecords: readonly ChefMenuPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): ChefMenuStore => {
  const scope = {
    collection: chefMenuSchema.collection,
    domain: chefMenuSchema.domain,
    tenantId,
  };
  const store = cache.getStore<ChefMenuPrototype>(
    scope,
    () =>
      createTenantProjectionStore<ChefMenuPrototype>(
        scope,
        (record) => String(record[chefMenuSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectChefMenuLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<ChefMenuPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: ChefMenuStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<ChefMenuPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createChefMenuStore(tenantId, [], cache);
  return createLiveProjectionBinding<ChefMenuPrototype>({
    ...bindingOptions,
    scope: {
      collection: chefMenuSchema.collection,
      domain: chefMenuSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerChefMenuStore = (
  tenantId: string,
  store: ChefMenuStore = createChefMenuStore(tenantId)
): ChefMenuStore => {
  prototypeStoreRegistry.register("menu.chef_menus", store);
  return store;
};

export const useChefMenuSnapshot = () => prototypeStoreRegistry.use("menu.chef_menus");

export const chefMenuFixtures = {
  empty: (): ChefMenuPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly ChefMenuPrototype[] =>
    createCachedChefMenuDummyRecords(tenantId, 8, { seed: "menu.chef_menus:happy" }),
  invalidMissingId: (): ChefMenuPrototype[] =>
    createChefMenuDummyFactory({ seed: "menu.chef_menus:invalid" }).list(1, () => ({
      [chefMenuSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly ChefMenuPrototype[] =>
    createCachedChefMenuDummyRecords(tenantId, count, { seed: "menu.chef_menus:high-volume" }),
};

export const chefMenuFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<ChefMenuPrototype> => ({
    collection: chefMenuSchema.collection,
    domain: chefMenuSchema.domain,
    name: "menu.chef_menus.empty",
    records: chefMenuFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<ChefMenuPrototype> => ({
    collection: chefMenuSchema.collection,
    domain: chefMenuSchema.domain,
    name: "menu.chef_menus.happyPath",
    records: chefMenuFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<ChefMenuPrototype> => ({
    collection: chefMenuSchema.collection,
    domain: chefMenuSchema.domain,
    name: "menu.chef_menus.invalidMissingId",
    records: chefMenuFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<ChefMenuPrototype> => ({
    collection: chefMenuSchema.collection,
    domain: chefMenuSchema.domain,
    name: "menu.chef_menus.highVolume",
    records: chefMenuFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const chefMenuBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.chef_menus.dummy.list.1000",
    records: 1000,
    run: () => createCachedChefMenuDummyRecords("tenant-benchmark", 1000, { seed: "menu.chef_menus:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.chef_menus.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createChefMenuDummyFactory({ seed: "menu.chef_menus:bench-store" }).list(1000);
      const store = createChefMenuStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.chef_menus.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createChefMenuDummyFactory({ seed: "menu.chef_menus:bench-apply" }).list(1000);
      const store = createChefMenuStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[chefMenuSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: chefMenuSchema.collection,
          domain: chefMenuSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.chef_menus.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createChefMenuDummyFactory({ seed: "menu.chef_menus:bench-reset" }).list(1000);
      const store = createChefMenuStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.chef_menus.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createChefMenuDummyFactory({ seed: "menu.chef_menus:bench-selector" }).list(1000);
      const store = createChefMenuStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[chefMenuSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type DishFavouritePrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.DishFavourite. Use it to narrow DishFavouritePrototype records. */
export type DishFavouriteFields = {
  readonly "created_at"?: string | null;
  readonly "dish_id": string;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "profile_id": string;
  readonly "updated_at"?: string | null;
};

export const dishFavouriteFieldOrder = ["id","organization_id","created_at","updated_at","profile_id","dish_id"] as const satisfies readonly (keyof DishFavouriteFields)[];

export const dishFavouriteRequiredFields = ["dish_id","id","organization_id","profile_id"] as const satisfies readonly (keyof DishFavouriteFields)[];

export type DishFavouriteStore = TenantProjectionStore<DishFavouritePrototype>;

export const dishFavouriteSchema = {
  "collection": "dish_favourites",
  "domain": "menu",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "dish_id"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createDishFavouriteDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<DishFavouritePrototype>(dishFavouriteSchema, options);

export const dishFavouriteRuntimeConstants = {
  collection: dishFavouriteSchema.collection,
  domain: dishFavouriteSchema.domain,
  idField: dishFavouriteSchema.idField ?? "id",
  key: "menu.dish_favourites",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedDishFavouriteDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly DishFavouritePrototype[] =>
  cache.getDummyRecords(dishFavouriteSchema, { ...options, tenantId }, count, () =>
    createDishFavouriteDummyFactory({ ...options, tenantId }).list(count)
  );

export const createDishFavouriteStore = (
  tenantId: string,
  initialRecords: readonly DishFavouritePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): DishFavouriteStore => {
  const scope = {
    collection: dishFavouriteSchema.collection,
    domain: dishFavouriteSchema.domain,
    tenantId,
  };
  const store = cache.getStore<DishFavouritePrototype>(
    scope,
    () =>
      createTenantProjectionStore<DishFavouritePrototype>(
        scope,
        (record) => String(record[dishFavouriteSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectDishFavouriteLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<DishFavouritePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: DishFavouriteStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<DishFavouritePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createDishFavouriteStore(tenantId, [], cache);
  return createLiveProjectionBinding<DishFavouritePrototype>({
    ...bindingOptions,
    scope: {
      collection: dishFavouriteSchema.collection,
      domain: dishFavouriteSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerDishFavouriteStore = (
  tenantId: string,
  store: DishFavouriteStore = createDishFavouriteStore(tenantId)
): DishFavouriteStore => {
  prototypeStoreRegistry.register("menu.dish_favourites", store);
  return store;
};

export const useDishFavouriteSnapshot = () => prototypeStoreRegistry.use("menu.dish_favourites");

export const dishFavouriteFixtures = {
  empty: (): DishFavouritePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly DishFavouritePrototype[] =>
    createCachedDishFavouriteDummyRecords(tenantId, 8, { seed: "menu.dish_favourites:happy" }),
  invalidMissingId: (): DishFavouritePrototype[] =>
    createDishFavouriteDummyFactory({ seed: "menu.dish_favourites:invalid" }).list(1, () => ({
      [dishFavouriteSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly DishFavouritePrototype[] =>
    createCachedDishFavouriteDummyRecords(tenantId, count, { seed: "menu.dish_favourites:high-volume" }),
};

export const dishFavouriteFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<DishFavouritePrototype> => ({
    collection: dishFavouriteSchema.collection,
    domain: dishFavouriteSchema.domain,
    name: "menu.dish_favourites.empty",
    records: dishFavouriteFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<DishFavouritePrototype> => ({
    collection: dishFavouriteSchema.collection,
    domain: dishFavouriteSchema.domain,
    name: "menu.dish_favourites.happyPath",
    records: dishFavouriteFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<DishFavouritePrototype> => ({
    collection: dishFavouriteSchema.collection,
    domain: dishFavouriteSchema.domain,
    name: "menu.dish_favourites.invalidMissingId",
    records: dishFavouriteFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<DishFavouritePrototype> => ({
    collection: dishFavouriteSchema.collection,
    domain: dishFavouriteSchema.domain,
    name: "menu.dish_favourites.highVolume",
    records: dishFavouriteFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const dishFavouriteBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.dish_favourites.dummy.list.1000",
    records: 1000,
    run: () => createCachedDishFavouriteDummyRecords("tenant-benchmark", 1000, { seed: "menu.dish_favourites:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.dish_favourites.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createDishFavouriteDummyFactory({ seed: "menu.dish_favourites:bench-store" }).list(1000);
      const store = createDishFavouriteStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.dish_favourites.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createDishFavouriteDummyFactory({ seed: "menu.dish_favourites:bench-apply" }).list(1000);
      const store = createDishFavouriteStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[dishFavouriteSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: dishFavouriteSchema.collection,
          domain: dishFavouriteSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.dish_favourites.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createDishFavouriteDummyFactory({ seed: "menu.dish_favourites:bench-reset" }).list(1000);
      const store = createDishFavouriteStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.dish_favourites.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createDishFavouriteDummyFactory({ seed: "menu.dish_favourites:bench-selector" }).list(1000);
      const store = createDishFavouriteStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[dishFavouriteSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type DishPrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.Dish. Use it to narrow DishPrototype records. */
export type DishFields = {
  readonly "allergens": readonly (string)[];
  readonly "available_days": readonly (number)[];
  readonly "category": string;
  readonly "chef_profile_id": string;
  readonly "cook_time_minutes": number;
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "description": string;
  readonly "id": string;
  readonly "image_url": string;
  readonly "ingredients": readonly (string)[];
  readonly "menu_id": string;
  readonly "name": string;
  readonly "organization_id": string;
  readonly "position": number;
  readonly "price_minor": number;
  readonly "status": "DISH_STATUS_AVAILABLE" | "DISH_STATUS_SOLD_OUT" | "DISH_STATUS_HIDDEN";
  readonly "stock_quantity"?: number | null;
  readonly "updated_at"?: string | null;
};

export const dishFieldOrder = ["id","organization_id","created_at","updated_at","menu_id","chef_profile_id","name","description","category","price_minor","currency","image_url","available_days","position","status","ingredients","allergens","cook_time_minutes","stock_quantity"] as const satisfies readonly (keyof DishFields)[];

export const dishRequiredFields = ["allergens","available_days","category","chef_profile_id","cook_time_minutes","currency","description","id","image_url","ingredients","menu_id","name","organization_id","position","price_minor","status"] as const satisfies readonly (keyof DishFields)[];

export type DishStore = TenantProjectionStore<DishPrototype>;

export const dishSchema = {
  "collection": "dishes",
  "domain": "menu",
  "fields": [
    {
      "kind": "string",
      "name": "allergens",
      "repeated": true
    },
    {
      "kind": "int",
      "name": "available_days",
      "repeated": true
    },
    {
      "kind": "string",
      "name": "category"
    },
    {
      "kind": "uuid",
      "name": "chef_profile_id"
    },
    {
      "kind": "int",
      "name": "cook_time_minutes"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "text",
      "name": "description"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "url",
      "name": "image_url"
    },
    {
      "kind": "string",
      "name": "ingredients",
      "repeated": true
    },
    {
      "kind": "uuid",
      "name": "menu_id"
    },
    {
      "kind": "string",
      "name": "name"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "int",
      "name": "position"
    },
    {
      "kind": "int",
      "name": "price_minor"
    },
    {
      "enumValues": [
        "DISH_STATUS_AVAILABLE",
        "DISH_STATUS_SOLD_OUT",
        "DISH_STATUS_HIDDEN"
      ],
      "kind": "enum",
      "name": "status"
    },
    {
      "kind": "int",
      "name": "stock_quantity",
      "optional": true
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createDishDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<DishPrototype>(dishSchema, options);

export const dishRuntimeConstants = {
  collection: dishSchema.collection,
  domain: dishSchema.domain,
  idField: dishSchema.idField ?? "id",
  key: "menu.dishes",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedDishDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly DishPrototype[] =>
  cache.getDummyRecords(dishSchema, { ...options, tenantId }, count, () =>
    createDishDummyFactory({ ...options, tenantId }).list(count)
  );

export const createDishStore = (
  tenantId: string,
  initialRecords: readonly DishPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): DishStore => {
  const scope = {
    collection: dishSchema.collection,
    domain: dishSchema.domain,
    tenantId,
  };
  const store = cache.getStore<DishPrototype>(
    scope,
    () =>
      createTenantProjectionStore<DishPrototype>(
        scope,
        (record) => String(record[dishSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectDishLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<DishPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: DishStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<DishPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createDishStore(tenantId, [], cache);
  return createLiveProjectionBinding<DishPrototype>({
    ...bindingOptions,
    scope: {
      collection: dishSchema.collection,
      domain: dishSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerDishStore = (
  tenantId: string,
  store: DishStore = createDishStore(tenantId)
): DishStore => {
  prototypeStoreRegistry.register("menu.dishes", store);
  return store;
};

export const useDishSnapshot = () => prototypeStoreRegistry.use("menu.dishes");

export const dishFixtures = {
  empty: (): DishPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly DishPrototype[] =>
    createCachedDishDummyRecords(tenantId, 8, { seed: "menu.dishes:happy" }),
  invalidMissingId: (): DishPrototype[] =>
    createDishDummyFactory({ seed: "menu.dishes:invalid" }).list(1, () => ({
      [dishSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly DishPrototype[] =>
    createCachedDishDummyRecords(tenantId, count, { seed: "menu.dishes:high-volume" }),
};

export const dishFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<DishPrototype> => ({
    collection: dishSchema.collection,
    domain: dishSchema.domain,
    name: "menu.dishes.empty",
    records: dishFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<DishPrototype> => ({
    collection: dishSchema.collection,
    domain: dishSchema.domain,
    name: "menu.dishes.happyPath",
    records: dishFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<DishPrototype> => ({
    collection: dishSchema.collection,
    domain: dishSchema.domain,
    name: "menu.dishes.invalidMissingId",
    records: dishFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<DishPrototype> => ({
    collection: dishSchema.collection,
    domain: dishSchema.domain,
    name: "menu.dishes.highVolume",
    records: dishFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const dishBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.dishes.dummy.list.1000",
    records: 1000,
    run: () => createCachedDishDummyRecords("tenant-benchmark", 1000, { seed: "menu.dishes:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.dishes.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createDishDummyFactory({ seed: "menu.dishes:bench-store" }).list(1000);
      const store = createDishStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.dishes.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createDishDummyFactory({ seed: "menu.dishes:bench-apply" }).list(1000);
      const store = createDishStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[dishSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: dishSchema.collection,
          domain: dishSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.dishes.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createDishDummyFactory({ seed: "menu.dishes:bench-reset" }).list(1000);
      const store = createDishStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.dishes.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createDishDummyFactory({ seed: "menu.dishes:bench-selector" }).list(1000);
      const store = createDishStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[dishSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MealCategoryPrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.MealCategory. Use it to narrow MealCategoryPrototype records. */
export type MealCategoryFields = {
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "is_active": boolean;
  readonly "label": string;
  readonly "organization_id": string;
  readonly "slug": string;
  readonly "updated_at"?: string | null;
};

export const mealCategoryFieldOrder = ["slug","label","id","organization_id","is_active","created_at","updated_at"] as const satisfies readonly (keyof MealCategoryFields)[];

export const mealCategoryRequiredFields = ["id","is_active","label","organization_id","slug"] as const satisfies readonly (keyof MealCategoryFields)[];

export type MealCategoryStore = TenantProjectionStore<MealCategoryPrototype>;

export const mealCategorySchema = {
  "collection": "meal_categories",
  "domain": "menu",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "bool",
      "name": "is_active"
    },
    {
      "kind": "string",
      "name": "label"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "slug"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMealCategoryDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MealCategoryPrototype>(mealCategorySchema, options);

export const mealCategoryRuntimeConstants = {
  collection: mealCategorySchema.collection,
  domain: mealCategorySchema.domain,
  idField: mealCategorySchema.idField ?? "id",
  key: "menu.meal_categories",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMealCategoryDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MealCategoryPrototype[] =>
  cache.getDummyRecords(mealCategorySchema, { ...options, tenantId }, count, () =>
    createMealCategoryDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMealCategoryStore = (
  tenantId: string,
  initialRecords: readonly MealCategoryPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MealCategoryStore => {
  const scope = {
    collection: mealCategorySchema.collection,
    domain: mealCategorySchema.domain,
    tenantId,
  };
  const store = cache.getStore<MealCategoryPrototype>(
    scope,
    () =>
      createTenantProjectionStore<MealCategoryPrototype>(
        scope,
        (record) => String(record[mealCategorySchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMealCategoryLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MealCategoryPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MealCategoryStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MealCategoryPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMealCategoryStore(tenantId, [], cache);
  return createLiveProjectionBinding<MealCategoryPrototype>({
    ...bindingOptions,
    scope: {
      collection: mealCategorySchema.collection,
      domain: mealCategorySchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMealCategoryStore = (
  tenantId: string,
  store: MealCategoryStore = createMealCategoryStore(tenantId)
): MealCategoryStore => {
  prototypeStoreRegistry.register("menu.meal_categories", store);
  return store;
};

export const useMealCategorySnapshot = () => prototypeStoreRegistry.use("menu.meal_categories");

export const mealCategoryFixtures = {
  empty: (): MealCategoryPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MealCategoryPrototype[] =>
    createCachedMealCategoryDummyRecords(tenantId, 8, { seed: "menu.meal_categories:happy" }),
  invalidMissingId: (): MealCategoryPrototype[] =>
    createMealCategoryDummyFactory({ seed: "menu.meal_categories:invalid" }).list(1, () => ({
      [mealCategorySchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MealCategoryPrototype[] =>
    createCachedMealCategoryDummyRecords(tenantId, count, { seed: "menu.meal_categories:high-volume" }),
};

export const mealCategoryFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealCategoryPrototype> => ({
    collection: mealCategorySchema.collection,
    domain: mealCategorySchema.domain,
    name: "menu.meal_categories.empty",
    records: mealCategoryFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealCategoryPrototype> => ({
    collection: mealCategorySchema.collection,
    domain: mealCategorySchema.domain,
    name: "menu.meal_categories.happyPath",
    records: mealCategoryFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealCategoryPrototype> => ({
    collection: mealCategorySchema.collection,
    domain: mealCategorySchema.domain,
    name: "menu.meal_categories.invalidMissingId",
    records: mealCategoryFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MealCategoryPrototype> => ({
    collection: mealCategorySchema.collection,
    domain: mealCategorySchema.domain,
    name: "menu.meal_categories.highVolume",
    records: mealCategoryFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const mealCategoryBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.meal_categories.dummy.list.1000",
    records: 1000,
    run: () => createCachedMealCategoryDummyRecords("tenant-benchmark", 1000, { seed: "menu.meal_categories:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.meal_categories.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMealCategoryDummyFactory({ seed: "menu.meal_categories:bench-store" }).list(1000);
      const store = createMealCategoryStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_categories.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMealCategoryDummyFactory({ seed: "menu.meal_categories:bench-apply" }).list(1000);
      const store = createMealCategoryStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[mealCategorySchema.idField ?? "id"] ?? index);
        store.apply({
          collection: mealCategorySchema.collection,
          domain: mealCategorySchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_categories.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMealCategoryDummyFactory({ seed: "menu.meal_categories:bench-reset" }).list(1000);
      const store = createMealCategoryStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_categories.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMealCategoryDummyFactory({ seed: "menu.meal_categories:bench-selector" }).list(1000);
      const store = createMealCategoryStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[mealCategorySchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MealPlanCyclePrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.MealPlanCycle. Use it to narrow MealPlanCyclePrototype records. */
export type MealPlanCycleFields = {
  readonly "confirmed_at": string;
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "cycle_index": number;
  readonly "ends_on": string;
  readonly "id": string;
  readonly "locks_at": string;
  readonly "order_id": string;
  readonly "organization_id": string;
  readonly "plan_id": string;
  readonly "starts_on": string;
  readonly "status": string;
  readonly "total_minor": number;
  readonly "updated_at"?: string | null;
};

export const mealPlanCycleFieldOrder = ["id","organization_id","created_at","updated_at","plan_id","cycle_index","starts_on","ends_on","status","locks_at","total_minor","currency","order_id","confirmed_at"] as const satisfies readonly (keyof MealPlanCycleFields)[];

export const mealPlanCycleRequiredFields = ["confirmed_at","currency","cycle_index","ends_on","id","locks_at","order_id","organization_id","plan_id","starts_on","status","total_minor"] as const satisfies readonly (keyof MealPlanCycleFields)[];

export type MealPlanCycleStore = TenantProjectionStore<MealPlanCyclePrototype>;

export const mealPlanCycleSchema = {
  "collection": "meal_plan_cycles",
  "domain": "menu",
  "fields": [
    {
      "kind": "timestamp",
      "name": "confirmed_at"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "int",
      "name": "cycle_index"
    },
    {
      "kind": "string",
      "name": "ends_on"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "timestamp",
      "name": "locks_at"
    },
    {
      "kind": "uuid",
      "name": "order_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "plan_id"
    },
    {
      "kind": "string",
      "name": "starts_on"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "int",
      "name": "total_minor"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMealPlanCycleDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MealPlanCyclePrototype>(mealPlanCycleSchema, options);

export const mealPlanCycleRuntimeConstants = {
  collection: mealPlanCycleSchema.collection,
  domain: mealPlanCycleSchema.domain,
  idField: mealPlanCycleSchema.idField ?? "id",
  key: "menu.meal_plan_cycles",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMealPlanCycleDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MealPlanCyclePrototype[] =>
  cache.getDummyRecords(mealPlanCycleSchema, { ...options, tenantId }, count, () =>
    createMealPlanCycleDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMealPlanCycleStore = (
  tenantId: string,
  initialRecords: readonly MealPlanCyclePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MealPlanCycleStore => {
  const scope = {
    collection: mealPlanCycleSchema.collection,
    domain: mealPlanCycleSchema.domain,
    tenantId,
  };
  const store = cache.getStore<MealPlanCyclePrototype>(
    scope,
    () =>
      createTenantProjectionStore<MealPlanCyclePrototype>(
        scope,
        (record) => String(record[mealPlanCycleSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMealPlanCycleLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MealPlanCyclePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MealPlanCycleStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MealPlanCyclePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMealPlanCycleStore(tenantId, [], cache);
  return createLiveProjectionBinding<MealPlanCyclePrototype>({
    ...bindingOptions,
    scope: {
      collection: mealPlanCycleSchema.collection,
      domain: mealPlanCycleSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMealPlanCycleStore = (
  tenantId: string,
  store: MealPlanCycleStore = createMealPlanCycleStore(tenantId)
): MealPlanCycleStore => {
  prototypeStoreRegistry.register("menu.meal_plan_cycles", store);
  return store;
};

export const useMealPlanCycleSnapshot = () => prototypeStoreRegistry.use("menu.meal_plan_cycles");

export const mealPlanCycleFixtures = {
  empty: (): MealPlanCyclePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MealPlanCyclePrototype[] =>
    createCachedMealPlanCycleDummyRecords(tenantId, 8, { seed: "menu.meal_plan_cycles:happy" }),
  invalidMissingId: (): MealPlanCyclePrototype[] =>
    createMealPlanCycleDummyFactory({ seed: "menu.meal_plan_cycles:invalid" }).list(1, () => ({
      [mealPlanCycleSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MealPlanCyclePrototype[] =>
    createCachedMealPlanCycleDummyRecords(tenantId, count, { seed: "menu.meal_plan_cycles:high-volume" }),
};

export const mealPlanCycleFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanCyclePrototype> => ({
    collection: mealPlanCycleSchema.collection,
    domain: mealPlanCycleSchema.domain,
    name: "menu.meal_plan_cycles.empty",
    records: mealPlanCycleFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanCyclePrototype> => ({
    collection: mealPlanCycleSchema.collection,
    domain: mealPlanCycleSchema.domain,
    name: "menu.meal_plan_cycles.happyPath",
    records: mealPlanCycleFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanCyclePrototype> => ({
    collection: mealPlanCycleSchema.collection,
    domain: mealPlanCycleSchema.domain,
    name: "menu.meal_plan_cycles.invalidMissingId",
    records: mealPlanCycleFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MealPlanCyclePrototype> => ({
    collection: mealPlanCycleSchema.collection,
    domain: mealPlanCycleSchema.domain,
    name: "menu.meal_plan_cycles.highVolume",
    records: mealPlanCycleFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const mealPlanCycleBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.meal_plan_cycles.dummy.list.1000",
    records: 1000,
    run: () => createCachedMealPlanCycleDummyRecords("tenant-benchmark", 1000, { seed: "menu.meal_plan_cycles:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.meal_plan_cycles.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanCycleDummyFactory({ seed: "menu.meal_plan_cycles:bench-store" }).list(1000);
      const store = createMealPlanCycleStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plan_cycles.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanCycleDummyFactory({ seed: "menu.meal_plan_cycles:bench-apply" }).list(1000);
      const store = createMealPlanCycleStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[mealPlanCycleSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: mealPlanCycleSchema.collection,
          domain: mealPlanCycleSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plan_cycles.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanCycleDummyFactory({ seed: "menu.meal_plan_cycles:bench-reset" }).list(1000);
      const store = createMealPlanCycleStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plan_cycles.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanCycleDummyFactory({ seed: "menu.meal_plan_cycles:bench-selector" }).list(1000);
      const store = createMealPlanCycleStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[mealPlanCycleSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MealPlanEntryPrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.MealPlanEntry. Use it to narrow MealPlanEntryPrototype records. */
export type MealPlanEntryFields = {
  readonly "created_at"?: string | null;
  readonly "day": number;
  readonly "dish_id": string;
  readonly "id": string;
  readonly "meal_slot": string;
  readonly "organization_id": string;
  readonly "plan_id": string;
  readonly "position": number;
  readonly "updated_at"?: string | null;
};

export const mealPlanEntryFieldOrder = ["id","organization_id","created_at","updated_at","plan_id","dish_id","day","meal_slot","position"] as const satisfies readonly (keyof MealPlanEntryFields)[];

export const mealPlanEntryRequiredFields = ["day","dish_id","id","meal_slot","organization_id","plan_id","position"] as const satisfies readonly (keyof MealPlanEntryFields)[];

export type MealPlanEntryStore = TenantProjectionStore<MealPlanEntryPrototype>;

export const mealPlanEntrySchema = {
  "collection": "meal_plan_entries",
  "domain": "menu",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "int",
      "name": "day"
    },
    {
      "kind": "uuid",
      "name": "dish_id"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "meal_slot"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "plan_id"
    },
    {
      "kind": "int",
      "name": "position"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMealPlanEntryDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MealPlanEntryPrototype>(mealPlanEntrySchema, options);

export const mealPlanEntryRuntimeConstants = {
  collection: mealPlanEntrySchema.collection,
  domain: mealPlanEntrySchema.domain,
  idField: mealPlanEntrySchema.idField ?? "id",
  key: "menu.meal_plan_entries",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMealPlanEntryDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MealPlanEntryPrototype[] =>
  cache.getDummyRecords(mealPlanEntrySchema, { ...options, tenantId }, count, () =>
    createMealPlanEntryDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMealPlanEntryStore = (
  tenantId: string,
  initialRecords: readonly MealPlanEntryPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MealPlanEntryStore => {
  const scope = {
    collection: mealPlanEntrySchema.collection,
    domain: mealPlanEntrySchema.domain,
    tenantId,
  };
  const store = cache.getStore<MealPlanEntryPrototype>(
    scope,
    () =>
      createTenantProjectionStore<MealPlanEntryPrototype>(
        scope,
        (record) => String(record[mealPlanEntrySchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMealPlanEntryLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MealPlanEntryPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MealPlanEntryStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MealPlanEntryPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMealPlanEntryStore(tenantId, [], cache);
  return createLiveProjectionBinding<MealPlanEntryPrototype>({
    ...bindingOptions,
    scope: {
      collection: mealPlanEntrySchema.collection,
      domain: mealPlanEntrySchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMealPlanEntryStore = (
  tenantId: string,
  store: MealPlanEntryStore = createMealPlanEntryStore(tenantId)
): MealPlanEntryStore => {
  prototypeStoreRegistry.register("menu.meal_plan_entries", store);
  return store;
};

export const useMealPlanEntrySnapshot = () => prototypeStoreRegistry.use("menu.meal_plan_entries");

export const mealPlanEntryFixtures = {
  empty: (): MealPlanEntryPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MealPlanEntryPrototype[] =>
    createCachedMealPlanEntryDummyRecords(tenantId, 8, { seed: "menu.meal_plan_entries:happy" }),
  invalidMissingId: (): MealPlanEntryPrototype[] =>
    createMealPlanEntryDummyFactory({ seed: "menu.meal_plan_entries:invalid" }).list(1, () => ({
      [mealPlanEntrySchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MealPlanEntryPrototype[] =>
    createCachedMealPlanEntryDummyRecords(tenantId, count, { seed: "menu.meal_plan_entries:high-volume" }),
};

export const mealPlanEntryFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanEntryPrototype> => ({
    collection: mealPlanEntrySchema.collection,
    domain: mealPlanEntrySchema.domain,
    name: "menu.meal_plan_entries.empty",
    records: mealPlanEntryFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanEntryPrototype> => ({
    collection: mealPlanEntrySchema.collection,
    domain: mealPlanEntrySchema.domain,
    name: "menu.meal_plan_entries.happyPath",
    records: mealPlanEntryFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanEntryPrototype> => ({
    collection: mealPlanEntrySchema.collection,
    domain: mealPlanEntrySchema.domain,
    name: "menu.meal_plan_entries.invalidMissingId",
    records: mealPlanEntryFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MealPlanEntryPrototype> => ({
    collection: mealPlanEntrySchema.collection,
    domain: mealPlanEntrySchema.domain,
    name: "menu.meal_plan_entries.highVolume",
    records: mealPlanEntryFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const mealPlanEntryBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.meal_plan_entries.dummy.list.1000",
    records: 1000,
    run: () => createCachedMealPlanEntryDummyRecords("tenant-benchmark", 1000, { seed: "menu.meal_plan_entries:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.meal_plan_entries.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanEntryDummyFactory({ seed: "menu.meal_plan_entries:bench-store" }).list(1000);
      const store = createMealPlanEntryStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plan_entries.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanEntryDummyFactory({ seed: "menu.meal_plan_entries:bench-apply" }).list(1000);
      const store = createMealPlanEntryStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[mealPlanEntrySchema.idField ?? "id"] ?? index);
        store.apply({
          collection: mealPlanEntrySchema.collection,
          domain: mealPlanEntrySchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plan_entries.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanEntryDummyFactory({ seed: "menu.meal_plan_entries:bench-reset" }).list(1000);
      const store = createMealPlanEntryStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plan_entries.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanEntryDummyFactory({ seed: "menu.meal_plan_entries:bench-selector" }).list(1000);
      const store = createMealPlanEntryStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[mealPlanEntrySchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type MealPlanPrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.MealPlan. Use it to narrow MealPlanPrototype records. */
export type MealPlanFields = {
  readonly "cadence": string;
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "lock_lead_hours": number;
  readonly "organization_id": string;
  readonly "owner_profile_id": string;
  readonly "plan_kind": string;
  readonly "status": string;
  readonly "title": string;
  readonly "updated_at"?: string | null;
  readonly "week_start": string;
};

export const mealPlanFieldOrder = ["id","organization_id","created_at","updated_at","owner_profile_id","title","week_start","status","cadence","plan_kind","lock_lead_hours"] as const satisfies readonly (keyof MealPlanFields)[];

export const mealPlanRequiredFields = ["cadence","id","lock_lead_hours","organization_id","owner_profile_id","plan_kind","status","title","week_start"] as const satisfies readonly (keyof MealPlanFields)[];

export type MealPlanStore = TenantProjectionStore<MealPlanPrototype>;

export const mealPlanSchema = {
  "collection": "meal_plans",
  "domain": "menu",
  "fields": [
    {
      "kind": "string",
      "name": "cadence"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "int",
      "name": "lock_lead_hours"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "owner_profile_id"
    },
    {
      "kind": "string",
      "name": "plan_kind"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "string",
      "name": "title"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "week_start"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createMealPlanDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<MealPlanPrototype>(mealPlanSchema, options);

export const mealPlanRuntimeConstants = {
  collection: mealPlanSchema.collection,
  domain: mealPlanSchema.domain,
  idField: mealPlanSchema.idField ?? "id",
  key: "menu.meal_plans",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedMealPlanDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly MealPlanPrototype[] =>
  cache.getDummyRecords(mealPlanSchema, { ...options, tenantId }, count, () =>
    createMealPlanDummyFactory({ ...options, tenantId }).list(count)
  );

export const createMealPlanStore = (
  tenantId: string,
  initialRecords: readonly MealPlanPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): MealPlanStore => {
  const scope = {
    collection: mealPlanSchema.collection,
    domain: mealPlanSchema.domain,
    tenantId,
  };
  const store = cache.getStore<MealPlanPrototype>(
    scope,
    () =>
      createTenantProjectionStore<MealPlanPrototype>(
        scope,
        (record) => String(record[mealPlanSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectMealPlanLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<MealPlanPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: MealPlanStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<MealPlanPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createMealPlanStore(tenantId, [], cache);
  return createLiveProjectionBinding<MealPlanPrototype>({
    ...bindingOptions,
    scope: {
      collection: mealPlanSchema.collection,
      domain: mealPlanSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerMealPlanStore = (
  tenantId: string,
  store: MealPlanStore = createMealPlanStore(tenantId)
): MealPlanStore => {
  prototypeStoreRegistry.register("menu.meal_plans", store);
  return store;
};

export const useMealPlanSnapshot = () => prototypeStoreRegistry.use("menu.meal_plans");

export const mealPlanFixtures = {
  empty: (): MealPlanPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly MealPlanPrototype[] =>
    createCachedMealPlanDummyRecords(tenantId, 8, { seed: "menu.meal_plans:happy" }),
  invalidMissingId: (): MealPlanPrototype[] =>
    createMealPlanDummyFactory({ seed: "menu.meal_plans:invalid" }).list(1, () => ({
      [mealPlanSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly MealPlanPrototype[] =>
    createCachedMealPlanDummyRecords(tenantId, count, { seed: "menu.meal_plans:high-volume" }),
};

export const mealPlanFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanPrototype> => ({
    collection: mealPlanSchema.collection,
    domain: mealPlanSchema.domain,
    name: "menu.meal_plans.empty",
    records: mealPlanFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanPrototype> => ({
    collection: mealPlanSchema.collection,
    domain: mealPlanSchema.domain,
    name: "menu.meal_plans.happyPath",
    records: mealPlanFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<MealPlanPrototype> => ({
    collection: mealPlanSchema.collection,
    domain: mealPlanSchema.domain,
    name: "menu.meal_plans.invalidMissingId",
    records: mealPlanFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<MealPlanPrototype> => ({
    collection: mealPlanSchema.collection,
    domain: mealPlanSchema.domain,
    name: "menu.meal_plans.highVolume",
    records: mealPlanFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const mealPlanBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.meal_plans.dummy.list.1000",
    records: 1000,
    run: () => createCachedMealPlanDummyRecords("tenant-benchmark", 1000, { seed: "menu.meal_plans:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.meal_plans.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDummyFactory({ seed: "menu.meal_plans:bench-store" }).list(1000);
      const store = createMealPlanStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plans.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDummyFactory({ seed: "menu.meal_plans:bench-apply" }).list(1000);
      const store = createMealPlanStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[mealPlanSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: mealPlanSchema.collection,
          domain: mealPlanSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plans.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDummyFactory({ seed: "menu.meal_plans:bench-reset" }).list(1000);
      const store = createMealPlanStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.meal_plans.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createMealPlanDummyFactory({ seed: "menu.meal_plans:bench-selector" }).list(1000);
      const store = createMealPlanStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[mealPlanSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type TrendingChefPrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.TrendingChef. Use it to narrow TrendingChefPrototype records. */
export type TrendingChefFields = {
  readonly "chef_profile_id": string;
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "rank": number;
  readonly "score": number;
  readonly "window_days": number;
};

export const trendingChefFieldOrder = ["id","organization_id","created_at","chef_profile_id","score","window_days","rank"] as const satisfies readonly (keyof TrendingChefFields)[];

export const trendingChefRequiredFields = ["chef_profile_id","id","organization_id","rank","score","window_days"] as const satisfies readonly (keyof TrendingChefFields)[];

export type TrendingChefStore = TenantProjectionStore<TrendingChefPrototype>;

export const trendingChefSchema = {
  "collection": "trending_chefs",
  "domain": "menu",
  "fields": [
    {
      "kind": "uuid",
      "name": "chef_profile_id"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "int",
      "name": "rank"
    },
    {
      "kind": "int",
      "name": "score"
    },
    {
      "kind": "int",
      "name": "window_days"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createTrendingChefDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<TrendingChefPrototype>(trendingChefSchema, options);

export const trendingChefRuntimeConstants = {
  collection: trendingChefSchema.collection,
  domain: trendingChefSchema.domain,
  idField: trendingChefSchema.idField ?? "id",
  key: "menu.trending_chefs",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedTrendingChefDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly TrendingChefPrototype[] =>
  cache.getDummyRecords(trendingChefSchema, { ...options, tenantId }, count, () =>
    createTrendingChefDummyFactory({ ...options, tenantId }).list(count)
  );

export const createTrendingChefStore = (
  tenantId: string,
  initialRecords: readonly TrendingChefPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): TrendingChefStore => {
  const scope = {
    collection: trendingChefSchema.collection,
    domain: trendingChefSchema.domain,
    tenantId,
  };
  const store = cache.getStore<TrendingChefPrototype>(
    scope,
    () =>
      createTenantProjectionStore<TrendingChefPrototype>(
        scope,
        (record) => String(record[trendingChefSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectTrendingChefLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<TrendingChefPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: TrendingChefStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<TrendingChefPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createTrendingChefStore(tenantId, [], cache);
  return createLiveProjectionBinding<TrendingChefPrototype>({
    ...bindingOptions,
    scope: {
      collection: trendingChefSchema.collection,
      domain: trendingChefSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerTrendingChefStore = (
  tenantId: string,
  store: TrendingChefStore = createTrendingChefStore(tenantId)
): TrendingChefStore => {
  prototypeStoreRegistry.register("menu.trending_chefs", store);
  return store;
};

export const useTrendingChefSnapshot = () => prototypeStoreRegistry.use("menu.trending_chefs");

export const trendingChefFixtures = {
  empty: (): TrendingChefPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly TrendingChefPrototype[] =>
    createCachedTrendingChefDummyRecords(tenantId, 8, { seed: "menu.trending_chefs:happy" }),
  invalidMissingId: (): TrendingChefPrototype[] =>
    createTrendingChefDummyFactory({ seed: "menu.trending_chefs:invalid" }).list(1, () => ({
      [trendingChefSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly TrendingChefPrototype[] =>
    createCachedTrendingChefDummyRecords(tenantId, count, { seed: "menu.trending_chefs:high-volume" }),
};

export const trendingChefFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<TrendingChefPrototype> => ({
    collection: trendingChefSchema.collection,
    domain: trendingChefSchema.domain,
    name: "menu.trending_chefs.empty",
    records: trendingChefFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<TrendingChefPrototype> => ({
    collection: trendingChefSchema.collection,
    domain: trendingChefSchema.domain,
    name: "menu.trending_chefs.happyPath",
    records: trendingChefFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<TrendingChefPrototype> => ({
    collection: trendingChefSchema.collection,
    domain: trendingChefSchema.domain,
    name: "menu.trending_chefs.invalidMissingId",
    records: trendingChefFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<TrendingChefPrototype> => ({
    collection: trendingChefSchema.collection,
    domain: trendingChefSchema.domain,
    name: "menu.trending_chefs.highVolume",
    records: trendingChefFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const trendingChefBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.trending_chefs.dummy.list.1000",
    records: 1000,
    run: () => createCachedTrendingChefDummyRecords("tenant-benchmark", 1000, { seed: "menu.trending_chefs:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.trending_chefs.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createTrendingChefDummyFactory({ seed: "menu.trending_chefs:bench-store" }).list(1000);
      const store = createTrendingChefStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.trending_chefs.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createTrendingChefDummyFactory({ seed: "menu.trending_chefs:bench-apply" }).list(1000);
      const store = createTrendingChefStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[trendingChefSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: trendingChefSchema.collection,
          domain: trendingChefSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.trending_chefs.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createTrendingChefDummyFactory({ seed: "menu.trending_chefs:bench-reset" }).list(1000);
      const store = createTrendingChefStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.trending_chefs.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createTrendingChefDummyFactory({ seed: "menu.trending_chefs:bench-selector" }).list(1000);
      const store = createTrendingChefStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[trendingChefSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type TrendingDishPrototype = Record<string, unknown>;

/** Typed field shape for menu.v1.TrendingDish. Use it to narrow TrendingDishPrototype records. */
export type TrendingDishFields = {
  readonly "chef_profile_id": string;
  readonly "created_at"?: string | null;
  readonly "dish_id": string;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "rank": number;
  readonly "score": number;
  readonly "window_days": number;
};

export const trendingDishFieldOrder = ["id","organization_id","created_at","dish_id","chef_profile_id","score","window_days","rank"] as const satisfies readonly (keyof TrendingDishFields)[];

export const trendingDishRequiredFields = ["chef_profile_id","dish_id","id","organization_id","rank","score","window_days"] as const satisfies readonly (keyof TrendingDishFields)[];

export type TrendingDishStore = TenantProjectionStore<TrendingDishPrototype>;

export const trendingDishSchema = {
  "collection": "trending_dishes",
  "domain": "menu",
  "fields": [
    {
      "kind": "uuid",
      "name": "chef_profile_id"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "dish_id"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "int",
      "name": "rank"
    },
    {
      "kind": "int",
      "name": "score"
    },
    {
      "kind": "int",
      "name": "window_days"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createTrendingDishDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<TrendingDishPrototype>(trendingDishSchema, options);

export const trendingDishRuntimeConstants = {
  collection: trendingDishSchema.collection,
  domain: trendingDishSchema.domain,
  idField: trendingDishSchema.idField ?? "id",
  key: "menu.trending_dishes",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedTrendingDishDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly TrendingDishPrototype[] =>
  cache.getDummyRecords(trendingDishSchema, { ...options, tenantId }, count, () =>
    createTrendingDishDummyFactory({ ...options, tenantId }).list(count)
  );

export const createTrendingDishStore = (
  tenantId: string,
  initialRecords: readonly TrendingDishPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): TrendingDishStore => {
  const scope = {
    collection: trendingDishSchema.collection,
    domain: trendingDishSchema.domain,
    tenantId,
  };
  const store = cache.getStore<TrendingDishPrototype>(
    scope,
    () =>
      createTenantProjectionStore<TrendingDishPrototype>(
        scope,
        (record) => String(record[trendingDishSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectTrendingDishLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<TrendingDishPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: TrendingDishStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<TrendingDishPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createTrendingDishStore(tenantId, [], cache);
  return createLiveProjectionBinding<TrendingDishPrototype>({
    ...bindingOptions,
    scope: {
      collection: trendingDishSchema.collection,
      domain: trendingDishSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerTrendingDishStore = (
  tenantId: string,
  store: TrendingDishStore = createTrendingDishStore(tenantId)
): TrendingDishStore => {
  prototypeStoreRegistry.register("menu.trending_dishes", store);
  return store;
};

export const useTrendingDishSnapshot = () => prototypeStoreRegistry.use("menu.trending_dishes");

export const trendingDishFixtures = {
  empty: (): TrendingDishPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly TrendingDishPrototype[] =>
    createCachedTrendingDishDummyRecords(tenantId, 8, { seed: "menu.trending_dishes:happy" }),
  invalidMissingId: (): TrendingDishPrototype[] =>
    createTrendingDishDummyFactory({ seed: "menu.trending_dishes:invalid" }).list(1, () => ({
      [trendingDishSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly TrendingDishPrototype[] =>
    createCachedTrendingDishDummyRecords(tenantId, count, { seed: "menu.trending_dishes:high-volume" }),
};

export const trendingDishFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<TrendingDishPrototype> => ({
    collection: trendingDishSchema.collection,
    domain: trendingDishSchema.domain,
    name: "menu.trending_dishes.empty",
    records: trendingDishFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<TrendingDishPrototype> => ({
    collection: trendingDishSchema.collection,
    domain: trendingDishSchema.domain,
    name: "menu.trending_dishes.happyPath",
    records: trendingDishFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<TrendingDishPrototype> => ({
    collection: trendingDishSchema.collection,
    domain: trendingDishSchema.domain,
    name: "menu.trending_dishes.invalidMissingId",
    records: trendingDishFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<TrendingDishPrototype> => ({
    collection: trendingDishSchema.collection,
    domain: trendingDishSchema.domain,
    name: "menu.trending_dishes.highVolume",
    records: trendingDishFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const trendingDishBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "menu.trending_dishes.dummy.list.1000",
    records: 1000,
    run: () => createCachedTrendingDishDummyRecords("tenant-benchmark", 1000, { seed: "menu.trending_dishes:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "menu.trending_dishes.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createTrendingDishDummyFactory({ seed: "menu.trending_dishes:bench-store" }).list(1000);
      const store = createTrendingDishStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.trending_dishes.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createTrendingDishDummyFactory({ seed: "menu.trending_dishes:bench-apply" }).list(1000);
      const store = createTrendingDishStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[trendingDishSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: trendingDishSchema.collection,
          domain: trendingDishSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.trending_dishes.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createTrendingDishDummyFactory({ seed: "menu.trending_dishes:bench-reset" }).list(1000);
      const store = createTrendingDishStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "menu.trending_dishes.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createTrendingDishDummyFactory({ seed: "menu.trending_dishes:bench-selector" }).list(1000);
      const store = createTrendingDishStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[trendingDishSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type OpsConversationPrototype = Record<string, unknown>;

/** Typed field shape for message.v1.OpsConversation. Use it to narrow OpsConversationPrototype records. */
export type OpsConversationFields = {
  readonly "chef_name": string;
  readonly "created_at": string;
  readonly "customer_name": string;
  readonly "id": string;
  readonly "merchant_profile_id": string;
  readonly "order_id": string;
  readonly "status": string;
  readonly "subject": string;
};

export const opsConversationFieldOrder = ["id","order_id","subject","status","created_at","customer_name","chef_name","merchant_profile_id"] as const satisfies readonly (keyof OpsConversationFields)[];

export const opsConversationRequiredFields = ["chef_name","created_at","customer_name","id","merchant_profile_id","order_id","status","subject"] as const satisfies readonly (keyof OpsConversationFields)[];

export type OpsConversationStore = TenantProjectionStore<OpsConversationPrototype>;

export const opsConversationSchema = {
  "collection": "ops_conversations",
  "domain": "message",
  "fields": [
    {
      "kind": "string",
      "name": "chef_name"
    },
    {
      "kind": "timestamp",
      "name": "created_at"
    },
    {
      "kind": "string",
      "name": "customer_name"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "merchant_profile_id"
    },
    {
      "kind": "uuid",
      "name": "order_id"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "string",
      "name": "subject"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createOpsConversationDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<OpsConversationPrototype>(opsConversationSchema, options);

export const opsConversationRuntimeConstants = {
  collection: opsConversationSchema.collection,
  domain: opsConversationSchema.domain,
  idField: opsConversationSchema.idField ?? "id",
  key: "message.ops_conversations",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedOpsConversationDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly OpsConversationPrototype[] =>
  cache.getDummyRecords(opsConversationSchema, { ...options, tenantId }, count, () =>
    createOpsConversationDummyFactory({ ...options, tenantId }).list(count)
  );

export const createOpsConversationStore = (
  tenantId: string,
  initialRecords: readonly OpsConversationPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): OpsConversationStore => {
  const scope = {
    collection: opsConversationSchema.collection,
    domain: opsConversationSchema.domain,
    tenantId,
  };
  const store = cache.getStore<OpsConversationPrototype>(
    scope,
    () =>
      createTenantProjectionStore<OpsConversationPrototype>(
        scope,
        (record) => String(record[opsConversationSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectOpsConversationLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<OpsConversationPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: OpsConversationStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<OpsConversationPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createOpsConversationStore(tenantId, [], cache);
  return createLiveProjectionBinding<OpsConversationPrototype>({
    ...bindingOptions,
    scope: {
      collection: opsConversationSchema.collection,
      domain: opsConversationSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerOpsConversationStore = (
  tenantId: string,
  store: OpsConversationStore = createOpsConversationStore(tenantId)
): OpsConversationStore => {
  prototypeStoreRegistry.register("message.ops_conversations", store);
  return store;
};

export const useOpsConversationSnapshot = () => prototypeStoreRegistry.use("message.ops_conversations");

export const opsConversationFixtures = {
  empty: (): OpsConversationPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly OpsConversationPrototype[] =>
    createCachedOpsConversationDummyRecords(tenantId, 8, { seed: "message.ops_conversations:happy" }),
  invalidMissingId: (): OpsConversationPrototype[] =>
    createOpsConversationDummyFactory({ seed: "message.ops_conversations:invalid" }).list(1, () => ({
      [opsConversationSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly OpsConversationPrototype[] =>
    createCachedOpsConversationDummyRecords(tenantId, count, { seed: "message.ops_conversations:high-volume" }),
};

export const opsConversationFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<OpsConversationPrototype> => ({
    collection: opsConversationSchema.collection,
    domain: opsConversationSchema.domain,
    name: "message.ops_conversations.empty",
    records: opsConversationFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<OpsConversationPrototype> => ({
    collection: opsConversationSchema.collection,
    domain: opsConversationSchema.domain,
    name: "message.ops_conversations.happyPath",
    records: opsConversationFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<OpsConversationPrototype> => ({
    collection: opsConversationSchema.collection,
    domain: opsConversationSchema.domain,
    name: "message.ops_conversations.invalidMissingId",
    records: opsConversationFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<OpsConversationPrototype> => ({
    collection: opsConversationSchema.collection,
    domain: opsConversationSchema.domain,
    name: "message.ops_conversations.highVolume",
    records: opsConversationFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const opsConversationBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "message.ops_conversations.dummy.list.1000",
    records: 1000,
    run: () => createCachedOpsConversationDummyRecords("tenant-benchmark", 1000, { seed: "message.ops_conversations:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "message.ops_conversations.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createOpsConversationDummyFactory({ seed: "message.ops_conversations:bench-store" }).list(1000);
      const store = createOpsConversationStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.ops_conversations.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createOpsConversationDummyFactory({ seed: "message.ops_conversations:bench-apply" }).list(1000);
      const store = createOpsConversationStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[opsConversationSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: opsConversationSchema.collection,
          domain: opsConversationSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.ops_conversations.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createOpsConversationDummyFactory({ seed: "message.ops_conversations:bench-reset" }).list(1000);
      const store = createOpsConversationStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.ops_conversations.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createOpsConversationDummyFactory({ seed: "message.ops_conversations:bench-selector" }).list(1000);
      const store = createOpsConversationStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[opsConversationSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type OrderMessagePrototype = Record<string, unknown>;

/** Typed field shape for message.v1.OrderMessage. Use it to narrow OrderMessagePrototype records. */
export type OrderMessageFields = {
  readonly "body": string;
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "order_id": string;
  readonly "organization_id": string;
  readonly "sender_class": string;
  readonly "sender_profile_id": string;
  readonly "thread_id": string;
};

export const orderMessageFieldOrder = ["id","organization_id","thread_id","order_id","sender_profile_id","sender_class","body","created_at"] as const satisfies readonly (keyof OrderMessageFields)[];

export const orderMessageRequiredFields = ["body","id","order_id","organization_id","sender_class","sender_profile_id","thread_id"] as const satisfies readonly (keyof OrderMessageFields)[];

export type OrderMessageStore = TenantProjectionStore<OrderMessagePrototype>;

export const orderMessageSchema = {
  "collection": "order_messages",
  "domain": "message",
  "fields": [
    {
      "kind": "text",
      "name": "body"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "order_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "sender_class"
    },
    {
      "kind": "uuid",
      "name": "sender_profile_id"
    },
    {
      "kind": "uuid",
      "name": "thread_id"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createOrderMessageDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<OrderMessagePrototype>(orderMessageSchema, options);

export const orderMessageRuntimeConstants = {
  collection: orderMessageSchema.collection,
  domain: orderMessageSchema.domain,
  idField: orderMessageSchema.idField ?? "id",
  key: "message.order_messages",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedOrderMessageDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly OrderMessagePrototype[] =>
  cache.getDummyRecords(orderMessageSchema, { ...options, tenantId }, count, () =>
    createOrderMessageDummyFactory({ ...options, tenantId }).list(count)
  );

export const createOrderMessageStore = (
  tenantId: string,
  initialRecords: readonly OrderMessagePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): OrderMessageStore => {
  const scope = {
    collection: orderMessageSchema.collection,
    domain: orderMessageSchema.domain,
    tenantId,
  };
  const store = cache.getStore<OrderMessagePrototype>(
    scope,
    () =>
      createTenantProjectionStore<OrderMessagePrototype>(
        scope,
        (record) => String(record[orderMessageSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectOrderMessageLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<OrderMessagePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: OrderMessageStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<OrderMessagePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createOrderMessageStore(tenantId, [], cache);
  return createLiveProjectionBinding<OrderMessagePrototype>({
    ...bindingOptions,
    scope: {
      collection: orderMessageSchema.collection,
      domain: orderMessageSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerOrderMessageStore = (
  tenantId: string,
  store: OrderMessageStore = createOrderMessageStore(tenantId)
): OrderMessageStore => {
  prototypeStoreRegistry.register("message.order_messages", store);
  return store;
};

export const useOrderMessageSnapshot = () => prototypeStoreRegistry.use("message.order_messages");

export const orderMessageFixtures = {
  empty: (): OrderMessagePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly OrderMessagePrototype[] =>
    createCachedOrderMessageDummyRecords(tenantId, 8, { seed: "message.order_messages:happy" }),
  invalidMissingId: (): OrderMessagePrototype[] =>
    createOrderMessageDummyFactory({ seed: "message.order_messages:invalid" }).list(1, () => ({
      [orderMessageSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly OrderMessagePrototype[] =>
    createCachedOrderMessageDummyRecords(tenantId, count, { seed: "message.order_messages:high-volume" }),
};

export const orderMessageFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderMessagePrototype> => ({
    collection: orderMessageSchema.collection,
    domain: orderMessageSchema.domain,
    name: "message.order_messages.empty",
    records: orderMessageFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderMessagePrototype> => ({
    collection: orderMessageSchema.collection,
    domain: orderMessageSchema.domain,
    name: "message.order_messages.happyPath",
    records: orderMessageFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderMessagePrototype> => ({
    collection: orderMessageSchema.collection,
    domain: orderMessageSchema.domain,
    name: "message.order_messages.invalidMissingId",
    records: orderMessageFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<OrderMessagePrototype> => ({
    collection: orderMessageSchema.collection,
    domain: orderMessageSchema.domain,
    name: "message.order_messages.highVolume",
    records: orderMessageFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const orderMessageBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "message.order_messages.dummy.list.1000",
    records: 1000,
    run: () => createCachedOrderMessageDummyRecords("tenant-benchmark", 1000, { seed: "message.order_messages:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "message.order_messages.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createOrderMessageDummyFactory({ seed: "message.order_messages:bench-store" }).list(1000);
      const store = createOrderMessageStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.order_messages.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createOrderMessageDummyFactory({ seed: "message.order_messages:bench-apply" }).list(1000);
      const store = createOrderMessageStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[orderMessageSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: orderMessageSchema.collection,
          domain: orderMessageSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.order_messages.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createOrderMessageDummyFactory({ seed: "message.order_messages:bench-reset" }).list(1000);
      const store = createOrderMessageStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.order_messages.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createOrderMessageDummyFactory({ seed: "message.order_messages:bench-selector" }).list(1000);
      const store = createOrderMessageStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[orderMessageSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type OrderThreadPrototype = Record<string, unknown>;

/** Typed field shape for message.v1.OrderThread. Use it to narrow OrderThreadPrototype records. */
export type OrderThreadFields = {
  readonly "created_at"?: string | null;
  readonly "delivery_id": string;
  readonly "id": string;
  readonly "order_id": string;
  readonly "organization_id": string;
  readonly "status": string;
  readonly "subject": string;
};

export const orderThreadFieldOrder = ["id","organization_id","order_id","delivery_id","status","subject","created_at"] as const satisfies readonly (keyof OrderThreadFields)[];

export const orderThreadRequiredFields = ["delivery_id","id","order_id","organization_id","status","subject"] as const satisfies readonly (keyof OrderThreadFields)[];

export type OrderThreadStore = TenantProjectionStore<OrderThreadPrototype>;

export const orderThreadSchema = {
  "collection": "order_threads",
  "domain": "message",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "delivery_id"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "order_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "kind": "string",
      "name": "subject"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createOrderThreadDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<OrderThreadPrototype>(orderThreadSchema, options);

export const orderThreadRuntimeConstants = {
  collection: orderThreadSchema.collection,
  domain: orderThreadSchema.domain,
  idField: orderThreadSchema.idField ?? "id",
  key: "message.order_threads",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedOrderThreadDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly OrderThreadPrototype[] =>
  cache.getDummyRecords(orderThreadSchema, { ...options, tenantId }, count, () =>
    createOrderThreadDummyFactory({ ...options, tenantId }).list(count)
  );

export const createOrderThreadStore = (
  tenantId: string,
  initialRecords: readonly OrderThreadPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): OrderThreadStore => {
  const scope = {
    collection: orderThreadSchema.collection,
    domain: orderThreadSchema.domain,
    tenantId,
  };
  const store = cache.getStore<OrderThreadPrototype>(
    scope,
    () =>
      createTenantProjectionStore<OrderThreadPrototype>(
        scope,
        (record) => String(record[orderThreadSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectOrderThreadLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<OrderThreadPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: OrderThreadStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<OrderThreadPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createOrderThreadStore(tenantId, [], cache);
  return createLiveProjectionBinding<OrderThreadPrototype>({
    ...bindingOptions,
    scope: {
      collection: orderThreadSchema.collection,
      domain: orderThreadSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerOrderThreadStore = (
  tenantId: string,
  store: OrderThreadStore = createOrderThreadStore(tenantId)
): OrderThreadStore => {
  prototypeStoreRegistry.register("message.order_threads", store);
  return store;
};

export const useOrderThreadSnapshot = () => prototypeStoreRegistry.use("message.order_threads");

export const orderThreadFixtures = {
  empty: (): OrderThreadPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly OrderThreadPrototype[] =>
    createCachedOrderThreadDummyRecords(tenantId, 8, { seed: "message.order_threads:happy" }),
  invalidMissingId: (): OrderThreadPrototype[] =>
    createOrderThreadDummyFactory({ seed: "message.order_threads:invalid" }).list(1, () => ({
      [orderThreadSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly OrderThreadPrototype[] =>
    createCachedOrderThreadDummyRecords(tenantId, count, { seed: "message.order_threads:high-volume" }),
};

export const orderThreadFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderThreadPrototype> => ({
    collection: orderThreadSchema.collection,
    domain: orderThreadSchema.domain,
    name: "message.order_threads.empty",
    records: orderThreadFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderThreadPrototype> => ({
    collection: orderThreadSchema.collection,
    domain: orderThreadSchema.domain,
    name: "message.order_threads.happyPath",
    records: orderThreadFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<OrderThreadPrototype> => ({
    collection: orderThreadSchema.collection,
    domain: orderThreadSchema.domain,
    name: "message.order_threads.invalidMissingId",
    records: orderThreadFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<OrderThreadPrototype> => ({
    collection: orderThreadSchema.collection,
    domain: orderThreadSchema.domain,
    name: "message.order_threads.highVolume",
    records: orderThreadFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const orderThreadBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "message.order_threads.dummy.list.1000",
    records: 1000,
    run: () => createCachedOrderThreadDummyRecords("tenant-benchmark", 1000, { seed: "message.order_threads:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "message.order_threads.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createOrderThreadDummyFactory({ seed: "message.order_threads:bench-store" }).list(1000);
      const store = createOrderThreadStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.order_threads.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createOrderThreadDummyFactory({ seed: "message.order_threads:bench-apply" }).list(1000);
      const store = createOrderThreadStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[orderThreadSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: orderThreadSchema.collection,
          domain: orderThreadSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.order_threads.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createOrderThreadDummyFactory({ seed: "message.order_threads:bench-reset" }).list(1000);
      const store = createOrderThreadStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "message.order_threads.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createOrderThreadDummyFactory({ seed: "message.order_threads:bench-selector" }).list(1000);
      const store = createOrderThreadStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[orderThreadSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type OfferPrototype = Record<string, unknown>;

/** Typed field shape for offer.v1.Offer. Use it to narrow OfferPrototype records. */
export type OfferFields = {
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "delivery_id": string;
  readonly "eta_minutes": number;
  readonly "expires_at"?: string | null;
  readonly "id": string;
  readonly "order_type": string;
  readonly "organization_id": string;
  readonly "pay_total_minor": number;
  readonly "peak_pay": boolean;
  readonly "pickup_distance_km": number;
  readonly "pricing"?: Record<string, unknown> | null;
  readonly "restaurant_name": string;
  readonly "scheduled_at"?: string | null;
};

export const offerFieldOrder = ["id","organization_id","created_at","delivery_id","restaurant_name","order_type","pickup_distance_km","eta_minutes","pay_total_minor","currency","peak_pay","expires_at","pricing","scheduled_at"] as const satisfies readonly (keyof OfferFields)[];

export const offerRequiredFields = ["currency","delivery_id","eta_minutes","id","order_type","organization_id","pay_total_minor","peak_pay","pickup_distance_km","restaurant_name"] as const satisfies readonly (keyof OfferFields)[];

export type OfferStore = TenantProjectionStore<OfferPrototype>;

export const offerSchema = {
  "collection": "offers",
  "domain": "offer",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "delivery_id"
    },
    {
      "kind": "int",
      "name": "eta_minutes"
    },
    {
      "kind": "timestamp",
      "name": "expires_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "string",
      "name": "order_type"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "int",
      "name": "pay_total_minor"
    },
    {
      "kind": "bool",
      "name": "peak_pay"
    },
    {
      "kind": "float",
      "name": "pickup_distance_km"
    },
    {
      "kind": "object",
      "name": "pricing",
      "optional": true
    },
    {
      "kind": "string",
      "name": "restaurant_name"
    },
    {
      "kind": "timestamp",
      "name": "scheduled_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createOfferDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<OfferPrototype>(offerSchema, options);

export const offerRuntimeConstants = {
  collection: offerSchema.collection,
  domain: offerSchema.domain,
  idField: offerSchema.idField ?? "id",
  key: "offer.offers",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedOfferDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly OfferPrototype[] =>
  cache.getDummyRecords(offerSchema, { ...options, tenantId }, count, () =>
    createOfferDummyFactory({ ...options, tenantId }).list(count)
  );

export const createOfferStore = (
  tenantId: string,
  initialRecords: readonly OfferPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): OfferStore => {
  const scope = {
    collection: offerSchema.collection,
    domain: offerSchema.domain,
    tenantId,
  };
  const store = cache.getStore<OfferPrototype>(
    scope,
    () =>
      createTenantProjectionStore<OfferPrototype>(
        scope,
        (record) => String(record[offerSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectOfferLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<OfferPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: OfferStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<OfferPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createOfferStore(tenantId, [], cache);
  return createLiveProjectionBinding<OfferPrototype>({
    ...bindingOptions,
    scope: {
      collection: offerSchema.collection,
      domain: offerSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerOfferStore = (
  tenantId: string,
  store: OfferStore = createOfferStore(tenantId)
): OfferStore => {
  prototypeStoreRegistry.register("offer.offers", store);
  return store;
};

export const useOfferSnapshot = () => prototypeStoreRegistry.use("offer.offers");

export const offerFixtures = {
  empty: (): OfferPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly OfferPrototype[] =>
    createCachedOfferDummyRecords(tenantId, 8, { seed: "offer.offers:happy" }),
  invalidMissingId: (): OfferPrototype[] =>
    createOfferDummyFactory({ seed: "offer.offers:invalid" }).list(1, () => ({
      [offerSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly OfferPrototype[] =>
    createCachedOfferDummyRecords(tenantId, count, { seed: "offer.offers:high-volume" }),
};

export const offerFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<OfferPrototype> => ({
    collection: offerSchema.collection,
    domain: offerSchema.domain,
    name: "offer.offers.empty",
    records: offerFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<OfferPrototype> => ({
    collection: offerSchema.collection,
    domain: offerSchema.domain,
    name: "offer.offers.happyPath",
    records: offerFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<OfferPrototype> => ({
    collection: offerSchema.collection,
    domain: offerSchema.domain,
    name: "offer.offers.invalidMissingId",
    records: offerFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<OfferPrototype> => ({
    collection: offerSchema.collection,
    domain: offerSchema.domain,
    name: "offer.offers.highVolume",
    records: offerFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const offerBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "offer.offers.dummy.list.1000",
    records: 1000,
    run: () => createCachedOfferDummyRecords("tenant-benchmark", 1000, { seed: "offer.offers:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "offer.offers.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createOfferDummyFactory({ seed: "offer.offers:bench-store" }).list(1000);
      const store = createOfferStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "offer.offers.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createOfferDummyFactory({ seed: "offer.offers:bench-apply" }).list(1000);
      const store = createOfferStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[offerSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: offerSchema.collection,
          domain: offerSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "offer.offers.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createOfferDummyFactory({ seed: "offer.offers:bench-reset" }).list(1000);
      const store = createOfferStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "offer.offers.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createOfferDummyFactory({ seed: "offer.offers:bench-selector" }).list(1000);
      const store = createOfferStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[offerSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type ChowProfilePrototype = Record<string, unknown>;

/** Typed field shape for profile.v1.ChowProfile. Use it to narrow ChowProfilePrototype records. */
export type ChowProfileFields = {
  readonly "allergies": readonly (string)[];
  readonly "avatar_url": string;
  readonly "city": string;
  readonly "classes": readonly (Record<string, unknown>)[];
  readonly "created_at"?: string | null;
  readonly "default_currency": string;
  readonly "display_name": string;
  readonly "email": string;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "phone": string;
  readonly "primary_app": "CHOW_APP_CHOOSECHOW" | "CHOW_APP_CHOWDASH" | "CHOW_APP_SHARED";
  readonly "status": "PROFILE_STATUS_PENDING" | "PROFILE_STATUS_ACTIVE" | "PROFILE_STATUS_SUSPENDED" | "PROFILE_STATUS_DELETED";
  readonly "updated_at"?: string | null;
};

export const chowProfileFieldOrder = ["id","organization_id","created_at","updated_at","display_name","phone","email","city","avatar_url","primary_app","status","classes","default_currency","allergies"] as const satisfies readonly (keyof ChowProfileFields)[];

export const chowProfileRequiredFields = ["allergies","avatar_url","city","classes","default_currency","display_name","email","id","organization_id","phone","primary_app","status"] as const satisfies readonly (keyof ChowProfileFields)[];

export type ChowProfileStore = TenantProjectionStore<ChowProfilePrototype>;

export const chowProfileSchema = {
  "collection": "chow_profiles",
  "domain": "profile",
  "fields": [
    {
      "kind": "string",
      "name": "allergies",
      "repeated": true
    },
    {
      "kind": "url",
      "name": "avatar_url"
    },
    {
      "kind": "string",
      "name": "city"
    },
    {
      "kind": "object",
      "name": "classes",
      "repeated": true
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "default_currency"
    },
    {
      "kind": "string",
      "name": "display_name"
    },
    {
      "kind": "email",
      "name": "email"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "phone"
    },
    {
      "enumValues": [
        "CHOW_APP_CHOOSECHOW",
        "CHOW_APP_CHOWDASH",
        "CHOW_APP_SHARED"
      ],
      "kind": "enum",
      "name": "primary_app"
    },
    {
      "enumValues": [
        "PROFILE_STATUS_PENDING",
        "PROFILE_STATUS_ACTIVE",
        "PROFILE_STATUS_SUSPENDED",
        "PROFILE_STATUS_DELETED"
      ],
      "kind": "enum",
      "name": "status"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createChowProfileDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<ChowProfilePrototype>(chowProfileSchema, options);

export const chowProfileRuntimeConstants = {
  collection: chowProfileSchema.collection,
  domain: chowProfileSchema.domain,
  idField: chowProfileSchema.idField ?? "id",
  key: "profile.chow_profiles",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedChowProfileDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly ChowProfilePrototype[] =>
  cache.getDummyRecords(chowProfileSchema, { ...options, tenantId }, count, () =>
    createChowProfileDummyFactory({ ...options, tenantId }).list(count)
  );

export const createChowProfileStore = (
  tenantId: string,
  initialRecords: readonly ChowProfilePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): ChowProfileStore => {
  const scope = {
    collection: chowProfileSchema.collection,
    domain: chowProfileSchema.domain,
    tenantId,
  };
  const store = cache.getStore<ChowProfilePrototype>(
    scope,
    () =>
      createTenantProjectionStore<ChowProfilePrototype>(
        scope,
        (record) => String(record[chowProfileSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectChowProfileLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<ChowProfilePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: ChowProfileStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<ChowProfilePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createChowProfileStore(tenantId, [], cache);
  return createLiveProjectionBinding<ChowProfilePrototype>({
    ...bindingOptions,
    scope: {
      collection: chowProfileSchema.collection,
      domain: chowProfileSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerChowProfileStore = (
  tenantId: string,
  store: ChowProfileStore = createChowProfileStore(tenantId)
): ChowProfileStore => {
  prototypeStoreRegistry.register("profile.chow_profiles", store);
  return store;
};

export const useChowProfileSnapshot = () => prototypeStoreRegistry.use("profile.chow_profiles");

export const chowProfileFixtures = {
  empty: (): ChowProfilePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly ChowProfilePrototype[] =>
    createCachedChowProfileDummyRecords(tenantId, 8, { seed: "profile.chow_profiles:happy" }),
  invalidMissingId: (): ChowProfilePrototype[] =>
    createChowProfileDummyFactory({ seed: "profile.chow_profiles:invalid" }).list(1, () => ({
      [chowProfileSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly ChowProfilePrototype[] =>
    createCachedChowProfileDummyRecords(tenantId, count, { seed: "profile.chow_profiles:high-volume" }),
};

export const chowProfileFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<ChowProfilePrototype> => ({
    collection: chowProfileSchema.collection,
    domain: chowProfileSchema.domain,
    name: "profile.chow_profiles.empty",
    records: chowProfileFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<ChowProfilePrototype> => ({
    collection: chowProfileSchema.collection,
    domain: chowProfileSchema.domain,
    name: "profile.chow_profiles.happyPath",
    records: chowProfileFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<ChowProfilePrototype> => ({
    collection: chowProfileSchema.collection,
    domain: chowProfileSchema.domain,
    name: "profile.chow_profiles.invalidMissingId",
    records: chowProfileFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<ChowProfilePrototype> => ({
    collection: chowProfileSchema.collection,
    domain: chowProfileSchema.domain,
    name: "profile.chow_profiles.highVolume",
    records: chowProfileFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const chowProfileBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "profile.chow_profiles.dummy.list.1000",
    records: 1000,
    run: () => createCachedChowProfileDummyRecords("tenant-benchmark", 1000, { seed: "profile.chow_profiles:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "profile.chow_profiles.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createChowProfileDummyFactory({ seed: "profile.chow_profiles:bench-store" }).list(1000);
      const store = createChowProfileStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.chow_profiles.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createChowProfileDummyFactory({ seed: "profile.chow_profiles:bench-apply" }).list(1000);
      const store = createChowProfileStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[chowProfileSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: chowProfileSchema.collection,
          domain: chowProfileSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.chow_profiles.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createChowProfileDummyFactory({ seed: "profile.chow_profiles:bench-reset" }).list(1000);
      const store = createChowProfileStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.chow_profiles.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createChowProfileDummyFactory({ seed: "profile.chow_profiles:bench-selector" }).list(1000);
      const store = createChowProfileStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[chowProfileSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type ProfileLinkPrototype = Record<string, unknown>;

/** Typed field shape for profile.v1.ProfileLink. Use it to narrow ProfileLinkPrototype records. */
export type ProfileLinkFields = {
  readonly "linked_entity_id": string;
  readonly "linked_entity_type": string;
  readonly "profile_id": string;
  readonly "relationship": string;
  readonly "source_app": "CHOW_APP_CHOOSECHOW" | "CHOW_APP_CHOWDASH" | "CHOW_APP_SHARED";
};

export const profileLinkFieldOrder = ["profile_id","linked_entity_type","linked_entity_id","relationship","source_app"] as const satisfies readonly (keyof ProfileLinkFields)[];

export const profileLinkRequiredFields = ["linked_entity_id","linked_entity_type","profile_id","relationship","source_app"] as const satisfies readonly (keyof ProfileLinkFields)[];

export type ProfileLinkStore = TenantProjectionStore<ProfileLinkPrototype>;

export const profileLinkSchema = {
  "collection": "profile_links",
  "domain": "profile",
  "fields": [
    {
      "kind": "uuid",
      "name": "linked_entity_id"
    },
    {
      "kind": "url",
      "name": "linked_entity_type"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    },
    {
      "kind": "string",
      "name": "relationship"
    },
    {
      "enumValues": [
        "CHOW_APP_CHOOSECHOW",
        "CHOW_APP_CHOWDASH",
        "CHOW_APP_SHARED"
      ],
      "kind": "enum",
      "name": "source_app"
    }
  ],
  "idField": "profile_id"
} as const satisfies DummySchemaSpec;

export const createProfileLinkDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<ProfileLinkPrototype>(profileLinkSchema, options);

export const profileLinkRuntimeConstants = {
  collection: profileLinkSchema.collection,
  domain: profileLinkSchema.domain,
  idField: profileLinkSchema.idField ?? "id",
  key: "profile.profile_links",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedProfileLinkDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly ProfileLinkPrototype[] =>
  cache.getDummyRecords(profileLinkSchema, { ...options, tenantId }, count, () =>
    createProfileLinkDummyFactory({ ...options, tenantId }).list(count)
  );

export const createProfileLinkStore = (
  tenantId: string,
  initialRecords: readonly ProfileLinkPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): ProfileLinkStore => {
  const scope = {
    collection: profileLinkSchema.collection,
    domain: profileLinkSchema.domain,
    tenantId,
  };
  const store = cache.getStore<ProfileLinkPrototype>(
    scope,
    () =>
      createTenantProjectionStore<ProfileLinkPrototype>(
        scope,
        (record) => String(record[profileLinkSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectProfileLinkLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<ProfileLinkPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: ProfileLinkStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<ProfileLinkPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createProfileLinkStore(tenantId, [], cache);
  return createLiveProjectionBinding<ProfileLinkPrototype>({
    ...bindingOptions,
    scope: {
      collection: profileLinkSchema.collection,
      domain: profileLinkSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerProfileLinkStore = (
  tenantId: string,
  store: ProfileLinkStore = createProfileLinkStore(tenantId)
): ProfileLinkStore => {
  prototypeStoreRegistry.register("profile.profile_links", store);
  return store;
};

export const useProfileLinkSnapshot = () => prototypeStoreRegistry.use("profile.profile_links");

export const profileLinkFixtures = {
  empty: (): ProfileLinkPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly ProfileLinkPrototype[] =>
    createCachedProfileLinkDummyRecords(tenantId, 8, { seed: "profile.profile_links:happy" }),
  invalidMissingId: (): ProfileLinkPrototype[] =>
    createProfileLinkDummyFactory({ seed: "profile.profile_links:invalid" }).list(1, () => ({
      [profileLinkSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly ProfileLinkPrototype[] =>
    createCachedProfileLinkDummyRecords(tenantId, count, { seed: "profile.profile_links:high-volume" }),
};

export const profileLinkFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<ProfileLinkPrototype> => ({
    collection: profileLinkSchema.collection,
    domain: profileLinkSchema.domain,
    name: "profile.profile_links.empty",
    records: profileLinkFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<ProfileLinkPrototype> => ({
    collection: profileLinkSchema.collection,
    domain: profileLinkSchema.domain,
    name: "profile.profile_links.happyPath",
    records: profileLinkFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<ProfileLinkPrototype> => ({
    collection: profileLinkSchema.collection,
    domain: profileLinkSchema.domain,
    name: "profile.profile_links.invalidMissingId",
    records: profileLinkFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<ProfileLinkPrototype> => ({
    collection: profileLinkSchema.collection,
    domain: profileLinkSchema.domain,
    name: "profile.profile_links.highVolume",
    records: profileLinkFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const profileLinkBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "profile.profile_links.dummy.list.1000",
    records: 1000,
    run: () => createCachedProfileLinkDummyRecords("tenant-benchmark", 1000, { seed: "profile.profile_links:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "profile.profile_links.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createProfileLinkDummyFactory({ seed: "profile.profile_links:bench-store" }).list(1000);
      const store = createProfileLinkStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.profile_links.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createProfileLinkDummyFactory({ seed: "profile.profile_links:bench-apply" }).list(1000);
      const store = createProfileLinkStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[profileLinkSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: profileLinkSchema.collection,
          domain: profileLinkSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.profile_links.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createProfileLinkDummyFactory({ seed: "profile.profile_links:bench-reset" }).list(1000);
      const store = createProfileLinkStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.profile_links.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createProfileLinkDummyFactory({ seed: "profile.profile_links:bench-selector" }).list(1000);
      const store = createProfileLinkStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[profileLinkSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type VendorLocationPrototype = Record<string, unknown>;

/** Typed field shape for profile.v1.VendorLocation. Use it to narrow VendorLocationPrototype records. */
export type VendorLocationFields = {
  readonly "address": string;
  readonly "area": string;
  readonly "city": string;
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "is_primary": boolean;
  readonly "label": string;
  readonly "latitude": number;
  readonly "longitude": number;
  readonly "organization_id": string;
  readonly "profile_id": string;
  readonly "updated_at"?: string | null;
};

export const vendorLocationFieldOrder = ["id","organization_id","created_at","updated_at","profile_id","label","address","city","area","latitude","longitude","is_primary"] as const satisfies readonly (keyof VendorLocationFields)[];

export const vendorLocationRequiredFields = ["address","area","city","id","is_primary","label","latitude","longitude","organization_id","profile_id"] as const satisfies readonly (keyof VendorLocationFields)[];

export type VendorLocationStore = TenantProjectionStore<VendorLocationPrototype>;

export const vendorLocationSchema = {
  "collection": "vendor_locations",
  "domain": "profile",
  "fields": [
    {
      "kind": "string",
      "name": "address"
    },
    {
      "kind": "string",
      "name": "area"
    },
    {
      "kind": "string",
      "name": "city"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "bool",
      "name": "is_primary"
    },
    {
      "kind": "string",
      "name": "label"
    },
    {
      "kind": "float",
      "name": "latitude"
    },
    {
      "kind": "float",
      "name": "longitude"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createVendorLocationDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<VendorLocationPrototype>(vendorLocationSchema, options);

export const vendorLocationRuntimeConstants = {
  collection: vendorLocationSchema.collection,
  domain: vendorLocationSchema.domain,
  idField: vendorLocationSchema.idField ?? "id",
  key: "profile.vendor_locations",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedVendorLocationDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly VendorLocationPrototype[] =>
  cache.getDummyRecords(vendorLocationSchema, { ...options, tenantId }, count, () =>
    createVendorLocationDummyFactory({ ...options, tenantId }).list(count)
  );

export const createVendorLocationStore = (
  tenantId: string,
  initialRecords: readonly VendorLocationPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): VendorLocationStore => {
  const scope = {
    collection: vendorLocationSchema.collection,
    domain: vendorLocationSchema.domain,
    tenantId,
  };
  const store = cache.getStore<VendorLocationPrototype>(
    scope,
    () =>
      createTenantProjectionStore<VendorLocationPrototype>(
        scope,
        (record) => String(record[vendorLocationSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectVendorLocationLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<VendorLocationPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: VendorLocationStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<VendorLocationPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createVendorLocationStore(tenantId, [], cache);
  return createLiveProjectionBinding<VendorLocationPrototype>({
    ...bindingOptions,
    scope: {
      collection: vendorLocationSchema.collection,
      domain: vendorLocationSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerVendorLocationStore = (
  tenantId: string,
  store: VendorLocationStore = createVendorLocationStore(tenantId)
): VendorLocationStore => {
  prototypeStoreRegistry.register("profile.vendor_locations", store);
  return store;
};

export const useVendorLocationSnapshot = () => prototypeStoreRegistry.use("profile.vendor_locations");

export const vendorLocationFixtures = {
  empty: (): VendorLocationPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly VendorLocationPrototype[] =>
    createCachedVendorLocationDummyRecords(tenantId, 8, { seed: "profile.vendor_locations:happy" }),
  invalidMissingId: (): VendorLocationPrototype[] =>
    createVendorLocationDummyFactory({ seed: "profile.vendor_locations:invalid" }).list(1, () => ({
      [vendorLocationSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly VendorLocationPrototype[] =>
    createCachedVendorLocationDummyRecords(tenantId, count, { seed: "profile.vendor_locations:high-volume" }),
};

export const vendorLocationFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<VendorLocationPrototype> => ({
    collection: vendorLocationSchema.collection,
    domain: vendorLocationSchema.domain,
    name: "profile.vendor_locations.empty",
    records: vendorLocationFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<VendorLocationPrototype> => ({
    collection: vendorLocationSchema.collection,
    domain: vendorLocationSchema.domain,
    name: "profile.vendor_locations.happyPath",
    records: vendorLocationFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<VendorLocationPrototype> => ({
    collection: vendorLocationSchema.collection,
    domain: vendorLocationSchema.domain,
    name: "profile.vendor_locations.invalidMissingId",
    records: vendorLocationFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<VendorLocationPrototype> => ({
    collection: vendorLocationSchema.collection,
    domain: vendorLocationSchema.domain,
    name: "profile.vendor_locations.highVolume",
    records: vendorLocationFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const vendorLocationBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "profile.vendor_locations.dummy.list.1000",
    records: 1000,
    run: () => createCachedVendorLocationDummyRecords("tenant-benchmark", 1000, { seed: "profile.vendor_locations:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "profile.vendor_locations.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createVendorLocationDummyFactory({ seed: "profile.vendor_locations:bench-store" }).list(1000);
      const store = createVendorLocationStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.vendor_locations.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createVendorLocationDummyFactory({ seed: "profile.vendor_locations:bench-apply" }).list(1000);
      const store = createVendorLocationStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[vendorLocationSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: vendorLocationSchema.collection,
          domain: vendorLocationSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.vendor_locations.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createVendorLocationDummyFactory({ seed: "profile.vendor_locations:bench-reset" }).list(1000);
      const store = createVendorLocationStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.vendor_locations.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createVendorLocationDummyFactory({ seed: "profile.vendor_locations:bench-selector" }).list(1000);
      const store = createVendorLocationStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[vendorLocationSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type VendorProfilePrototype = Record<string, unknown>;

/** Typed field shape for profile.v1.VendorProfile. Use it to narrow VendorProfilePrototype records. */
export type VendorProfileFields = {
  readonly "badge_tier": string;
  readonly "created_at"?: string | null;
  readonly "cuisine": string;
  readonly "display_name": string;
  readonly "id": string;
  readonly "is_open": boolean;
  readonly "is_sick_paused": boolean;
  readonly "location_label": string;
  readonly "organization_id": string;
  readonly "profile_id": string;
  readonly "schedule"?: Record<string, unknown> | null;
  readonly "store_id": string;
  readonly "updated_at"?: string | null;
  readonly "vendor_type": "VENDOR_TYPE_HOME_CHEF" | "VENDOR_TYPE_BAKERY" | "VENDOR_TYPE_FOOD_TRUCK" | "VENDOR_TYPE_CATERER" | "VENDOR_TYPE_MARKET_STORE" | "VENDOR_TYPE_GROCERY" | "VENDOR_TYPE_SUPERMARKET" | "VENDOR_TYPE_PHARMACY" | "VENDOR_TYPE_SPECIALTY";
  readonly "verification_status": "VERIFICATION_STATUS_PENDING" | "VERIFICATION_STATUS_VERIFIED" | "VERIFICATION_STATUS_REJECTED" | "VERIFICATION_STATUS_UNVERIFIED" | "VERIFICATION_STATUS_SUSPENDED" | "VERIFICATION_STATUS_REVOKED";
};

export const vendorProfileFieldOrder = ["id","organization_id","profile_id","vendor_type","display_name","cuisine","location_label","is_open","verification_status","store_id","created_at","updated_at","schedule","badge_tier","is_sick_paused"] as const satisfies readonly (keyof VendorProfileFields)[];

export const vendorProfileRequiredFields = ["badge_tier","cuisine","display_name","id","is_open","is_sick_paused","location_label","organization_id","profile_id","store_id","vendor_type","verification_status"] as const satisfies readonly (keyof VendorProfileFields)[];

export type VendorProfileStore = TenantProjectionStore<VendorProfilePrototype>;

export const vendorProfileSchema = {
  "collection": "vendor_profiles",
  "domain": "profile",
  "fields": [
    {
      "kind": "string",
      "name": "badge_tier"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "cuisine"
    },
    {
      "kind": "string",
      "name": "display_name"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "bool",
      "name": "is_open"
    },
    {
      "kind": "bool",
      "name": "is_sick_paused"
    },
    {
      "kind": "string",
      "name": "location_label"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    },
    {
      "kind": "object",
      "name": "schedule",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "store_id"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    },
    {
      "enumValues": [
        "VENDOR_TYPE_HOME_CHEF",
        "VENDOR_TYPE_BAKERY",
        "VENDOR_TYPE_FOOD_TRUCK",
        "VENDOR_TYPE_CATERER",
        "VENDOR_TYPE_MARKET_STORE",
        "VENDOR_TYPE_GROCERY",
        "VENDOR_TYPE_SUPERMARKET",
        "VENDOR_TYPE_PHARMACY",
        "VENDOR_TYPE_SPECIALTY"
      ],
      "kind": "enum",
      "name": "vendor_type"
    },
    {
      "enumValues": [
        "VERIFICATION_STATUS_PENDING",
        "VERIFICATION_STATUS_VERIFIED",
        "VERIFICATION_STATUS_REJECTED",
        "VERIFICATION_STATUS_UNVERIFIED",
        "VERIFICATION_STATUS_SUSPENDED",
        "VERIFICATION_STATUS_REVOKED"
      ],
      "kind": "enum",
      "name": "verification_status"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createVendorProfileDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<VendorProfilePrototype>(vendorProfileSchema, options);

export const vendorProfileRuntimeConstants = {
  collection: vendorProfileSchema.collection,
  domain: vendorProfileSchema.domain,
  idField: vendorProfileSchema.idField ?? "id",
  key: "profile.vendor_profiles",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedVendorProfileDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly VendorProfilePrototype[] =>
  cache.getDummyRecords(vendorProfileSchema, { ...options, tenantId }, count, () =>
    createVendorProfileDummyFactory({ ...options, tenantId }).list(count)
  );

export const createVendorProfileStore = (
  tenantId: string,
  initialRecords: readonly VendorProfilePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): VendorProfileStore => {
  const scope = {
    collection: vendorProfileSchema.collection,
    domain: vendorProfileSchema.domain,
    tenantId,
  };
  const store = cache.getStore<VendorProfilePrototype>(
    scope,
    () =>
      createTenantProjectionStore<VendorProfilePrototype>(
        scope,
        (record) => String(record[vendorProfileSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectVendorProfileLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<VendorProfilePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: VendorProfileStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<VendorProfilePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createVendorProfileStore(tenantId, [], cache);
  return createLiveProjectionBinding<VendorProfilePrototype>({
    ...bindingOptions,
    scope: {
      collection: vendorProfileSchema.collection,
      domain: vendorProfileSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerVendorProfileStore = (
  tenantId: string,
  store: VendorProfileStore = createVendorProfileStore(tenantId)
): VendorProfileStore => {
  prototypeStoreRegistry.register("profile.vendor_profiles", store);
  return store;
};

export const useVendorProfileSnapshot = () => prototypeStoreRegistry.use("profile.vendor_profiles");

export const vendorProfileFixtures = {
  empty: (): VendorProfilePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly VendorProfilePrototype[] =>
    createCachedVendorProfileDummyRecords(tenantId, 8, { seed: "profile.vendor_profiles:happy" }),
  invalidMissingId: (): VendorProfilePrototype[] =>
    createVendorProfileDummyFactory({ seed: "profile.vendor_profiles:invalid" }).list(1, () => ({
      [vendorProfileSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly VendorProfilePrototype[] =>
    createCachedVendorProfileDummyRecords(tenantId, count, { seed: "profile.vendor_profiles:high-volume" }),
};

export const vendorProfileFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<VendorProfilePrototype> => ({
    collection: vendorProfileSchema.collection,
    domain: vendorProfileSchema.domain,
    name: "profile.vendor_profiles.empty",
    records: vendorProfileFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<VendorProfilePrototype> => ({
    collection: vendorProfileSchema.collection,
    domain: vendorProfileSchema.domain,
    name: "profile.vendor_profiles.happyPath",
    records: vendorProfileFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<VendorProfilePrototype> => ({
    collection: vendorProfileSchema.collection,
    domain: vendorProfileSchema.domain,
    name: "profile.vendor_profiles.invalidMissingId",
    records: vendorProfileFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<VendorProfilePrototype> => ({
    collection: vendorProfileSchema.collection,
    domain: vendorProfileSchema.domain,
    name: "profile.vendor_profiles.highVolume",
    records: vendorProfileFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const vendorProfileBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "profile.vendor_profiles.dummy.list.1000",
    records: 1000,
    run: () => createCachedVendorProfileDummyRecords("tenant-benchmark", 1000, { seed: "profile.vendor_profiles:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "profile.vendor_profiles.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createVendorProfileDummyFactory({ seed: "profile.vendor_profiles:bench-store" }).list(1000);
      const store = createVendorProfileStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.vendor_profiles.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createVendorProfileDummyFactory({ seed: "profile.vendor_profiles:bench-apply" }).list(1000);
      const store = createVendorProfileStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[vendorProfileSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: vendorProfileSchema.collection,
          domain: vendorProfileSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.vendor_profiles.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createVendorProfileDummyFactory({ seed: "profile.vendor_profiles:bench-reset" }).list(1000);
      const store = createVendorProfileStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "profile.vendor_profiles.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createVendorProfileDummyFactory({ seed: "profile.vendor_profiles:bench-selector" }).list(1000);
      const store = createVendorProfileStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[vendorProfileSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type RiderProfilePrototype = Record<string, unknown>;

/** Typed field shape for rider.v1.RiderProfile. Use it to narrow RiderProfilePrototype records. */
export type RiderProfileFields = {
  readonly "created_at"?: string | null;
  readonly "email": string;
  readonly "full_name": string;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "phone": string;
  readonly "rating_avg": number;
  readonly "rating_count": number;
  readonly "status": "RIDER_STATUS_PENDING_VERIFICATION" | "RIDER_STATUS_ACTIVE" | "RIDER_STATUS_SUSPENDED";
  readonly "updated_at"?: string | null;
  readonly "vehicle_type": "VEHICLE_TYPE_ON_FOOT" | "VEHICLE_TYPE_BICYCLE" | "VEHICLE_TYPE_MOTORBIKE" | "VEHICLE_TYPE_KEKE" | "VEHICLE_TYPE_CAR" | "VEHICLE_TYPE_VAN";
};

export const riderProfileFieldOrder = ["id","organization_id","created_at","updated_at","full_name","phone","email","vehicle_type","status","rating_avg","rating_count"] as const satisfies readonly (keyof RiderProfileFields)[];

export const riderProfileRequiredFields = ["email","full_name","id","organization_id","phone","rating_avg","rating_count","status","vehicle_type"] as const satisfies readonly (keyof RiderProfileFields)[];

export type RiderProfileStore = TenantProjectionStore<RiderProfilePrototype>;

export const riderProfileSchema = {
  "collection": "rider_profiles",
  "domain": "rider",
  "fields": [
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "email",
      "name": "email"
    },
    {
      "kind": "string",
      "name": "full_name"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "phone"
    },
    {
      "kind": "float",
      "name": "rating_avg"
    },
    {
      "kind": "int",
      "name": "rating_count"
    },
    {
      "enumValues": [
        "RIDER_STATUS_PENDING_VERIFICATION",
        "RIDER_STATUS_ACTIVE",
        "RIDER_STATUS_SUSPENDED"
      ],
      "kind": "enum",
      "name": "status"
    },
    {
      "kind": "timestamp",
      "name": "updated_at",
      "optional": true
    },
    {
      "enumValues": [
        "VEHICLE_TYPE_ON_FOOT",
        "VEHICLE_TYPE_BICYCLE",
        "VEHICLE_TYPE_MOTORBIKE",
        "VEHICLE_TYPE_KEKE",
        "VEHICLE_TYPE_CAR",
        "VEHICLE_TYPE_VAN"
      ],
      "kind": "enum",
      "name": "vehicle_type"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createRiderProfileDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<RiderProfilePrototype>(riderProfileSchema, options);

export const riderProfileRuntimeConstants = {
  collection: riderProfileSchema.collection,
  domain: riderProfileSchema.domain,
  idField: riderProfileSchema.idField ?? "id",
  key: "rider.rider_profiles",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedRiderProfileDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly RiderProfilePrototype[] =>
  cache.getDummyRecords(riderProfileSchema, { ...options, tenantId }, count, () =>
    createRiderProfileDummyFactory({ ...options, tenantId }).list(count)
  );

export const createRiderProfileStore = (
  tenantId: string,
  initialRecords: readonly RiderProfilePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): RiderProfileStore => {
  const scope = {
    collection: riderProfileSchema.collection,
    domain: riderProfileSchema.domain,
    tenantId,
  };
  const store = cache.getStore<RiderProfilePrototype>(
    scope,
    () =>
      createTenantProjectionStore<RiderProfilePrototype>(
        scope,
        (record) => String(record[riderProfileSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectRiderProfileLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<RiderProfilePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: RiderProfileStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<RiderProfilePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createRiderProfileStore(tenantId, [], cache);
  return createLiveProjectionBinding<RiderProfilePrototype>({
    ...bindingOptions,
    scope: {
      collection: riderProfileSchema.collection,
      domain: riderProfileSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerRiderProfileStore = (
  tenantId: string,
  store: RiderProfileStore = createRiderProfileStore(tenantId)
): RiderProfileStore => {
  prototypeStoreRegistry.register("rider.rider_profiles", store);
  return store;
};

export const useRiderProfileSnapshot = () => prototypeStoreRegistry.use("rider.rider_profiles");

export const riderProfileFixtures = {
  empty: (): RiderProfilePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly RiderProfilePrototype[] =>
    createCachedRiderProfileDummyRecords(tenantId, 8, { seed: "rider.rider_profiles:happy" }),
  invalidMissingId: (): RiderProfilePrototype[] =>
    createRiderProfileDummyFactory({ seed: "rider.rider_profiles:invalid" }).list(1, () => ({
      [riderProfileSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly RiderProfilePrototype[] =>
    createCachedRiderProfileDummyRecords(tenantId, count, { seed: "rider.rider_profiles:high-volume" }),
};

export const riderProfileFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<RiderProfilePrototype> => ({
    collection: riderProfileSchema.collection,
    domain: riderProfileSchema.domain,
    name: "rider.rider_profiles.empty",
    records: riderProfileFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<RiderProfilePrototype> => ({
    collection: riderProfileSchema.collection,
    domain: riderProfileSchema.domain,
    name: "rider.rider_profiles.happyPath",
    records: riderProfileFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<RiderProfilePrototype> => ({
    collection: riderProfileSchema.collection,
    domain: riderProfileSchema.domain,
    name: "rider.rider_profiles.invalidMissingId",
    records: riderProfileFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<RiderProfilePrototype> => ({
    collection: riderProfileSchema.collection,
    domain: riderProfileSchema.domain,
    name: "rider.rider_profiles.highVolume",
    records: riderProfileFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const riderProfileBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "rider.rider_profiles.dummy.list.1000",
    records: 1000,
    run: () => createCachedRiderProfileDummyRecords("tenant-benchmark", 1000, { seed: "rider.rider_profiles:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "rider.rider_profiles.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createRiderProfileDummyFactory({ seed: "rider.rider_profiles:bench-store" }).list(1000);
      const store = createRiderProfileStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "rider.rider_profiles.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createRiderProfileDummyFactory({ seed: "rider.rider_profiles:bench-apply" }).list(1000);
      const store = createRiderProfileStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[riderProfileSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: riderProfileSchema.collection,
          domain: riderProfileSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "rider.rider_profiles.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createRiderProfileDummyFactory({ seed: "rider.rider_profiles:bench-reset" }).list(1000);
      const store = createRiderProfileStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "rider.rider_profiles.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createRiderProfileDummyFactory({ seed: "rider.rider_profiles:bench-selector" }).list(1000);
      const store = createRiderProfileStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[riderProfileSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type SupportMessagePrototype = Record<string, unknown>;

/** Typed field shape for support.v1.SupportMessage. Use it to narrow SupportMessagePrototype records. */
export type SupportMessageFields = {
  readonly "body": string;
  readonly "created_at"?: string | null;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "sender_id": string;
  readonly "sender_type": string;
  readonly "thread_id": string;
};

export const supportMessageFieldOrder = ["id","organization_id","created_at","thread_id","sender_id","sender_type","body"] as const satisfies readonly (keyof SupportMessageFields)[];

export const supportMessageRequiredFields = ["body","id","organization_id","sender_id","sender_type","thread_id"] as const satisfies readonly (keyof SupportMessageFields)[];

export type SupportMessageStore = TenantProjectionStore<SupportMessagePrototype>;

export const supportMessageSchema = {
  "collection": "support_messages",
  "domain": "support",
  "fields": [
    {
      "kind": "text",
      "name": "body"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "uuid",
      "name": "sender_id"
    },
    {
      "kind": "string",
      "name": "sender_type"
    },
    {
      "kind": "uuid",
      "name": "thread_id"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createSupportMessageDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<SupportMessagePrototype>(supportMessageSchema, options);

export const supportMessageRuntimeConstants = {
  collection: supportMessageSchema.collection,
  domain: supportMessageSchema.domain,
  idField: supportMessageSchema.idField ?? "id",
  key: "support.support_messages",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedSupportMessageDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly SupportMessagePrototype[] =>
  cache.getDummyRecords(supportMessageSchema, { ...options, tenantId }, count, () =>
    createSupportMessageDummyFactory({ ...options, tenantId }).list(count)
  );

export const createSupportMessageStore = (
  tenantId: string,
  initialRecords: readonly SupportMessagePrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): SupportMessageStore => {
  const scope = {
    collection: supportMessageSchema.collection,
    domain: supportMessageSchema.domain,
    tenantId,
  };
  const store = cache.getStore<SupportMessagePrototype>(
    scope,
    () =>
      createTenantProjectionStore<SupportMessagePrototype>(
        scope,
        (record) => String(record[supportMessageSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectSupportMessageLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<SupportMessagePrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: SupportMessageStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<SupportMessagePrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createSupportMessageStore(tenantId, [], cache);
  return createLiveProjectionBinding<SupportMessagePrototype>({
    ...bindingOptions,
    scope: {
      collection: supportMessageSchema.collection,
      domain: supportMessageSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerSupportMessageStore = (
  tenantId: string,
  store: SupportMessageStore = createSupportMessageStore(tenantId)
): SupportMessageStore => {
  prototypeStoreRegistry.register("support.support_messages", store);
  return store;
};

export const useSupportMessageSnapshot = () => prototypeStoreRegistry.use("support.support_messages");

export const supportMessageFixtures = {
  empty: (): SupportMessagePrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly SupportMessagePrototype[] =>
    createCachedSupportMessageDummyRecords(tenantId, 8, { seed: "support.support_messages:happy" }),
  invalidMissingId: (): SupportMessagePrototype[] =>
    createSupportMessageDummyFactory({ seed: "support.support_messages:invalid" }).list(1, () => ({
      [supportMessageSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly SupportMessagePrototype[] =>
    createCachedSupportMessageDummyRecords(tenantId, count, { seed: "support.support_messages:high-volume" }),
};

export const supportMessageFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<SupportMessagePrototype> => ({
    collection: supportMessageSchema.collection,
    domain: supportMessageSchema.domain,
    name: "support.support_messages.empty",
    records: supportMessageFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<SupportMessagePrototype> => ({
    collection: supportMessageSchema.collection,
    domain: supportMessageSchema.domain,
    name: "support.support_messages.happyPath",
    records: supportMessageFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<SupportMessagePrototype> => ({
    collection: supportMessageSchema.collection,
    domain: supportMessageSchema.domain,
    name: "support.support_messages.invalidMissingId",
    records: supportMessageFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<SupportMessagePrototype> => ({
    collection: supportMessageSchema.collection,
    domain: supportMessageSchema.domain,
    name: "support.support_messages.highVolume",
    records: supportMessageFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const supportMessageBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "support.support_messages.dummy.list.1000",
    records: 1000,
    run: () => createCachedSupportMessageDummyRecords("tenant-benchmark", 1000, { seed: "support.support_messages:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "support.support_messages.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createSupportMessageDummyFactory({ seed: "support.support_messages:bench-store" }).list(1000);
      const store = createSupportMessageStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "support.support_messages.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createSupportMessageDummyFactory({ seed: "support.support_messages:bench-apply" }).list(1000);
      const store = createSupportMessageStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[supportMessageSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: supportMessageSchema.collection,
          domain: supportMessageSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "support.support_messages.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createSupportMessageDummyFactory({ seed: "support.support_messages:bench-reset" }).list(1000);
      const store = createSupportMessageStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "support.support_messages.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createSupportMessageDummyFactory({ seed: "support.support_messages:bench-selector" }).list(1000);
      const store = createSupportMessageStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[supportMessageSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type AccountSessionPrototype = Record<string, unknown>;

/** Typed field shape for user.v1.AccountSession. Use it to narrow AccountSessionPrototype records. */
export type AccountSessionFields = {
  readonly "access_token": string;
  readonly "avatar_url": string;
  readonly "display_name": string;
  readonly "email": string;
  readonly "expires_in": number;
  readonly "external_user_id": string;
  readonly "organization_id": string;
  readonly "phone": string;
  readonly "profile_id": string;
  readonly "role": string;
  readonly "token_type": string;
};

export const accountSessionFieldOrder = ["external_user_id","profile_id","organization_id","email","display_name","role","access_token","token_type","expires_in","avatar_url","phone"] as const satisfies readonly (keyof AccountSessionFields)[];

export const accountSessionRequiredFields = ["access_token","avatar_url","display_name","email","expires_in","external_user_id","organization_id","phone","profile_id","role","token_type"] as const satisfies readonly (keyof AccountSessionFields)[];

export type AccountSessionStore = TenantProjectionStore<AccountSessionPrototype>;

export const accountSessionSchema = {
  "collection": "account_sessions",
  "domain": "user",
  "fields": [
    {
      "kind": "string",
      "name": "access_token"
    },
    {
      "kind": "url",
      "name": "avatar_url"
    },
    {
      "kind": "string",
      "name": "display_name"
    },
    {
      "kind": "email",
      "name": "email"
    },
    {
      "kind": "int",
      "name": "expires_in"
    },
    {
      "kind": "uuid",
      "name": "external_user_id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "phone"
    },
    {
      "kind": "uuid",
      "name": "profile_id"
    },
    {
      "kind": "string",
      "name": "role"
    },
    {
      "kind": "string",
      "name": "token_type"
    }
  ],
  "idField": "external_user_id"
} as const satisfies DummySchemaSpec;

export const createAccountSessionDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<AccountSessionPrototype>(accountSessionSchema, options);

export const accountSessionRuntimeConstants = {
  collection: accountSessionSchema.collection,
  domain: accountSessionSchema.domain,
  idField: accountSessionSchema.idField ?? "id",
  key: "user.account_sessions",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedAccountSessionDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly AccountSessionPrototype[] =>
  cache.getDummyRecords(accountSessionSchema, { ...options, tenantId }, count, () =>
    createAccountSessionDummyFactory({ ...options, tenantId }).list(count)
  );

export const createAccountSessionStore = (
  tenantId: string,
  initialRecords: readonly AccountSessionPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): AccountSessionStore => {
  const scope = {
    collection: accountSessionSchema.collection,
    domain: accountSessionSchema.domain,
    tenantId,
  };
  const store = cache.getStore<AccountSessionPrototype>(
    scope,
    () =>
      createTenantProjectionStore<AccountSessionPrototype>(
        scope,
        (record) => String(record[accountSessionSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectAccountSessionLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<AccountSessionPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: AccountSessionStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<AccountSessionPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createAccountSessionStore(tenantId, [], cache);
  return createLiveProjectionBinding<AccountSessionPrototype>({
    ...bindingOptions,
    scope: {
      collection: accountSessionSchema.collection,
      domain: accountSessionSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerAccountSessionStore = (
  tenantId: string,
  store: AccountSessionStore = createAccountSessionStore(tenantId)
): AccountSessionStore => {
  prototypeStoreRegistry.register("user.account_sessions", store);
  return store;
};

export const useAccountSessionSnapshot = () => prototypeStoreRegistry.use("user.account_sessions");

export const accountSessionFixtures = {
  empty: (): AccountSessionPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly AccountSessionPrototype[] =>
    createCachedAccountSessionDummyRecords(tenantId, 8, { seed: "user.account_sessions:happy" }),
  invalidMissingId: (): AccountSessionPrototype[] =>
    createAccountSessionDummyFactory({ seed: "user.account_sessions:invalid" }).list(1, () => ({
      [accountSessionSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly AccountSessionPrototype[] =>
    createCachedAccountSessionDummyRecords(tenantId, count, { seed: "user.account_sessions:high-volume" }),
};

export const accountSessionFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<AccountSessionPrototype> => ({
    collection: accountSessionSchema.collection,
    domain: accountSessionSchema.domain,
    name: "user.account_sessions.empty",
    records: accountSessionFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<AccountSessionPrototype> => ({
    collection: accountSessionSchema.collection,
    domain: accountSessionSchema.domain,
    name: "user.account_sessions.happyPath",
    records: accountSessionFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<AccountSessionPrototype> => ({
    collection: accountSessionSchema.collection,
    domain: accountSessionSchema.domain,
    name: "user.account_sessions.invalidMissingId",
    records: accountSessionFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<AccountSessionPrototype> => ({
    collection: accountSessionSchema.collection,
    domain: accountSessionSchema.domain,
    name: "user.account_sessions.highVolume",
    records: accountSessionFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const accountSessionBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "user.account_sessions.dummy.list.1000",
    records: 1000,
    run: () => createCachedAccountSessionDummyRecords("tenant-benchmark", 1000, { seed: "user.account_sessions:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "user.account_sessions.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createAccountSessionDummyFactory({ seed: "user.account_sessions:bench-store" }).list(1000);
      const store = createAccountSessionStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "user.account_sessions.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createAccountSessionDummyFactory({ seed: "user.account_sessions:bench-apply" }).list(1000);
      const store = createAccountSessionStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[accountSessionSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: accountSessionSchema.collection,
          domain: accountSessionSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "user.account_sessions.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createAccountSessionDummyFactory({ seed: "user.account_sessions:bench-reset" }).list(1000);
      const store = createAccountSessionStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "user.account_sessions.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createAccountSessionDummyFactory({ seed: "user.account_sessions:bench-selector" }).list(1000);
      const store = createAccountSessionStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[accountSessionSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export type WalletTransactionPrototype = Record<string, unknown>;

/** Typed field shape for wallet.v1.WalletTransaction. Use it to narrow WalletTransactionPrototype records. */
export type WalletTransactionFields = {
  readonly "amount_minor": number;
  readonly "created_at"?: string | null;
  readonly "currency": string;
  readonly "id": string;
  readonly "organization_id": string;
  readonly "reference": string;
  readonly "rider_id": string;
  readonly "status": string;
  readonly "type": "WALLET_TRANSACTION_TYPE_DELIVERY" | "WALLET_TRANSACTION_TYPE_TIP" | "WALLET_TRANSACTION_TYPE_BONUS" | "WALLET_TRANSACTION_TYPE_CASHOUT" | "WALLET_TRANSACTION_TYPE_ADJUSTMENT";
};

export const walletTransactionFieldOrder = ["id","organization_id","created_at","rider_id","type","amount_minor","currency","reference","status"] as const satisfies readonly (keyof WalletTransactionFields)[];

export const walletTransactionRequiredFields = ["amount_minor","currency","id","organization_id","reference","rider_id","status","type"] as const satisfies readonly (keyof WalletTransactionFields)[];

export type WalletTransactionStore = TenantProjectionStore<WalletTransactionPrototype>;

export const walletTransactionSchema = {
  "collection": "wallet_transactions",
  "domain": "wallet",
  "fields": [
    {
      "kind": "int",
      "name": "amount_minor"
    },
    {
      "kind": "timestamp",
      "name": "created_at",
      "optional": true
    },
    {
      "kind": "string",
      "name": "currency"
    },
    {
      "kind": "uuid",
      "name": "id"
    },
    {
      "kind": "uuid",
      "name": "organization_id"
    },
    {
      "kind": "string",
      "name": "reference"
    },
    {
      "kind": "uuid",
      "name": "rider_id"
    },
    {
      "kind": "string",
      "name": "status"
    },
    {
      "enumValues": [
        "WALLET_TRANSACTION_TYPE_DELIVERY",
        "WALLET_TRANSACTION_TYPE_TIP",
        "WALLET_TRANSACTION_TYPE_BONUS",
        "WALLET_TRANSACTION_TYPE_CASHOUT",
        "WALLET_TRANSACTION_TYPE_ADJUSTMENT"
      ],
      "kind": "enum",
      "name": "type"
    }
  ],
  "idField": "id"
} as const satisfies DummySchemaSpec;

export const createWalletTransactionDummyFactory = (options: DummyDataFactoryOptions = {}) =>
  createDummyDataFactory<WalletTransactionPrototype>(walletTransactionSchema, options);

export const walletTransactionRuntimeConstants = {
  collection: walletTransactionSchema.collection,
  domain: walletTransactionSchema.domain,
  idField: walletTransactionSchema.idField ?? "id",
  key: "wallet.wallet_transactions",
} as const satisfies PrototypeSchemaRuntimeConstants;

export const createCachedWalletTransactionDummyRecords = (
  tenantId: string,
  count = 1000,
  options: DummyDataFactoryOptions = {},
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): readonly WalletTransactionPrototype[] =>
  cache.getDummyRecords(walletTransactionSchema, { ...options, tenantId }, count, () =>
    createWalletTransactionDummyFactory({ ...options, tenantId }).list(count)
  );

export const createWalletTransactionStore = (
  tenantId: string,
  initialRecords: readonly WalletTransactionPrototype[] = [],
  cache: PrototypeRuntimeCache = prototypeRuntimeCache
): WalletTransactionStore => {
  const scope = {
    collection: walletTransactionSchema.collection,
    domain: walletTransactionSchema.domain,
    tenantId,
  };
  const store = cache.getStore<WalletTransactionPrototype>(
    scope,
    () =>
      createTenantProjectionStore<WalletTransactionPrototype>(
        scope,
        (record) => String(record[walletTransactionSchema.idField ?? "id"] ?? "")
      )
  );
  if (initialRecords.length > 0) {
    store.replace(initialRecords);
  }
  return store;
};

export const connectWalletTransactionLiveProjection = (
  options: Omit<LiveProjectionBindingOptions<WalletTransactionPrototype>, "scope" | "store"> & {
    tenantId: string;
    store?: WalletTransactionStore;
    cache?: PrototypeRuntimeCache;
  }
): LiveProjectionBinding<WalletTransactionPrototype> => {
  const { tenantId, store, cache = prototypeRuntimeCache, ...bindingOptions } = options;
  const resolvedStore = store ?? createWalletTransactionStore(tenantId, [], cache);
  return createLiveProjectionBinding<WalletTransactionPrototype>({
    ...bindingOptions,
    scope: {
      collection: walletTransactionSchema.collection,
      domain: walletTransactionSchema.domain,
      tenantId,
    },
    store: resolvedStore,
  });
};

export const registerWalletTransactionStore = (
  tenantId: string,
  store: WalletTransactionStore = createWalletTransactionStore(tenantId)
): WalletTransactionStore => {
  prototypeStoreRegistry.register("wallet.wallet_transactions", store);
  return store;
};

export const useWalletTransactionSnapshot = () => prototypeStoreRegistry.use("wallet.wallet_transactions");

export const walletTransactionFixtures = {
  empty: (): WalletTransactionPrototype[] => [],
  happyPath: (tenantId = "tenant-prototype"): readonly WalletTransactionPrototype[] =>
    createCachedWalletTransactionDummyRecords(tenantId, 8, { seed: "wallet.wallet_transactions:happy" }),
  invalidMissingId: (): WalletTransactionPrototype[] =>
    createWalletTransactionDummyFactory({ seed: "wallet.wallet_transactions:invalid" }).list(1, () => ({
      [walletTransactionSchema.idField ?? "id"]: "",
    })),
  highVolume: (tenantId = "tenant-prototype", count = 1000): readonly WalletTransactionPrototype[] =>
    createCachedWalletTransactionDummyRecords(tenantId, count, { seed: "wallet.wallet_transactions:high-volume" }),
};

export const walletTransactionFixtureStates = {
  empty: (tenantId = "tenant-prototype"): PrototypeFixtureState<WalletTransactionPrototype> => ({
    collection: walletTransactionSchema.collection,
    domain: walletTransactionSchema.domain,
    name: "wallet.wallet_transactions.empty",
    records: walletTransactionFixtures.empty(),
    tenantId,
  }),
  happyPath: (tenantId = "tenant-prototype"): PrototypeFixtureState<WalletTransactionPrototype> => ({
    collection: walletTransactionSchema.collection,
    domain: walletTransactionSchema.domain,
    name: "wallet.wallet_transactions.happyPath",
    records: walletTransactionFixtures.happyPath(tenantId),
    tenantId,
  }),
  invalidMissingId: (tenantId = "tenant-prototype"): PrototypeFixtureState<WalletTransactionPrototype> => ({
    collection: walletTransactionSchema.collection,
    domain: walletTransactionSchema.domain,
    name: "wallet.wallet_transactions.invalidMissingId",
    records: walletTransactionFixtures.invalidMissingId(),
    tenantId,
  }),
  highVolume: (tenantId = "tenant-prototype", count = 1000): PrototypeFixtureState<WalletTransactionPrototype> => ({
    collection: walletTransactionSchema.collection,
    domain: walletTransactionSchema.domain,
    name: "wallet.wallet_transactions.highVolume",
    records: walletTransactionFixtures.highVolume(tenantId, count),
    tenantId,
  }),
};

export const walletTransactionBenchmarkFixtures: readonly PrototypeBenchmarkFixture[] = [
  {
    lane: "dummy",
    name: "wallet.wallet_transactions.dummy.list.1000",
    records: 1000,
    run: () => createCachedWalletTransactionDummyRecords("tenant-benchmark", 1000, { seed: "wallet.wallet_transactions:bench-dummy" }).length,
  },
  {
    lane: "store",
    name: "wallet.wallet_transactions.store.replace.1000",
    records: 1000,
    run: () => {
      const records = createWalletTransactionDummyFactory({ seed: "wallet.wallet_transactions:bench-store" }).list(1000);
      const store = createWalletTransactionStore("tenant-benchmark", records);
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "wallet.wallet_transactions.store.apply.1000",
    records: 1000,
    run: () => {
      const records = createWalletTransactionDummyFactory({ seed: "wallet.wallet_transactions:bench-apply" }).list(1000);
      const store = createWalletTransactionStore("tenant-benchmark");
      records.forEach((record, index) => {
        const recordId = String(record[walletTransactionSchema.idField ?? "id"] ?? index);
        store.apply({
          collection: walletTransactionSchema.collection,
          domain: walletTransactionSchema.domain,
          operation: "upsert",
          record,
          recordId,
          tenantId: "tenant-benchmark",
          version: index,
        });
      });
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "wallet.wallet_transactions.store.reset.1000",
    records: 1000,
    run: () => {
      const records = createWalletTransactionDummyFactory({ seed: "wallet.wallet_transactions:bench-reset" }).list(1000);
      const store = createWalletTransactionStore("tenant-benchmark", records);
      store.reset();
      return store.getSnapshot().records.length;
    },
  },
  {
    lane: "store",
    name: "wallet.wallet_transactions.store.selector.1000",
    records: 1000,
    run: () => {
      const records = createWalletTransactionDummyFactory({ seed: "wallet.wallet_transactions:bench-selector" }).list(1000);
      const store = createWalletTransactionStore("tenant-benchmark", records);
      const selectedId = String(records[500]?.[walletTransactionSchema.idField ?? "id"] ?? "");
      return store.get(selectedId);
    },
  },
];

export const prototypeSchemaSource = "api/protos";

export const prototypeSchemas = [aIResolutionSchema, helpTicketSchema, checkoutSessionSchema, paymentSchema, profileWalletBalanceSchema, refundSchema, subscriptionSchema, deliverySchema, discoverChefSchema, discoverDishSchema, observationSchema, stopSchema, unservedStopSchema, offerCandidateSchema, riderLocationSchema, geoPointSchema, routePlanSchema, cartItemSchema, marketplaceOrderSchema, orderItemSchema, mealPlanDropSchema, mealPlanSubscriptionSchema, mealPlanZoneSchema, chefMenuSchema, dishFavouriteSchema, dishSchema, mealCategorySchema, mealPlanCycleSchema, mealPlanEntrySchema, mealPlanSchema, trendingChefSchema, trendingDishSchema, opsConversationSchema, orderMessageSchema, orderThreadSchema, offerSchema, chowProfileSchema, profileLinkSchema, vendorLocationSchema, vendorProfileSchema, riderProfileSchema, supportMessageSchema, accountSessionSchema, walletTransactionSchema] as const satisfies readonly DummySchemaSpec[];

export const prototypeSchemaRuntimeConstants = [aIResolutionRuntimeConstants, helpTicketRuntimeConstants, checkoutSessionRuntimeConstants, paymentRuntimeConstants, profileWalletBalanceRuntimeConstants, refundRuntimeConstants, subscriptionRuntimeConstants, deliveryRuntimeConstants, discoverChefRuntimeConstants, discoverDishRuntimeConstants, observationRuntimeConstants, stopRuntimeConstants, unservedStopRuntimeConstants, offerCandidateRuntimeConstants, riderLocationRuntimeConstants, geoPointRuntimeConstants, routePlanRuntimeConstants, cartItemRuntimeConstants, marketplaceOrderRuntimeConstants, orderItemRuntimeConstants, mealPlanDropRuntimeConstants, mealPlanSubscriptionRuntimeConstants, mealPlanZoneRuntimeConstants, chefMenuRuntimeConstants, dishFavouriteRuntimeConstants, dishRuntimeConstants, mealCategoryRuntimeConstants, mealPlanCycleRuntimeConstants, mealPlanEntryRuntimeConstants, mealPlanRuntimeConstants, trendingChefRuntimeConstants, trendingDishRuntimeConstants, opsConversationRuntimeConstants, orderMessageRuntimeConstants, orderThreadRuntimeConstants, offerRuntimeConstants, chowProfileRuntimeConstants, profileLinkRuntimeConstants, vendorLocationRuntimeConstants, vendorProfileRuntimeConstants, riderProfileRuntimeConstants, supportMessageRuntimeConstants, accountSessionRuntimeConstants, walletTransactionRuntimeConstants] as const satisfies readonly PrototypeSchemaRuntimeConstants[];

export const prototypeSchemaByCollection = {
  "assist.ai_resolutions": aIResolutionSchema,
  "assist.help_tickets": helpTicketSchema,
  "billing.checkout_sessions": checkoutSessionSchema,
  "billing.payments": paymentSchema,
  "billing.profile_wallet_balances": profileWalletBalanceSchema,
  "billing.refunds": refundSchema,
  "billing.subscriptions": subscriptionSchema,
  "delivery.deliveries": deliverySchema,
  "discovery.discover_chefs": discoverChefSchema,
  "discovery.discover_dishes": discoverDishSchema,
  "logistics_runtime.observations": observationSchema,
  "logistics_runtime.stops": stopSchema,
  "logistics_runtime.unserved_stops": unservedStopSchema,
  "logistics.offer_candidates": offerCandidateSchema,
  "logistics.rider_locations": riderLocationSchema,
  "maps.geo_points": geoPointSchema,
  "maps.route_plans": routePlanSchema,
  "marketplace.carts": cartItemSchema,
  "marketplace.marketplace_orders": marketplaceOrderSchema,
  "marketplace.orders": orderItemSchema,
  "mealplan.meal_plan_drops": mealPlanDropSchema,
  "mealplan.meal_plan_subscriptions": mealPlanSubscriptionSchema,
  "mealplan.meal_plan_zones": mealPlanZoneSchema,
  "menu.chef_menus": chefMenuSchema,
  "menu.dish_favourites": dishFavouriteSchema,
  "menu.dishes": dishSchema,
  "menu.meal_categories": mealCategorySchema,
  "menu.meal_plan_cycles": mealPlanCycleSchema,
  "menu.meal_plan_entries": mealPlanEntrySchema,
  "menu.meal_plans": mealPlanSchema,
  "menu.trending_chefs": trendingChefSchema,
  "menu.trending_dishes": trendingDishSchema,
  "message.ops_conversations": opsConversationSchema,
  "message.order_messages": orderMessageSchema,
  "message.order_threads": orderThreadSchema,
  "offer.offers": offerSchema,
  "profile.chow_profiles": chowProfileSchema,
  "profile.profile_links": profileLinkSchema,
  "profile.vendor_locations": vendorLocationSchema,
  "profile.vendor_profiles": vendorProfileSchema,
  "rider.rider_profiles": riderProfileSchema,
  "support.support_messages": supportMessageSchema,
  "user.account_sessions": accountSessionSchema,
  "wallet.wallet_transactions": walletTransactionSchema,
} as const;

export const prototypeBenchmarkFixtures = [
  ...aIResolutionBenchmarkFixtures,
  ...helpTicketBenchmarkFixtures,
  ...checkoutSessionBenchmarkFixtures,
  ...paymentBenchmarkFixtures,
  ...profileWalletBalanceBenchmarkFixtures,
  ...refundBenchmarkFixtures,
  ...subscriptionBenchmarkFixtures,
  ...deliveryBenchmarkFixtures,
  ...discoverChefBenchmarkFixtures,
  ...discoverDishBenchmarkFixtures,
  ...observationBenchmarkFixtures,
  ...stopBenchmarkFixtures,
  ...unservedStopBenchmarkFixtures,
  ...offerCandidateBenchmarkFixtures,
  ...riderLocationBenchmarkFixtures,
  ...geoPointBenchmarkFixtures,
  ...routePlanBenchmarkFixtures,
  ...cartItemBenchmarkFixtures,
  ...marketplaceOrderBenchmarkFixtures,
  ...orderItemBenchmarkFixtures,
  ...mealPlanDropBenchmarkFixtures,
  ...mealPlanSubscriptionBenchmarkFixtures,
  ...mealPlanZoneBenchmarkFixtures,
  ...chefMenuBenchmarkFixtures,
  ...dishFavouriteBenchmarkFixtures,
  ...dishBenchmarkFixtures,
  ...mealCategoryBenchmarkFixtures,
  ...mealPlanCycleBenchmarkFixtures,
  ...mealPlanEntryBenchmarkFixtures,
  ...mealPlanBenchmarkFixtures,
  ...trendingChefBenchmarkFixtures,
  ...trendingDishBenchmarkFixtures,
  ...opsConversationBenchmarkFixtures,
  ...orderMessageBenchmarkFixtures,
  ...orderThreadBenchmarkFixtures,
  ...offerBenchmarkFixtures,
  ...chowProfileBenchmarkFixtures,
  ...profileLinkBenchmarkFixtures,
  ...vendorLocationBenchmarkFixtures,
  ...vendorProfileBenchmarkFixtures,
  ...riderProfileBenchmarkFixtures,
  ...supportMessageBenchmarkFixtures,
  ...accountSessionBenchmarkFixtures,
  ...walletTransactionBenchmarkFixtures,
] as const satisfies readonly PrototypeBenchmarkFixture[];

export const prototypeDomains = [
  {
    benchmarkFixtures: aIResolutionBenchmarkFixtures,
    connectLiveProjection: connectAIResolutionLiveProjection,
    constants: aIResolutionRuntimeConstants,
    createDummyRecords: createCachedAIResolutionDummyRecords,
    createStore: createAIResolutionStore,
    fixtureStates: aIResolutionFixtureStates,
    registerStore: registerAIResolutionStore,
    schema: aIResolutionSchema,
  },
  {
    benchmarkFixtures: helpTicketBenchmarkFixtures,
    connectLiveProjection: connectHelpTicketLiveProjection,
    constants: helpTicketRuntimeConstants,
    createDummyRecords: createCachedHelpTicketDummyRecords,
    createStore: createHelpTicketStore,
    fixtureStates: helpTicketFixtureStates,
    registerStore: registerHelpTicketStore,
    schema: helpTicketSchema,
  },
  {
    benchmarkFixtures: checkoutSessionBenchmarkFixtures,
    connectLiveProjection: connectCheckoutSessionLiveProjection,
    constants: checkoutSessionRuntimeConstants,
    createDummyRecords: createCachedCheckoutSessionDummyRecords,
    createStore: createCheckoutSessionStore,
    fixtureStates: checkoutSessionFixtureStates,
    registerStore: registerCheckoutSessionStore,
    schema: checkoutSessionSchema,
  },
  {
    benchmarkFixtures: paymentBenchmarkFixtures,
    connectLiveProjection: connectPaymentLiveProjection,
    constants: paymentRuntimeConstants,
    createDummyRecords: createCachedPaymentDummyRecords,
    createStore: createPaymentStore,
    fixtureStates: paymentFixtureStates,
    registerStore: registerPaymentStore,
    schema: paymentSchema,
  },
  {
    benchmarkFixtures: profileWalletBalanceBenchmarkFixtures,
    connectLiveProjection: connectProfileWalletBalanceLiveProjection,
    constants: profileWalletBalanceRuntimeConstants,
    createDummyRecords: createCachedProfileWalletBalanceDummyRecords,
    createStore: createProfileWalletBalanceStore,
    fixtureStates: profileWalletBalanceFixtureStates,
    registerStore: registerProfileWalletBalanceStore,
    schema: profileWalletBalanceSchema,
  },
  {
    benchmarkFixtures: refundBenchmarkFixtures,
    connectLiveProjection: connectRefundLiveProjection,
    constants: refundRuntimeConstants,
    createDummyRecords: createCachedRefundDummyRecords,
    createStore: createRefundStore,
    fixtureStates: refundFixtureStates,
    registerStore: registerRefundStore,
    schema: refundSchema,
  },
  {
    benchmarkFixtures: subscriptionBenchmarkFixtures,
    connectLiveProjection: connectSubscriptionLiveProjection,
    constants: subscriptionRuntimeConstants,
    createDummyRecords: createCachedSubscriptionDummyRecords,
    createStore: createSubscriptionStore,
    fixtureStates: subscriptionFixtureStates,
    registerStore: registerSubscriptionStore,
    schema: subscriptionSchema,
  },
  {
    benchmarkFixtures: deliveryBenchmarkFixtures,
    connectLiveProjection: connectDeliveryLiveProjection,
    constants: deliveryRuntimeConstants,
    createDummyRecords: createCachedDeliveryDummyRecords,
    createStore: createDeliveryStore,
    fixtureStates: deliveryFixtureStates,
    registerStore: registerDeliveryStore,
    schema: deliverySchema,
  },
  {
    benchmarkFixtures: discoverChefBenchmarkFixtures,
    connectLiveProjection: connectDiscoverChefLiveProjection,
    constants: discoverChefRuntimeConstants,
    createDummyRecords: createCachedDiscoverChefDummyRecords,
    createStore: createDiscoverChefStore,
    fixtureStates: discoverChefFixtureStates,
    registerStore: registerDiscoverChefStore,
    schema: discoverChefSchema,
  },
  {
    benchmarkFixtures: discoverDishBenchmarkFixtures,
    connectLiveProjection: connectDiscoverDishLiveProjection,
    constants: discoverDishRuntimeConstants,
    createDummyRecords: createCachedDiscoverDishDummyRecords,
    createStore: createDiscoverDishStore,
    fixtureStates: discoverDishFixtureStates,
    registerStore: registerDiscoverDishStore,
    schema: discoverDishSchema,
  },
  {
    benchmarkFixtures: observationBenchmarkFixtures,
    connectLiveProjection: connectObservationLiveProjection,
    constants: observationRuntimeConstants,
    createDummyRecords: createCachedObservationDummyRecords,
    createStore: createObservationStore,
    fixtureStates: observationFixtureStates,
    registerStore: registerObservationStore,
    schema: observationSchema,
  },
  {
    benchmarkFixtures: stopBenchmarkFixtures,
    connectLiveProjection: connectStopLiveProjection,
    constants: stopRuntimeConstants,
    createDummyRecords: createCachedStopDummyRecords,
    createStore: createStopStore,
    fixtureStates: stopFixtureStates,
    registerStore: registerStopStore,
    schema: stopSchema,
  },
  {
    benchmarkFixtures: unservedStopBenchmarkFixtures,
    connectLiveProjection: connectUnservedStopLiveProjection,
    constants: unservedStopRuntimeConstants,
    createDummyRecords: createCachedUnservedStopDummyRecords,
    createStore: createUnservedStopStore,
    fixtureStates: unservedStopFixtureStates,
    registerStore: registerUnservedStopStore,
    schema: unservedStopSchema,
  },
  {
    benchmarkFixtures: offerCandidateBenchmarkFixtures,
    connectLiveProjection: connectOfferCandidateLiveProjection,
    constants: offerCandidateRuntimeConstants,
    createDummyRecords: createCachedOfferCandidateDummyRecords,
    createStore: createOfferCandidateStore,
    fixtureStates: offerCandidateFixtureStates,
    registerStore: registerOfferCandidateStore,
    schema: offerCandidateSchema,
  },
  {
    benchmarkFixtures: riderLocationBenchmarkFixtures,
    connectLiveProjection: connectRiderLocationLiveProjection,
    constants: riderLocationRuntimeConstants,
    createDummyRecords: createCachedRiderLocationDummyRecords,
    createStore: createRiderLocationStore,
    fixtureStates: riderLocationFixtureStates,
    registerStore: registerRiderLocationStore,
    schema: riderLocationSchema,
  },
  {
    benchmarkFixtures: geoPointBenchmarkFixtures,
    connectLiveProjection: connectGeoPointLiveProjection,
    constants: geoPointRuntimeConstants,
    createDummyRecords: createCachedGeoPointDummyRecords,
    createStore: createGeoPointStore,
    fixtureStates: geoPointFixtureStates,
    registerStore: registerGeoPointStore,
    schema: geoPointSchema,
  },
  {
    benchmarkFixtures: routePlanBenchmarkFixtures,
    connectLiveProjection: connectRoutePlanLiveProjection,
    constants: routePlanRuntimeConstants,
    createDummyRecords: createCachedRoutePlanDummyRecords,
    createStore: createRoutePlanStore,
    fixtureStates: routePlanFixtureStates,
    registerStore: registerRoutePlanStore,
    schema: routePlanSchema,
  },
  {
    benchmarkFixtures: cartItemBenchmarkFixtures,
    connectLiveProjection: connectCartItemLiveProjection,
    constants: cartItemRuntimeConstants,
    createDummyRecords: createCachedCartItemDummyRecords,
    createStore: createCartItemStore,
    fixtureStates: cartItemFixtureStates,
    registerStore: registerCartItemStore,
    schema: cartItemSchema,
  },
  {
    benchmarkFixtures: marketplaceOrderBenchmarkFixtures,
    connectLiveProjection: connectMarketplaceOrderLiveProjection,
    constants: marketplaceOrderRuntimeConstants,
    createDummyRecords: createCachedMarketplaceOrderDummyRecords,
    createStore: createMarketplaceOrderStore,
    fixtureStates: marketplaceOrderFixtureStates,
    registerStore: registerMarketplaceOrderStore,
    schema: marketplaceOrderSchema,
  },
  {
    benchmarkFixtures: orderItemBenchmarkFixtures,
    connectLiveProjection: connectOrderItemLiveProjection,
    constants: orderItemRuntimeConstants,
    createDummyRecords: createCachedOrderItemDummyRecords,
    createStore: createOrderItemStore,
    fixtureStates: orderItemFixtureStates,
    registerStore: registerOrderItemStore,
    schema: orderItemSchema,
  },
  {
    benchmarkFixtures: mealPlanDropBenchmarkFixtures,
    connectLiveProjection: connectMealPlanDropLiveProjection,
    constants: mealPlanDropRuntimeConstants,
    createDummyRecords: createCachedMealPlanDropDummyRecords,
    createStore: createMealPlanDropStore,
    fixtureStates: mealPlanDropFixtureStates,
    registerStore: registerMealPlanDropStore,
    schema: mealPlanDropSchema,
  },
  {
    benchmarkFixtures: mealPlanSubscriptionBenchmarkFixtures,
    connectLiveProjection: connectMealPlanSubscriptionLiveProjection,
    constants: mealPlanSubscriptionRuntimeConstants,
    createDummyRecords: createCachedMealPlanSubscriptionDummyRecords,
    createStore: createMealPlanSubscriptionStore,
    fixtureStates: mealPlanSubscriptionFixtureStates,
    registerStore: registerMealPlanSubscriptionStore,
    schema: mealPlanSubscriptionSchema,
  },
  {
    benchmarkFixtures: mealPlanZoneBenchmarkFixtures,
    connectLiveProjection: connectMealPlanZoneLiveProjection,
    constants: mealPlanZoneRuntimeConstants,
    createDummyRecords: createCachedMealPlanZoneDummyRecords,
    createStore: createMealPlanZoneStore,
    fixtureStates: mealPlanZoneFixtureStates,
    registerStore: registerMealPlanZoneStore,
    schema: mealPlanZoneSchema,
  },
  {
    benchmarkFixtures: chefMenuBenchmarkFixtures,
    connectLiveProjection: connectChefMenuLiveProjection,
    constants: chefMenuRuntimeConstants,
    createDummyRecords: createCachedChefMenuDummyRecords,
    createStore: createChefMenuStore,
    fixtureStates: chefMenuFixtureStates,
    registerStore: registerChefMenuStore,
    schema: chefMenuSchema,
  },
  {
    benchmarkFixtures: dishFavouriteBenchmarkFixtures,
    connectLiveProjection: connectDishFavouriteLiveProjection,
    constants: dishFavouriteRuntimeConstants,
    createDummyRecords: createCachedDishFavouriteDummyRecords,
    createStore: createDishFavouriteStore,
    fixtureStates: dishFavouriteFixtureStates,
    registerStore: registerDishFavouriteStore,
    schema: dishFavouriteSchema,
  },
  {
    benchmarkFixtures: dishBenchmarkFixtures,
    connectLiveProjection: connectDishLiveProjection,
    constants: dishRuntimeConstants,
    createDummyRecords: createCachedDishDummyRecords,
    createStore: createDishStore,
    fixtureStates: dishFixtureStates,
    registerStore: registerDishStore,
    schema: dishSchema,
  },
  {
    benchmarkFixtures: mealCategoryBenchmarkFixtures,
    connectLiveProjection: connectMealCategoryLiveProjection,
    constants: mealCategoryRuntimeConstants,
    createDummyRecords: createCachedMealCategoryDummyRecords,
    createStore: createMealCategoryStore,
    fixtureStates: mealCategoryFixtureStates,
    registerStore: registerMealCategoryStore,
    schema: mealCategorySchema,
  },
  {
    benchmarkFixtures: mealPlanCycleBenchmarkFixtures,
    connectLiveProjection: connectMealPlanCycleLiveProjection,
    constants: mealPlanCycleRuntimeConstants,
    createDummyRecords: createCachedMealPlanCycleDummyRecords,
    createStore: createMealPlanCycleStore,
    fixtureStates: mealPlanCycleFixtureStates,
    registerStore: registerMealPlanCycleStore,
    schema: mealPlanCycleSchema,
  },
  {
    benchmarkFixtures: mealPlanEntryBenchmarkFixtures,
    connectLiveProjection: connectMealPlanEntryLiveProjection,
    constants: mealPlanEntryRuntimeConstants,
    createDummyRecords: createCachedMealPlanEntryDummyRecords,
    createStore: createMealPlanEntryStore,
    fixtureStates: mealPlanEntryFixtureStates,
    registerStore: registerMealPlanEntryStore,
    schema: mealPlanEntrySchema,
  },
  {
    benchmarkFixtures: mealPlanBenchmarkFixtures,
    connectLiveProjection: connectMealPlanLiveProjection,
    constants: mealPlanRuntimeConstants,
    createDummyRecords: createCachedMealPlanDummyRecords,
    createStore: createMealPlanStore,
    fixtureStates: mealPlanFixtureStates,
    registerStore: registerMealPlanStore,
    schema: mealPlanSchema,
  },
  {
    benchmarkFixtures: trendingChefBenchmarkFixtures,
    connectLiveProjection: connectTrendingChefLiveProjection,
    constants: trendingChefRuntimeConstants,
    createDummyRecords: createCachedTrendingChefDummyRecords,
    createStore: createTrendingChefStore,
    fixtureStates: trendingChefFixtureStates,
    registerStore: registerTrendingChefStore,
    schema: trendingChefSchema,
  },
  {
    benchmarkFixtures: trendingDishBenchmarkFixtures,
    connectLiveProjection: connectTrendingDishLiveProjection,
    constants: trendingDishRuntimeConstants,
    createDummyRecords: createCachedTrendingDishDummyRecords,
    createStore: createTrendingDishStore,
    fixtureStates: trendingDishFixtureStates,
    registerStore: registerTrendingDishStore,
    schema: trendingDishSchema,
  },
  {
    benchmarkFixtures: opsConversationBenchmarkFixtures,
    connectLiveProjection: connectOpsConversationLiveProjection,
    constants: opsConversationRuntimeConstants,
    createDummyRecords: createCachedOpsConversationDummyRecords,
    createStore: createOpsConversationStore,
    fixtureStates: opsConversationFixtureStates,
    registerStore: registerOpsConversationStore,
    schema: opsConversationSchema,
  },
  {
    benchmarkFixtures: orderMessageBenchmarkFixtures,
    connectLiveProjection: connectOrderMessageLiveProjection,
    constants: orderMessageRuntimeConstants,
    createDummyRecords: createCachedOrderMessageDummyRecords,
    createStore: createOrderMessageStore,
    fixtureStates: orderMessageFixtureStates,
    registerStore: registerOrderMessageStore,
    schema: orderMessageSchema,
  },
  {
    benchmarkFixtures: orderThreadBenchmarkFixtures,
    connectLiveProjection: connectOrderThreadLiveProjection,
    constants: orderThreadRuntimeConstants,
    createDummyRecords: createCachedOrderThreadDummyRecords,
    createStore: createOrderThreadStore,
    fixtureStates: orderThreadFixtureStates,
    registerStore: registerOrderThreadStore,
    schema: orderThreadSchema,
  },
  {
    benchmarkFixtures: offerBenchmarkFixtures,
    connectLiveProjection: connectOfferLiveProjection,
    constants: offerRuntimeConstants,
    createDummyRecords: createCachedOfferDummyRecords,
    createStore: createOfferStore,
    fixtureStates: offerFixtureStates,
    registerStore: registerOfferStore,
    schema: offerSchema,
  },
  {
    benchmarkFixtures: chowProfileBenchmarkFixtures,
    connectLiveProjection: connectChowProfileLiveProjection,
    constants: chowProfileRuntimeConstants,
    createDummyRecords: createCachedChowProfileDummyRecords,
    createStore: createChowProfileStore,
    fixtureStates: chowProfileFixtureStates,
    registerStore: registerChowProfileStore,
    schema: chowProfileSchema,
  },
  {
    benchmarkFixtures: profileLinkBenchmarkFixtures,
    connectLiveProjection: connectProfileLinkLiveProjection,
    constants: profileLinkRuntimeConstants,
    createDummyRecords: createCachedProfileLinkDummyRecords,
    createStore: createProfileLinkStore,
    fixtureStates: profileLinkFixtureStates,
    registerStore: registerProfileLinkStore,
    schema: profileLinkSchema,
  },
  {
    benchmarkFixtures: vendorLocationBenchmarkFixtures,
    connectLiveProjection: connectVendorLocationLiveProjection,
    constants: vendorLocationRuntimeConstants,
    createDummyRecords: createCachedVendorLocationDummyRecords,
    createStore: createVendorLocationStore,
    fixtureStates: vendorLocationFixtureStates,
    registerStore: registerVendorLocationStore,
    schema: vendorLocationSchema,
  },
  {
    benchmarkFixtures: vendorProfileBenchmarkFixtures,
    connectLiveProjection: connectVendorProfileLiveProjection,
    constants: vendorProfileRuntimeConstants,
    createDummyRecords: createCachedVendorProfileDummyRecords,
    createStore: createVendorProfileStore,
    fixtureStates: vendorProfileFixtureStates,
    registerStore: registerVendorProfileStore,
    schema: vendorProfileSchema,
  },
  {
    benchmarkFixtures: riderProfileBenchmarkFixtures,
    connectLiveProjection: connectRiderProfileLiveProjection,
    constants: riderProfileRuntimeConstants,
    createDummyRecords: createCachedRiderProfileDummyRecords,
    createStore: createRiderProfileStore,
    fixtureStates: riderProfileFixtureStates,
    registerStore: registerRiderProfileStore,
    schema: riderProfileSchema,
  },
  {
    benchmarkFixtures: supportMessageBenchmarkFixtures,
    connectLiveProjection: connectSupportMessageLiveProjection,
    constants: supportMessageRuntimeConstants,
    createDummyRecords: createCachedSupportMessageDummyRecords,
    createStore: createSupportMessageStore,
    fixtureStates: supportMessageFixtureStates,
    registerStore: registerSupportMessageStore,
    schema: supportMessageSchema,
  },
  {
    benchmarkFixtures: accountSessionBenchmarkFixtures,
    connectLiveProjection: connectAccountSessionLiveProjection,
    constants: accountSessionRuntimeConstants,
    createDummyRecords: createCachedAccountSessionDummyRecords,
    createStore: createAccountSessionStore,
    fixtureStates: accountSessionFixtureStates,
    registerStore: registerAccountSessionStore,
    schema: accountSessionSchema,
  },
  {
    benchmarkFixtures: walletTransactionBenchmarkFixtures,
    connectLiveProjection: connectWalletTransactionLiveProjection,
    constants: walletTransactionRuntimeConstants,
    createDummyRecords: createCachedWalletTransactionDummyRecords,
    createStore: createWalletTransactionStore,
    fixtureStates: walletTransactionFixtureStates,
    registerStore: registerWalletTransactionStore,
    schema: walletTransactionSchema,
  },
] as const satisfies readonly PrototypeDomainRuntime[];

export const createPrototypeTenantStores = (
  tenantId: string,
  cache: PrototypeRuntimeCache = prototypeRuntimeCache,
  dummyRecordCount = 8
): readonly TenantProjectionStore<Record<string, unknown>>[] =>
  prototypeDomains.map((domain) => {
    const records =
      dummyRecordCount > 0
        ? domain.createDummyRecords(tenantId, dummyRecordCount, { seed: `${domain.constants.key}:scaffold` }, cache)
        : [];
    return domain.registerStore(tenantId, domain.createStore(tenantId, records, cache));
  });

